<template>
  <b-card
    no-body
    v-if="editor"
    class="editor-container"
    @contextmenu.prevent="showContextMenu"
  >
    <!-- Document Loading Overlay -->
    <transition name="fade">
      <div v-if="loadingDocument" class="document-loading-overlay">
        <div class="document-loading-spinner">
          <b-spinner variant="light" />
          <span>Loading document...</span>
        </div>
      </div>
    </transition>

    <!-- Toolbar -->
    <div class="editor-toolbar">
      <div class="toolbar-left">
        <!-- Back / Document selector -->
        <button
          class="tb-btn"
          @click="navigateBack"
          title="Back to documents"
        >
          <i class="fa fa-arrow-left"></i>
        </button>
        <div class="tb-doc-selector" v-if="currentDocName">
          <button class="tb-btn tb-doc-name" ref="docNameBtn" @click="toggleDocDropdown" :title="currentDocName">
            <i class="fa fa-file-text-o"></i>
            <span>{{ currentDocName }}</span>
            <i class="fa fa-caret-down"></i>
          </button>
          <div v-if="showDocDropdown" class="tb-doc-backdrop" @click="showDocDropdown = false"></div>
          <div v-if="showDocDropdown" class="tb-doc-dropdown" :style="docDropdownStyle">
            <div class="tb-doc-search">
              <input
                v-model="docSearchText"
                placeholder="Search documents..."
                @input="searchDocuments"
                ref="docSearchInput"
              />
            </div>
            <div class="tb-doc-list">
              <button
                v-for="doc in filteredDocList"
                :key="doc.name"
                class="tb-doc-item"
                :class="{ active: doc.name.replace(/^\//, '') === currentDocName }"
                @click="switchDocument(doc)"
              >
                {{ doc.name.replace(/^\//, '') }}
              </button>
              <div v-if="filteredDocList.length === 0" class="tb-doc-empty">No documents found</div>
            </div>
          </div>
        </div>

        <div class="tb-divider"></div>

        <!-- Sidebar Toggles -->
        <button
          class="tb-btn"
          :class="{ active: showLeftSidebar }"
          @click="showLeftSidebar = !showLeftSidebar"
          title="Toggle Sources & Fields"
        >
          <i class="fa fa-columns"></i>
        </button>
        <button
          class="tb-btn"
          :class="{ active: showRightSidebar }"
          @click="showRightSidebar = !showRightSidebar"
          title="Toggle Comments & Metadata"
        >
          <i class="fa fa-list-alt"></i>
        </button>

        <div class="tb-divider"></div>

        <!-- View Toggle -->
        <div class="tb-mode-switch">
          <button
            class="tb-mode-btn"
            :class="{ active: mode === 'editor' }"
            @click="mode = 'editor'"
          >
            <i class="fa fa-pencil"></i>
            <span>Edit</span>
          </button>
          <button
            class="tb-mode-btn"
            :class="{ active: mode === 'document' }"
            @click="mode = 'document'"
          >
            <i class="fa fa-eye"></i>
            <span>Preview</span>
          </button>
        </div>

        <template v-if="!isGenerateMode && mode === 'editor'">
          <div class="tb-divider"></div>

          <!-- Headings -->
          <div class="tb-group">
            <button
              class="tb-btn tb-btn--text"
              :class="{ active: editor.isActive('heading', { level: 1 }) }"
              @click="editor.chain().focus().toggleHeading({ level: 1 }).run()"
              title="Heading 1"
            >H1</button>
            <button
              class="tb-btn tb-btn--text"
              :class="{ active: editor.isActive('heading', { level: 2 }) }"
              @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
              title="Heading 2"
            >H2</button>
            <button
              class="tb-btn tb-btn--text"
              :class="{ active: editor.isActive('heading', { level: 3 }) }"
              @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
              title="Heading 3"
            >H3</button>
          </div>

          <div class="tb-divider"></div>

          <!-- Formatting -->
          <div class="tb-group">
            <button class="tb-btn" :class="{ active: editor.isActive('bold') }" @click="editor.chain().focus().toggleBold().run()" title="Bold"><i class="fa fa-bold"></i></button>
            <button class="tb-btn" :class="{ active: editor.isActive('italic') }" @click="editor.chain().focus().toggleItalic().run()" title="Italic"><i class="fa fa-italic"></i></button>
            <button class="tb-btn" :class="{ active: editor.isActive('strike') }" @click="editor.chain().focus().toggleStrike().run()" title="Strikethrough"><i class="fa fa-strikethrough"></i></button>
            <button class="tb-btn" :class="{ active: editor.isActive('highlight') }" @click="editor.chain().focus().toggleHighlight().run()" title="Highlight"><i class="fa fa-paint-brush"></i></button>
          </div>

          <div class="tb-divider"></div>

          <!-- Alignment -->
          <div class="tb-group">
            <button class="tb-btn" :class="{ active: editor.isActive({ textAlign: 'left' }) }" @click="editor.chain().focus().setTextAlign('left').run()" title="Align Left"><i class="fa fa-align-left"></i></button>
            <button class="tb-btn" :class="{ active: editor.isActive({ textAlign: 'center' }) }" @click="editor.chain().focus().setTextAlign('center').run()" title="Align Center"><i class="fa fa-align-center"></i></button>
            <button class="tb-btn" :class="{ active: editor.isActive({ textAlign: 'right' }) }" @click="editor.chain().focus().setTextAlign('right').run()" title="Align Right"><i class="fa fa-align-right"></i></button>
            <button class="tb-btn" :class="{ active: editor.isActive({ textAlign: 'justify' }) }" @click="editor.chain().focus().setTextAlign('justify').run()" title="Justify"><i class="fa fa-align-justify"></i></button>
          </div>

          <div class="tb-divider"></div>

          <!-- Color picker -->
          <button
            class="tb-btn tb-btn--color"
            :style="{ '--color-val': textColor || '#000' }"
            @click="colorPicker"
            title="Text Color"
          >
            <i class="fa fa-font"></i>
            <input
              style="visibility: hidden; width: 0; padding: 0; position: absolute"
              type="color"
              id="colorPicker"
              @input="editor.chain().focus().setColor($event.target.value).run()"
              :value="textColor"
            />
          </button>

          <!-- Font family -->
          <b-dropdown
            :text="fontFamily || 'Font'"
            variant="link"
            size="sm"
            class="tb-dropdown"
            toggle-class="tb-dropdown-toggle"
            no-caret
            :popper-opts="{ positionFixed: true }"
          >
            <template #button-content>
              <i class="fa fa-text-width"></i>
              <span>{{ fontFamily || 'Font' }}</span>
              <i class="fa fa-chevron-down ml-1" style="font-size:9px;opacity:0.5"></i>
            </template>
            <b-dropdown-item
              v-for="font in fonts"
              :key="font"
              @click="changeFontFamily(font)"
              :style="{ fontFamily: font }"
            >
              {{ font }}
            </b-dropdown-item>
          </b-dropdown>

          <!-- Font size -->
          <div class="tb-size-input">
            <input
              :value="fontSizeDisplay"
              type="number"
              min="6"
              max="100"
              title="Font Size"
              @mousedown="saveFontSizeSelection"
              @change="applyFontSize($event.target.value)"
              @keydown.enter.prevent="applyFontSize($event.target.value)"
            />
            <span class="tb-size-unit">px</span>
          </div>

          <!-- Line Spacing -->
          <b-dropdown
            variant="link"
            size="sm"
            class="tb-dropdown"
            toggle-class="tb-dropdown-toggle"
            no-caret
            :popper-opts="{ positionFixed: true }"
          >
            <template #button-content>
              <i class="fa fa-text-height"></i>
              <i class="fa fa-chevron-down ml-1" style="font-size:9px;opacity:0.5"></i>
            </template>
            <b-dropdown-header>Line Spacing</b-dropdown-header>
            <b-dropdown-item
              v-for="s in lineHeightPresets"
              :key="s.value"
              @click="editor.commands.setLineHeight(s.value)"
              :active="currentLineHeight === s.value"
            >
              {{ s.label }}
            </b-dropdown-item>
            <b-dropdown-divider />
            <b-dropdown-item @click="editor.commands.unsetLineHeight()">
              <i class="fa fa-times mr-1"></i> Reset line spacing
            </b-dropdown-item>
            <b-dropdown-divider />
            <b-dropdown-header>Text Padding (px)</b-dropdown-header>
            <b-dropdown-form class="tb-margin-custom tb-margin-sides">
              <div class="tb-margin-grid">
                <div class="tb-margin-field" v-for="side in ['top','right','bottom','left']" :key="'tp-'+side">
                  <span>{{ side.charAt(0).toUpperCase() + side.slice(1) }}</span>
                  <input type="number" min="0" max="200" :value="currentTextPadding[side]"
                    @change="updateTextPadding(side, $event.target.value)"
                    @keydown.enter.prevent="updateTextPadding(side, $event.target.value)" />
                </div>
              </div>
            </b-dropdown-form>
            <b-dropdown-item @click="editor.commands.unsetTextPadding()">
              <i class="fa fa-times mr-1"></i> Reset padding
            </b-dropdown-item>
          </b-dropdown>

          <div class="tb-divider"></div>

          <!-- Insert tools -->
          <div class="tb-group">
            <button
              class="tb-btn"
              :class="{ active: currentSettings === 'table' }"
              @click="() => currentSettings ? (currentSettings = null) : (currentSettings = 'table')"
              title="Table Tools"
            ><i class="fa fa-table"></i></button>

            <button class="tb-btn" @click="triggerImageImport" title="Insert Image">
              <i class="fa fa-image"></i>
              <b-form-file @input="imageChoosen" id="imageImport" style="display: none" />
            </button>

            <!-- Section Management -->
            <b-dropdown
              variant="link"
              size="sm"
              class="tb-dropdown"
              toggle-class="tb-dropdown-toggle"
              no-caret
              :popper-opts="{ positionFixed: true }"
            >
              <template #button-content>
                <i class="fa fa-files-o"></i>
                <span>Sections</span>
                <i class="fa fa-chevron-down ml-1" style="font-size:9px;opacity:0.5"></i>
              </template>
              <b-dropdown-header>Document Sections</b-dropdown-header>
              <b-dropdown-item
                v-for="(sec, idx) in editorSections"
                :key="idx"
                @click="scrollToSection(idx)"
              >
                <i class="fa fa-file-text-o mr-1"></i>
                {{ sec.name }}
                <span class="text-muted ml-1">({{ sec.pageCount }} {{ sec.pageCount === 1 ? 'page' : 'pages' }})</span>
              </b-dropdown-item>
              <b-dropdown-divider />
              <b-dropdown-item @click="addNewSection">
                <i class="fa fa-plus mr-1"></i> Add Document
              </b-dropdown-item>
              <b-dropdown-divider v-if="editorSections.length > 1" />
              <b-dropdown-item
                v-if="editorSections.length > 1"
                @click="openRenameSectionDialog"
              >
                <i class="fa fa-pencil mr-1"></i> Rename Current Section
              </b-dropdown-item>
              <b-dropdown-item
                v-if="editorSections.length > 1 && getCurrentSectionIndex() > 0"
                @click="mergeWithPreviousSection"
              >
                <i class="fa fa-compress mr-1"></i> Merge with Previous
              </b-dropdown-item>
              <b-dropdown-item
                v-if="editorSections.length > 1"
                @click="deleteCurrentSection"
                variant="danger"
              >
                <i class="fa fa-trash mr-1"></i> Delete Current Section
              </b-dropdown-item>
            </b-dropdown>

            <!-- Page Margins -->
            <b-dropdown
              variant="link"
              size="sm"
              class="tb-dropdown"
              toggle-class="tb-dropdown-toggle"
              no-caret
              :popper-opts="{ positionFixed: true }"
            >
              <template #button-content>
                <i class="fa fa-arrows-alt"></i>
                <span>Margins</span>
                <i class="fa fa-chevron-down ml-1" style="font-size:9px;opacity:0.5"></i>
              </template>
              <b-dropdown-header>Margin Presets</b-dropdown-header>
              <b-dropdown-item
                v-for="m in marginPresets"
                :key="m.label"
                @click="margins = m.value"
                :active="isMarginPreset(m.value)"
              >
                {{ m.label }}
              </b-dropdown-item>
              <b-dropdown-divider />
              <b-dropdown-form class="tb-margin-custom tb-margin-sides">
                <label>Custom (px)</label>
                <div class="tb-margin-grid">
                  <div class="tb-margin-field">
                    <span>Top</span>
                    <input type="number" min="0" max="200" :value="margins.top"
                      @change="updateMarginSide('top', $event.target.value)"
                      @keydown.enter.prevent="updateMarginSide('top', $event.target.value)" />
                  </div>
                  <div class="tb-margin-field">
                    <span>Right</span>
                    <input type="number" min="0" max="200" :value="margins.right"
                      @change="updateMarginSide('right', $event.target.value)"
                      @keydown.enter.prevent="updateMarginSide('right', $event.target.value)" />
                  </div>
                  <div class="tb-margin-field">
                    <span>Bottom</span>
                    <input type="number" min="0" max="200" :value="margins.bottom"
                      @change="updateMarginSide('bottom', $event.target.value)"
                      @keydown.enter.prevent="updateMarginSide('bottom', $event.target.value)" />
                  </div>
                  <div class="tb-margin-field">
                    <span>Left</span>
                    <input type="number" min="0" max="200" :value="margins.left"
                      @change="updateMarginSide('left', $event.target.value)"
                      @keydown.enter.prevent="updateMarginSide('left', $event.target.value)" />
                  </div>
                </div>
              </b-dropdown-form>
              <b-dropdown-divider />
              <b-dropdown-item @click="applyMarginsToAllPages">
                <i class="fa fa-files-o mr-1"></i> Apply to all pages
              </b-dropdown-item>
            </b-dropdown>

            <!-- Page Settings (Background, Header/Footer) -->
            <b-dropdown
              variant="link"
              size="sm"
              class="tb-dropdown"
              toggle-class="tb-dropdown-toggle"
              no-caret
              :popper-opts="{ positionFixed: true }"
            >
              <template #button-content>
                <i class="fa fa-file-image-o"></i>
                <span>Page</span>
                <i class="fa fa-chevron-down ml-1" style="font-size:9px;opacity:0.5"></i>
              </template>
              <b-dropdown-header>Background</b-dropdown-header>
              <b-dropdown-item @click="triggerBackgroundImageUpload">
                <i class="fa fa-image mr-1"></i>
                {{ currentSectionBackground ? 'Change Background' : 'Add Background Image' }}
              </b-dropdown-item>
              <b-dropdown-item v-if="currentSectionBackground" @click="removeBackgroundImage" variant="danger">
                <i class="fa fa-times mr-1"></i> Remove Background
              </b-dropdown-item>
              <b-dropdown-divider />
              <b-dropdown-header>Header &amp; Footer</b-dropdown-header>
              <b-dropdown-item @click="editor.commands.toggleSectionHeader()">
                <i class="fa fa-arrow-up mr-1"></i>
                {{ hasPageHeader ? 'Remove Header' : 'Add Header' }}
              </b-dropdown-item>
              <b-dropdown-item @click="editor.commands.toggleSectionFooter()">
                <i class="fa fa-arrow-down mr-1"></i>
                {{ hasPageFooter ? 'Remove Footer' : 'Add Footer' }}
              </b-dropdown-item>
              <b-dropdown-item v-if="hasPageHeader || hasPageFooter" @click="insertPageNumberWithFormat">
                <i class="fa fa-hashtag mr-1"></i> Insert Page Number
              </b-dropdown-item>
              <b-dropdown-form v-if="hasPageHeader || hasPageFooter" class="tb-hf-settings">
                <label><i class="fa fa-hashtag mr-1"></i>Page number format</label>
                <input
                  type="text"
                  class="tb-pn-format-input"
                  v-model="pageNumberFormat"
                  :placeholder="'e.g. Page ' + '{{ .page }}' + ' of ' + '{{ .pages }}'  "
                  @keydown.enter.prevent
                  @change="updateAllPageNumberFormats"
                />
              </b-dropdown-form>
              <template v-if="hasPageHeader">
                <b-dropdown-divider />
                <b-dropdown-form class="tb-hf-settings">
                  <label><i class="fa fa-arrow-up mr-1"></i>Header padding (px)</label>
                  <div class="tb-hf-settings-row">
                    <div class="tb-hf-field" v-for="side in ['Top','Right','Bottom','Left']" :key="'h-'+side">
                      <span>{{ side[0] }}</span>
                      <input type="number" min="0" max="200" :value="headerAttrs['padding'+side]" @change="setHeaderFooterAttr('pageHeader', 'padding'+side, $event.target.value)" />
                    </div>
                    <div class="tb-hf-field">
                      <span>Bg</span>
                      <input type="color" :value="headerAttrs.backgroundColor || '#ffffff'" @input="setHeaderFooterAttr('pageHeader', 'backgroundColor', $event.target.value)" />
                      <button v-if="headerAttrs.backgroundColor" class="tb-hf-color-clear" @click="setHeaderFooterAttr('pageHeader', 'backgroundColor', null)" title="Clear"><i class="fa fa-times"></i></button>
                    </div>
                  </div>
                </b-dropdown-form>
              </template>
              <template v-if="hasPageFooter">
                <b-dropdown-form class="tb-hf-settings">
                  <label><i class="fa fa-arrow-down mr-1"></i>Footer padding (px)</label>
                  <div class="tb-hf-settings-row">
                    <div class="tb-hf-field" v-for="side in ['Top','Right','Bottom','Left']" :key="'f-'+side">
                      <span>{{ side[0] }}</span>
                      <input type="number" min="0" max="200" :value="footerAttrs['padding'+side]" @change="setHeaderFooterAttr('pageFooter', 'padding'+side, $event.target.value)" />
                    </div>
                    <div class="tb-hf-field">
                      <span>Bg</span>
                      <input type="color" :value="footerAttrs.backgroundColor || '#ffffff'" @input="setHeaderFooterAttr('pageFooter', 'backgroundColor', $event.target.value)" />
                      <button v-if="footerAttrs.backgroundColor" class="tb-hf-color-clear" @click="setHeaderFooterAttr('pageFooter', 'backgroundColor', null)" title="Clear"><i class="fa fa-times"></i></button>
                    </div>
                  </div>
                </b-dropdown-form>
              </template>
            </b-dropdown>
            <b-form-file @input="backgroundImageChoosen" id="backgroundImageImport" style="display: none" accept="image/*" />
          </div>

          <div class="tb-divider"></div>

          <button
            class="tb-btn tb-btn--comment"
            @click="globalCommentsModal = true"
            title="Global Comments"
          >
            <i class="fa fa-comments"></i>
            <span v-if="totalGlobalCommentsCount > 0" class="tb-badge">{{ totalGlobalCommentsCount }}</span>
          </button>
        </template>
      </div>

      <div class="toolbar-right">
        <!-- Format -->
        <button
          class="tb-btn"
          :class="{ 'is-loading': formattingDocument && !isInitialFormat }"
          :disabled="formattingDocument"
          :title="formattingDocument ? 'Formatting...' : 'Format pages'"
          @click="manualFormat"
        >
          <i :class="formattingDocument && !isInitialFormat ? 'fa fa-spinner fa-spin' : 'fa fa-align-justify'"></i>
        </button>

        <!-- Print -->
        <button
          class="tb-btn"
          :class="{ 'is-loading': printingPreview }"
          :disabled="printingPreview"
          :title="printingPreview ? 'Preparing PDF' : 'Print'"
          :aria-busy="printingPreview ? 'true' : 'false'"
          @click="printPreview()"
        >
          <i :class="printingPreview ? 'fa fa-spinner fa-spin' : 'fa fa-print'"></i>
        </button>

        <div class="tb-divider"></div>

        <!-- Variants -->
        <b-dropdown
          v-if="storedDoc"
          variant="link"
          size="sm"
          class="tb-dropdown tb-dropdown--variant"
          toggle-class="tb-dropdown-toggle"
          no-caret
          :popper-opts="{ positionFixed: true }"
        >
          <template #button-content>
            <i class="fa fa-code-fork"></i>
            <span>{{ documentVariant }}</span>
            <i class="fa fa-chevron-down ml-1" style="font-size:9px;opacity:0.5"></i>
          </template>
          <b-dropdown-header>Variants</b-dropdown-header>
          <b-dropdown-item
            v-for="variant in storedDoc.variants.map((v) => v.name)"
            :key="variant"
            @click="() => (documentVariant = variant)"
            :active="documentVariant === variant"
          >
            {{ variant }}
          </b-dropdown-item>
          <b-dropdown-divider v-if="!isGenerateMode"></b-dropdown-divider>
          <b-dropdown-item
            v-if="!isGenerateMode"
            @click="() => { newVariantName = ''; showNewVariantModal = true; }"
          >
            <i class="fa fa-plus mr-1"></i> New variant
          </b-dropdown-item>
        </b-dropdown>

        <!-- Save -->
        <div v-if="!isGenerateMode" class="tb-save-group">
          <button
            class="tb-btn tb-btn--save"
            :class="{ 'has-unsaved': hasUnsavedChanges && !autoSave }"
            :disabled="savingTemplate || reviewingRevision"
            @click="saveDocument"
          >
            <b-spinner small v-if="savingTemplate || autosavingTemplate" />
            <template v-else>
              <i class="fa fa-check"></i>
              <span>{{ $t("save") }}</span>
            </template>
          </button>
          <label class="tb-autosave-switch" title="Toggle auto-save" @click.stop>
            <input type="checkbox" v-model="autoSave" />
            <span class="tb-switch-track"></span>
          </label>
        </div>
      </div>
    </div>

    <!-- Table Tools Bar (secondary) -->
    <transition name="slide-down">
      <div v-if="currentSettings === 'table'" class="table-toolbar">
        <span class="table-toolbar-label"><i class="fa fa-table mr-1"></i>Table</span>
        <button class="tb-tool-btn" @click="editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()"><i class="fa fa-th"></i> Insert</button>
        <div class="tb-divider"></div>
        <button class="tb-tool-btn" @click="editor.chain().focus().addColumnBefore().run()"><i class="fa fa-arrow-left"></i> Col before</button>
        <button class="tb-tool-btn" @click="editor.chain().focus().addColumnAfter().run()"><i class="fa fa-arrow-right"></i> Col after</button>
        <button class="tb-tool-btn tb-tool-btn--danger" @click="editor.chain().focus().deleteColumn().run()"><i class="fa fa-times"></i> Del col</button>
        <div class="tb-divider"></div>
        <button class="tb-tool-btn" @click="editor.chain().focus().addRowBefore().run()"><i class="fa fa-arrow-up"></i> Row before</button>
        <button class="tb-tool-btn" @click="editor.chain().focus().addRowAfter().run()"><i class="fa fa-arrow-down"></i> Row after</button>
        <button class="tb-tool-btn tb-tool-btn--danger" @click="editor.chain().focus().deleteRow().run()"><i class="fa fa-times"></i> Del row</button>
        <div class="tb-divider"></div>
        <button class="tb-tool-btn tb-tool-btn--danger" @click="editor.chain().focus().deleteTable().run()"><i class="fa fa-trash"></i> Del table</button>

        <div class="tb-divider"></div>

        <!-- Border styling -->
        <span class="table-toolbar-label"><i class="fa fa-square-o mr-1"></i>Borders</span>

        <div class="tb-border-controls">
          <input
            type="number"
            class="tb-border-width"
            v-model="tableBorderWidth"
            min="0"
            max="10"
            title="Border width (px)"
          />
          <select class="tb-border-style" v-model="tableBorderStyle" title="Border style">
            <option value="solid">Solid</option>
            <option value="dashed">Dashed</option>
            <option value="dotted">Dotted</option>
            <option value="double">Double</option>
            <option value="none">None</option>
          </select>
          <div class="tb-border-color-wrap" title="Border color">
            <div class="tb-border-color-swatch" :style="{ background: tableBorderColor }"></div>
            <input type="color" class="tb-border-color-input" v-model="tableBorderColor" />
          </div>
        </div>

        <div class="tb-border-sides">
          <button class="tb-tool-btn" @click="applyBorderToSide('all')" title="All borders"><i class="fa fa-th-large"></i> All</button>
          <button class="tb-tool-btn" @click="applyBorderToSide('top')" title="Top border"><i class="fa fa-long-arrow-up"></i> T</button>
          <button class="tb-tool-btn" @click="applyBorderToSide('right')" title="Right border"><i class="fa fa-long-arrow-right"></i> R</button>
          <button class="tb-tool-btn" @click="applyBorderToSide('bottom')" title="Bottom border"><i class="fa fa-long-arrow-down"></i> B</button>
          <button class="tb-tool-btn" @click="applyBorderToSide('left')" title="Left border"><i class="fa fa-long-arrow-left"></i> L</button>
          <button class="tb-tool-btn tb-tool-btn--danger" @click="removeBorderFromSide('all')" title="Remove all borders"><i class="fa fa-ban"></i></button>
        </div>

        <div class="tb-divider"></div>

        <!-- Cell background -->
        <span class="table-toolbar-label"><i class="fa fa-paint-brush mr-1"></i>Fill</span>
        <div class="tb-border-color-wrap" title="Cell background color">
          <div class="tb-border-color-swatch" :style="{ background: tableCellBgColor || 'transparent' }"></div>
          <input type="color" class="tb-border-color-input" :value="tableCellBgColor || '#ffffff'" @input="setTableCellBgColor($event.target.value)" />
        </div>
        <button class="tb-tool-btn" @click="setTableCellBgColor(null)" title="Clear background"><i class="fa fa-times"></i></button>

        <button class="tb-tool-btn table-toolbar-close" @click="currentSettings = null"><i class="fa fa-times"></i></button>
      </div>
    </transition>

    <!-- Main Editor Layout -->
    <div class="editor-main-layout">
      <!-- Sidebar Left -->
      <transition name="slide-sidebar-left">
        <div class="editor-sidebar editor-sidebar-left" v-if="showLeftSidebar">
          <div class="sidebar-section">
            <div class="sidebar-section-header" v-b-toggle.accordion-sources>
              <div class="sidebar-section-title">
                <i class="fa fa-database"></i>
                <span>Sources</span>
              </div>
              <i class="fa fa-chevron-down sidebar-chevron"></i>
            </div>
            <b-collapse id="accordion-sources" visible accordion="sidebar-left">
              <div class="sidebar-section-body">
                <source-tab v-bind="sourceTabBinds.props" v-on="sourceTabBinds.on" />
              </div>
            </b-collapse>
          </div>

          <div class="sidebar-section">
            <div class="sidebar-section-header" v-b-toggle.accordion-fields>
              <div class="sidebar-section-title">
                <i class="fa fa-list-ul"></i>
                <span>Fields</span>
              </div>
              <i class="fa fa-chevron-down sidebar-chevron"></i>
            </div>
            <b-collapse id="accordion-fields" accordion="sidebar-left">
              <div class="sidebar-section-body">
                <fields-tab v-bind="fieldsTabBinds.props" v-on="fieldsTabBinds.on" />
              </div>
            </b-collapse>
          </div>
        </div>
      </transition>

      <!-- Editor Canvas -->
      <div class="editor-content-area">
        <div v-if="reviewingRevision" class="revision-banner">
          <i class="fa fa-history"></i>
          <span>Reviewing revision <strong v-if="reviewingRevision.documentVersion">v{{ reviewingRevision.documentVersion }}</strong> from {{ formatDate(reviewingRevision.updatedBy.time) }}</span>
          <button class="revision-banner-btn restore" @click="restoreRevision">
            <i class="fa fa-check"></i> Restore
          </button>
          <button class="revision-banner-btn" @click="exitRevisionPreview">
            <i class="fa fa-times"></i> Exit
          </button>
        </div>
        <transition name="fade">
          <div v-if="formattingDocument" class="formatting-overlay">
            <div class="formatting-spinner">
              <i class="fa fa-spinner fa-spin"></i>
              <span>{{ formattingProgress || 'Formatting document...' }}</span>
            </div>
          </div>
        </transition>
        <div class="editor-canvas-wrapper" ref="editorCanvasWrapper">
          <editor-content v-show="mode === 'editor'" :editor="editor" class="editor" />

          <bubble-menu
            v-if="mode === 'editor'"
            :editor="editor"
            :tippy-options="{ duration: 100, maxWidth: 'none', placement: 'top', animation: 'shift-away' }"
            class="bubble-menu"
          >
            <!-- Row 1: Headings + Text Formatting -->
            <div class="bm-row">
              <div class="bm-group">
                <button
                  class="bm-btn bm-btn--text"
                  :class="{ active: editor.isActive('heading', { level: 1 }) }"
                  @click="editor.chain().focus().toggleHeading({ level: 1 }).run()"
                  title="Heading 1"
                >H1</button>
                <button
                  class="bm-btn bm-btn--text"
                  :class="{ active: editor.isActive('heading', { level: 2 }) }"
                  @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
                  title="Heading 2"
                >H2</button>
                <button
                  class="bm-btn bm-btn--text"
                  :class="{ active: editor.isActive('heading', { level: 3 }) }"
                  @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
                  title="Heading 3"
                >H3</button>
              </div>

              <div class="bm-divider"></div>

              <div class="bm-group">
                <button class="bm-btn" :class="{ active: editor.isActive('bold') }" @click="editor.chain().focus().toggleBold().run()" title="Bold"><i class="fa fa-bold"></i></button>
                <button class="bm-btn" :class="{ active: editor.isActive('italic') }" @click="editor.chain().focus().toggleItalic().run()" title="Italic"><i class="fa fa-italic"></i></button>
                <button class="bm-btn" :class="{ active: editor.isActive('strike') }" @click="editor.chain().focus().toggleStrike().run()" title="Strikethrough"><i class="fa fa-strikethrough"></i></button>
                <button class="bm-btn" :class="{ active: editor.isActive('highlight') }" @click="editor.chain().focus().toggleHighlight().run()" title="Highlight"><i class="fa fa-paint-brush"></i></button>
              </div>

              <div class="bm-divider"></div>

              <button
                class="bm-btn bm-btn--color"
                :style="{ '--color-val': textColor || '#000' }"
                @click="colorPicker"
                title="Text Color"
              >
                <i class="fa fa-font"></i>
              </button>
            </div>

            <!-- Row 2: Alignment + Font Controls -->
            <div class="bm-row">
              <div class="bm-group">
                <button class="bm-btn" :class="{ active: editor.isActive({ textAlign: 'left' }) }" @click="editor.chain().focus().setTextAlign('left').run()" title="Align Left"><i class="fa fa-align-left"></i></button>
                <button class="bm-btn" :class="{ active: editor.isActive({ textAlign: 'center' }) }" @click="editor.chain().focus().setTextAlign('center').run()" title="Align Center"><i class="fa fa-align-center"></i></button>
                <button class="bm-btn" :class="{ active: editor.isActive({ textAlign: 'right' }) }" @click="editor.chain().focus().setTextAlign('right').run()" title="Align Right"><i class="fa fa-align-right"></i></button>
                <button class="bm-btn" :class="{ active: editor.isActive({ textAlign: 'justify' }) }" @click="editor.chain().focus().setTextAlign('justify').run()" title="Justify"><i class="fa fa-align-justify"></i></button>
              </div>

              <div class="bm-divider"></div>

              <div class="bm-font-select">
                <select
                  :value="fontFamily || 'Arial'"
                  @change="changeFontFamily($event.target.value)"
                  title="Font Family"
                >
                  <option v-for="font in fonts" :key="font" :value="font" :style="{ fontFamily: font }">{{ font }}</option>
                </select>
              </div>

              <div class="bm-size-input">
                <input
                  :value="fontSizeDisplay"
                  type="number"
                  min="6"
                  max="100"
                  title="Font Size"
                  @mousedown="saveFontSizeSelection"
                  @change="applyFontSize($event.target.value)"
                  @keydown.enter.prevent="applyFontSize($event.target.value)"
                />
                <span class="bm-size-unit">px</span>
              </div>

              <div class="bm-divider"></div>

              <!-- Line Height -->
              <select
                class="bm-select"
                :value="currentLineHeight || ''"
                @change="$event.target.value ? editor.commands.setLineHeight($event.target.value) : editor.commands.unsetLineHeight()"
                title="Line Spacing"
              >
                <option value="">LH</option>
                <option v-for="s in lineHeightPresets" :key="s.value" :value="s.value">{{ s.label }}</option>
              </select>

              <!-- Text Padding -->
              <div class="bm-padding-group" title="Text Padding (T R B L)">
                <i class="fa fa-arrows-alt" style="font-size:10px;color:#8890a8;margin-right:2px"></i>
                <input v-for="side in ['top','right','bottom','left']" :key="'bm-tp-'+side"
                  type="number" min="0" max="200"
                  class="bm-padding-input"
                  :value="currentTextPadding[side]"
                  :title="side"
                  :placeholder="side[0].toUpperCase()"
                  @change="updateTextPadding(side, $event.target.value)"
                  @keydown.enter.prevent="updateTextPadding(side, $event.target.value)"
                />
              </div>
            </div>
          </bubble-menu>

          <div :hidden="mode === 'editor'" id="document-preview"></div>
        </div>

        <div v-if="totalPages > 1" class="page-indicator" @click="togglePageNumbering" title="Click to toggle numbering mode">
          <template v-if="sectionNumbering === 'per-section' && sectionCount > 1">
            {{ currentSectionName }} &mdash; Page {{ currentSectionPage }} of {{ currentSectionTotalPages }}
          </template>
          <template v-else>
            Page {{ currentPage }} of {{ totalPages }}
          </template>
        </div>
      </div>

      <!-- Sidebar Right -->
      <transition name="slide-sidebar-right">
        <div class="editor-sidebar editor-sidebar-right" v-if="showRightSidebar">

          <!-- Comments Section -->
          <div class="sidebar-section">
            <div class="sidebar-section-header" v-b-toggle.accordion-comments>
              <div class="sidebar-section-title">
                <i class="fa fa-comment-o"></i>
                <span>Comments</span>
                <span v-if="documentComments.length > 0" class="sidebar-count">{{ documentComments.length }}</span>
              </div>
              <i class="fa fa-chevron-down sidebar-chevron"></i>
            </div>
            <b-collapse id="accordion-comments" visible accordion="sidebar-right">
              <div class="sidebar-section-body sidebar-section-body--flush">
                <div v-if="documentComments.length === 0" class="sidebar-empty">
                  <i class="fa fa-comment-o"></i>
                  <p>No inline comments</p>
                </div>
                <div
                  v-for="(comment, index) in documentComments"
                  :key="comment.id || index"
                  class="comment-card"
                  :class="{ 'is-deleting': deletingCommentId === comment.id }"
                  @mouseenter="highlightComment(comment, true)"
                  @mouseleave="highlightComment(comment, false)"
                >
                  <div class="comment-card-header">
                    <div class="comment-avatar">{{ (comment.author || 'U').charAt(0).toUpperCase() }}</div>
                    <div class="comment-card-meta">
                      <span class="comment-author-name">{{ comment.author || 'Unknown' }}</span>
                      <span class="comment-timestamp" v-if="comment.time">{{ formatDate(comment.time) }}</span>
                    </div>
                    <div class="comment-card-actions">
                      <button class="comment-action-btn" @click.stop="openReplyCommentModal(comment)" title="Reply"><i class="fa fa-reply"></i></button>
                      <button v-if="canDeleteComment(comment) && deletingCommentId !== comment.id" class="comment-action-btn comment-action-btn--danger" @click.stop="confirmDeleteComment(comment)" title="Delete"><i class="fa fa-trash"></i></button>
                      <b-spinner v-if="deletingCommentId === comment.id" small variant="danger" />
                    </div>
                  </div>
                  <div class="comment-card-body" @click="scrollToComment(comment)">
                    <div class="comment-text">{{ comment.content }}</div>
                    <div class="comment-quote" v-if="comment.text">
                      <i class="fa fa-quote-left"></i>
                      {{ comment.text.substring(0, 40) }}{{ comment.text.length > 40 ? '…' : '' }}
                    </div>
                  </div>

                  <!-- Replies -->
                  <div v-if="comment.replies && comment.replies.length > 0" class="comment-replies">
                    <div
                      v-for="(reply, ri) in comment.replies"
                      :key="reply.id || ri"
                      class="comment-reply-card"
                      :class="{ 'is-deleting': deletingCommentId === reply.id }"
                    >
                      <div class="comment-card-header">
                        <div class="comment-avatar comment-avatar--sm">{{ (reply.author || 'U').charAt(0).toUpperCase() }}</div>
                        <div class="comment-card-meta">
                          <span class="comment-author-name">{{ reply.author || 'Unknown' }}</span>
                          <span class="comment-timestamp" v-if="reply.time">{{ formatDate(reply.time) }}</span>
                        </div>
                        <div class="comment-card-actions">
                          <button v-if="canDeleteComment(reply) && deletingCommentId !== reply.id" class="comment-action-btn comment-action-btn--danger" @click.stop="confirmDeleteComment(reply)" title="Delete"><i class="fa fa-trash"></i></button>
                          <b-spinner v-if="deletingCommentId === reply.id" small variant="danger" />
                        </div>
                      </div>
                      <div class="comment-text">{{ reply.content }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </b-collapse>
          </div>

          <!-- Metadata Section -->
          <div class="sidebar-section">
            <div class="sidebar-section-header" v-b-toggle.accordion-metadata>
              <div class="sidebar-section-title">
                <i class="fa fa-info-circle"></i>
                <span>Metadata</span>
              </div>
              <i class="fa fa-chevron-down sidebar-chevron"></i>
            </div>
            <b-collapse id="accordion-metadata" accordion="sidebar-right">
              <div class="sidebar-section-body">
                <div class="metadata-content" v-if="storedDoc">
                  <div class="meta-group">
                    <div class="meta-label">Document</div>
                    <div class="meta-row"><span>Name</span><span>{{ storedDoc.name }}</span></div>
                    <div class="meta-row"><span>Variants</span><span>{{ storedDoc.variants.length }}</span></div>
                  </div>
                  <div class="meta-group" v-if="currentVariantData">
                    <div class="meta-label">Active Variant</div>
                    <div class="meta-row"><span>Name</span><span>{{ currentVariantData.name }}</span></div>
                    <div class="meta-row"><span>Version</span><span>{{ currentVariantData.version }}</span></div>
                  </div>
                  <div class="meta-group" v-if="storedDoc.createdBy">
                    <div class="meta-label">Created</div>
                    <div class="meta-row"><span>By</span><span>{{ storedDoc.createdBy.name }}</span></div>
                    <div class="meta-row"><span>When</span><span>{{ formatDate(storedDoc.createdBy.time) }}</span></div>
                  </div>
                  <div class="meta-group" v-if="storedDoc.updatedBy">
                    <div class="meta-label">Last Updated</div>
                    <div class="meta-row"><span>By</span><span>{{ storedDoc.updatedBy.name }}</span></div>
                    <div class="meta-row"><span>When</span><span>{{ formatDate(storedDoc.updatedBy.time) }}</span></div>
                  </div>
                </div>
              </div>
            </b-collapse>
          </div>

          <!-- Revisions Section -->
          <div class="sidebar-section">
            <div class="sidebar-section-header" @click="toggleRevisions" v-b-toggle.accordion-revisions>
              <div class="sidebar-section-title">
                <i class="fa fa-history"></i>
                <span>Revisions</span>
                <span v-if="revisions.length > 0" class="sidebar-count">{{ revisions.length }}</span>
              </div>
              <i class="fa fa-chevron-down sidebar-chevron"></i>
            </div>
            <b-collapse id="accordion-revisions" accordion="sidebar-right">
              <div class="sidebar-section-body sidebar-section-body--flush">
                <div v-if="loadingRevisions" class="sidebar-empty">
                  <i class="fa fa-spinner fa-spin"></i> Loading revisions…
                </div>
                <div v-else-if="revisions.length === 0" class="sidebar-empty">
                  No revisions yet.
                </div>
                <div v-else class="revision-list">
                  <div
                    v-for="rev in revisions"
                    :key="rev.version"
                    class="revision-item"
                    @click="previewRevision(rev)"
                  >
                    <div class="revision-meta">
                      <span v-if="rev.documentVersion" class="revision-version">v{{ rev.documentVersion }}</span>
                      <span class="revision-date">{{ formatDate(rev.updatedBy.time) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </b-collapse>
          </div>

        </div>
      </transition>
    </div>

    <ContextMenu
      :contextMenu="contextMenu"
      :editor="editor"
      @hide="() => hideContextMenu()"
      @add-document-comment="openAddCommentModalFromContext"
      @split-document="addNewSection"
      @merge-document="mergeWithPreviousSection"
    />

    <!-- Add Source Dialog -->
    <main-dialog
      :show="addSourceSidebar"
      title="Add Source"
      max-width="440px"
      @close="addSourceSidebar = false"
    >
      <div class="fe-dialog-form">
        <div class="fe-field">
          <label class="fe-label">Source type</label>
          <editor-select
            :options="sourceList"
            label="text"
            :reduce="(o) => o.value"
            v-model="rootSource"

            placeholder="Select source type…"
          />
        </div>

        <div class="fe-field" v-if="rootSource == 'component'">
          <label class="fe-label">Component</label>
          <editor-select
            :options="componentsList"
            label="text"
            :reduce="(o) => o.value"
            v-model="rootSourceValue"
            @option:selected="(opt) => rootSourceValueSelected(rootSource, opt)"

            placeholder="Select component…"
          />
        </div>

        <div class="fe-field" v-if="rootSource == 'component' && rootSourceValue != null">
          <label class="fe-label">Tag</label>
          <editor-select
            v-if="rootSourceComponentFields"
            :options="rootSourceComponentFields"
            v-model="rootSourceValueLabel"
            label="text"
            :reduce="(o) => o.value"

            placeholder="Select tag…"
          />
          <div v-else class="fe-skeleton"></div>
        </div>

        <div class="fe-field" v-if="rootSource == 'component' && rootSourceValue != null">
          <label class="fe-label">Nickname</label>
          <input
            type="text"
            class="fe-input"
            v-model="rootSourceTag"
            placeholder="Enter source nickname…"
          />
        </div>
      </div>

      <template #footer>
        <button class="fe-btn fe-btn--cancel" @click="iSidebar = false">Cancel</button>
        <button class="fe-btn fe-btn--save" @click="addSource">
          <i class="fa fa-plus"></i>
          <span>Add Source</span>
        </button>
      </template>
    </main-dialog>

    <!-- Edit Group Dialog -->
    <main-dialog
      :show="groupEditor.show"
      title="Edit Group"
      max-width="400px"
      @close="groupEditor.show = false"
    >
      <div class="fe-dialog-form">
        <div class="fe-field">
          <label class="fe-label">Tag</label>
          <input
            type="text"
            class="fe-input"
            v-model="groupEditor.label"
            placeholder="Enter group tag…"
            @keyup.enter="saveGroup"
          />
        </div>
      </div>

      <template #footer>
        <button class="fe-btn fe-btn--cancel" @click="groupEditor.show = false">Cancel</button>
        <button class="fe-btn fe-btn--save" @click="saveGroup">
          <i class="fa fa-check"></i>
          <span>Save</span>
        </button>
      </template>
    </main-dialog>
    <!-- Field Editor Popover -->
    <transition name="fe-fade">
      <div
        v-if="fieldEditor.tooltipTarget"
        ref="fieldEditorPopover"
        class="fe-popover"
        :style="fieldEditorPopoverStyle"
      >
        <div class="fe-popover__arrow"></div>

        <!-- Header -->
        <div class="fe-popover__header">
          <div class="fe-popover__title">
            <i class="fa fa-cube"></i>
            <span>{{ fieldEditor.field || 'Field' }}</span>
          </div>
          <button class="fe-popover__close" @click="closeFieldEditor" title="Close">
            <i class="fa fa-times"></i>
          </button>
        </div>

        <!-- Body -->
        <div class="fe-popover__body">
          <div class="fe-field">
            <label class="fe-label">Nickname</label>
            <input
              type="text"
              class="fe-input"
              v-model="fieldEditor.label"
              placeholder="Enter nickname..."
            />
          </div>

          <div class="fe-field">
            <label class="fe-label">Source</label>
            <editor-select
              :options="fieldEditor.sources"
              label="text"
              :reduce="(o) => o.value"
              v-model="fieldEditor.source"
              @option:selected="fieldEditorSourceChanged"
  
            />
          </div>

          <div class="fe-field" v-if="fieldEditor.source == 'text'">
            <label class="fe-label">Value</label>
            <textarea
              class="fe-input fe-textarea"
              :value="getFieldSourceValue()"
              @input="fieldSourceValueChanged($event.target.value)"
              placeholder="Enter value..."
              rows="3"
            />
          </div>

          <div class="fe-field" v-if="fieldEditor.source == 'options'">
            <label class="fe-label">Value</label>
            <editor-select
              :options="fieldEditor.settings.options"
              :value="getFieldSourceValue()"
              @input="fieldSourceValueChanged"
  
            />
          </div>

          <div class="fe-field" v-if="fieldEditor.source == 'datepicker'">
            <label class="fe-label">Date</label>
            <b-form-datepicker
              :value="getFieldSourceValue()"
              @input="fieldSourceValueChanged"
              dark
              locale="sr"
              date-format="DD.MM.YYYY"
              class="fe-datepicker"
            />
          </div>

          <div class="fe-field" v-if="fieldEditorIsSourceFromTag">
            <label class="fe-label">Field mapping</label>
            <editor-select
              :options="fieldEditor.settings ? fieldEditor.settings.fields : []"
              label="text"
              :reduce="(o) => o.key"
              :value="fieldEditorFieldMappedValue"
              @input="fieldEditorFieldMapped"

            />
          </div>

          <div class="fe-field" v-if="fieldEditorIsSourceFromTag && fieldEditorFieldMappedValue">
            <div class="fe-format-header">
              <label class="fe-label">Formatting</label>
              <div class="fe-format-toggle">
                <button
                  class="fe-format-toggle-btn"
                  :class="{ active: fieldEditorFormatMode === 'visual' }"
                  @click="fieldEditorFormatMode = 'visual'"
                >Visual</button>
                <button
                  class="fe-format-toggle-btn"
                  :class="{ active: fieldEditorFormatMode === 'advanced' }"
                  @click="fieldEditorFormatMode = 'advanced'"
                >Expression</button>
              </div>
            </div>

            <!-- Visual mode -->
            <div v-if="fieldEditorFormatMode === 'visual'" class="fe-visual-format">
              <div class="fe-value-type-badge" v-if="fieldEditorValueType">
                <i :class="fieldEditorValueTypeIcon"></i>
                {{ fieldEditorValueTypeLabel }}
                <span v-if="fieldEditorValueType === 'array' || fieldEditorValueType === 'array_objects'">
                  ({{ fieldEditorSourceValue.length }} items)
                </span>
              </div>

              <!-- Array of objects -->
              <template v-if="fieldEditorValueType === 'array_objects'">
                <div class="fe-visual-row">
                  <label class="fe-visual-label">Display property</label>
                  <select class="fe-input fe-visual-select" v-model="fieldEditorVisualFormat.property" @change="buildVisualExpression">
                    <option value="">-- select property --</option>
                    <option v-for="k in fieldEditorObjectKeys" :key="k.value" :value="k.value">{{ k.text }}</option>
                  </select>
                </div>
                <div class="fe-visual-row">
                  <label class="fe-visual-label">Display as</label>
                  <div class="fe-visual-options">
                    <button class="fe-visual-option" :class="{ active: fieldEditorVisualFormat.displayAs === 'inline' }" @click="fieldEditorVisualFormat.displayAs = 'inline'; buildVisualExpression()">
                      <i class="fa fa-align-left"></i> Inline
                    </button>
                    <button class="fe-visual-option" :class="{ active: fieldEditorVisualFormat.displayAs === 'list' }" @click="fieldEditorVisualFormat.displayAs = 'list'; buildVisualExpression()">
                      <i class="fa fa-list"></i> List
                    </button>
                    <button class="fe-visual-option" :class="{ active: fieldEditorVisualFormat.displayAs === 'numbered' }" @click="fieldEditorVisualFormat.displayAs = 'numbered'; buildVisualExpression()">
                      <i class="fa fa-list-ol"></i> Numbered
                    </button>
                  </div>
                </div>
                <div class="fe-visual-row" v-if="fieldEditorVisualFormat.displayAs === 'inline'">
                  <label class="fe-visual-label">Separator</label>
                  <input class="fe-input fe-visual-input" v-model="fieldEditorVisualFormat.separator" @input="buildVisualExpression" placeholder=", " />
                </div>
              </template>

              <!-- Array of primitives -->
              <template v-if="fieldEditorValueType === 'array'">
                <div class="fe-visual-row">
                  <label class="fe-visual-label">Display as</label>
                  <div class="fe-visual-options">
                    <button class="fe-visual-option" :class="{ active: fieldEditorVisualFormat.displayAs === 'inline' }" @click="fieldEditorVisualFormat.displayAs = 'inline'; buildVisualExpression()">
                      <i class="fa fa-align-left"></i> Inline
                    </button>
                    <button class="fe-visual-option" :class="{ active: fieldEditorVisualFormat.displayAs === 'list' }" @click="fieldEditorVisualFormat.displayAs = 'list'; buildVisualExpression()">
                      <i class="fa fa-list"></i> List
                    </button>
                    <button class="fe-visual-option" :class="{ active: fieldEditorVisualFormat.displayAs === 'numbered' }" @click="fieldEditorVisualFormat.displayAs = 'numbered'; buildVisualExpression()">
                      <i class="fa fa-list-ol"></i> Numbered
                    </button>
                  </div>
                </div>
                <div class="fe-visual-row" v-if="fieldEditorVisualFormat.displayAs === 'inline'">
                  <label class="fe-visual-label">Separator</label>
                  <input class="fe-input fe-visual-input" v-model="fieldEditorVisualFormat.separator" @input="buildVisualExpression" placeholder=", " />
                </div>
              </template>

              <!-- Object -->
              <template v-if="fieldEditorValueType === 'object'">
                <div class="fe-visual-row">
                  <label class="fe-visual-label">Display property</label>
                  <select class="fe-input fe-visual-select" v-model="fieldEditorVisualFormat.objectProperty" @change="buildVisualExpression">
                    <option value="">-- show all (JSON) --</option>
                    <option v-for="k in fieldEditorObjectKeys" :key="k.value" :value="k.value">{{ k.text }}</option>
                  </select>
                </div>
              </template>

              <!-- Scalar text transforms -->
              <template v-if="fieldEditorValueType === 'scalar'">
                <div class="fe-visual-row" v-if="typeof fieldEditorSourceValue === 'string'">
                  <label class="fe-visual-label">Text transform</label>
                  <div class="fe-visual-options fe-visual-options--wrap">
                    <button class="fe-visual-option" :class="{ active: fieldEditorVisualFormat.textTransform === '' }" @click="fieldEditorVisualFormat.textTransform = ''; buildVisualExpression()">None</button>
                    <button class="fe-visual-option" :class="{ active: fieldEditorVisualFormat.textTransform === 'upper' }" @click="fieldEditorVisualFormat.textTransform = 'upper'; buildVisualExpression()">UPPER</button>
                    <button class="fe-visual-option" :class="{ active: fieldEditorVisualFormat.textTransform === 'lower' }" @click="fieldEditorVisualFormat.textTransform = 'lower'; buildVisualExpression()">lower</button>
                    <button class="fe-visual-option" :class="{ active: fieldEditorVisualFormat.textTransform === 'capitalize' }" @click="fieldEditorVisualFormat.textTransform = 'capitalize'; buildVisualExpression()">Capitalize</button>
                  </div>
                </div>
                <div class="fe-visual-row" v-if="typeof fieldEditorSourceValue === 'number'">
                  <label class="fe-visual-label">Number format</label>
                  <div class="fe-visual-options fe-visual-options--wrap">
                    <button class="fe-visual-option" :class="{ active: fieldEditorVisualFormat.numberFormat === '' }" @click="fieldEditorVisualFormat.numberFormat = ''; buildVisualExpression()">None</button>
                    <button class="fe-visual-option" :class="{ active: fieldEditorVisualFormat.numberFormat === 'round' }" @click="fieldEditorVisualFormat.numberFormat = 'round'; buildVisualExpression()">Round</button>
                    <button class="fe-visual-option" :class="{ active: fieldEditorVisualFormat.numberFormat === 'toFixed2' }" @click="fieldEditorVisualFormat.numberFormat = 'toFixed2'; buildVisualExpression()">2 decimals</button>
                    <button class="fe-visual-option" :class="{ active: fieldEditorVisualFormat.numberFormat === 'currency' }" @click="fieldEditorVisualFormat.numberFormat = 'currency'; buildVisualExpression()">Currency</button>
                    <button class="fe-visual-option" :class="{ active: fieldEditorVisualFormat.numberFormat === 'percent' }" @click="fieldEditorVisualFormat.numberFormat = 'percent'; buildVisualExpression()">Percent</button>
                  </div>
                </div>
                <div class="fe-visual-row" v-if="fieldEditorSourceValueLooksLikeDate">
                  <label class="fe-visual-label">Date format</label>
                  <div class="fe-visual-options fe-visual-options--wrap">
                    <button class="fe-visual-option" :class="{ active: fieldEditorVisualFormat.dateFormat === '' }" @click="fieldEditorVisualFormat.dateFormat = ''; buildVisualExpression()">None</button>
                    <button class="fe-visual-option" :class="{ active: fieldEditorVisualFormat.dateFormat === 'DD.MM.YYYY' }" @click="fieldEditorVisualFormat.dateFormat = 'DD.MM.YYYY'; buildVisualExpression()">DD.MM.YYYY</button>
                    <button class="fe-visual-option" :class="{ active: fieldEditorVisualFormat.dateFormat === 'MM/DD/YYYY' }" @click="fieldEditorVisualFormat.dateFormat = 'MM/DD/YYYY'; buildVisualExpression()">MM/DD/YYYY</button>
                    <button class="fe-visual-option" :class="{ active: fieldEditorVisualFormat.dateFormat === 'YYYY-MM-DD' }" @click="fieldEditorVisualFormat.dateFormat = 'YYYY-MM-DD'; buildVisualExpression()">YYYY-MM-DD</button>
                    <button class="fe-visual-option" :class="{ active: fieldEditorVisualFormat.dateFormat === 'DD.MM.YYYY HH:mm' }" @click="fieldEditorVisualFormat.dateFormat = 'DD.MM.YYYY HH:mm'; buildVisualExpression()">DD.MM.YYYY HH:mm</button>
                  </div>
                </div>
              </template>

              <!-- No special formatting needed -->
              <div v-if="!fieldEditorValueType" class="fe-visual-empty">
                <i class="fa fa-info-circle"></i> No data available for this field yet
              </div>
            </div>

            <!-- Advanced mode -->
            <div v-if="fieldEditorFormatMode === 'advanced'" class="fe-advanced-format">
              <textarea
                class="fe-input fe-textarea fe-format-expr"
                v-model="fieldEditor.format"
                placeholder="e.g. value|map('name')|join(', ')"
                rows="2"
              />
            </div>

            <!-- Preview (shown in both modes) -->
            <div v-if="fieldEditorFormatPreview !== null" class="fe-format-preview">
              <span class="fe-format-preview-label">Result:</span>
              <span class="fe-format-preview-value">{{ fieldEditorFormatPreview }}</span>
            </div>

            <!-- Generated expression shown in visual mode -->
            <div v-if="fieldEditorFormatMode === 'visual' && fieldEditor.format" class="fe-generated-expr">
              <code>{{ fieldEditor.format }}</code>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div v-if="fieldEditor.field" class="fe-popover__footer">
          <button class="fe-btn fe-btn--save" @click="saveField">
            <i class="fa fa-check"></i>
            <span>Save</span>
          </button>
          <button
            v-if="!isDocumentField(fieldEditor.field)"
            class="fe-btn fe-btn--remove"
            @click="removeSavedField"
          >
            <i class="fa fa-trash"></i>
            <span>Remove</span>
          </button>
        </div>
      </div>
    </transition>

    <!-- Add / Reply Comment Dialog -->
    <main-dialog
      :show="commentDialogOpen"
      :title="newComment.replyTo ? 'Reply to Comment' : (newComment.isGlobal ? 'Add Global Comment' : 'Add Comment')"
      max-width="480px"
      @close="commentDialogOpen = false"
    >
      <!-- Replying to block -->
      <div v-if="newComment.replyTo" class="cd-reply-context">
        <div class="cd-reply-context__label">
          <i class="fa fa-reply"></i>
          Replying to <strong>{{ newComment.replyTo.author || 'Unknown' }}</strong>
        </div>
        <div class="cd-reply-context__quote">{{ (newComment.replyTo.content || '').substring(0, 120) }}{{ (newComment.replyTo.content || '').length > 120 ? '…' : '' }}</div>
      </div>

      <!-- Selection context -->
      <div v-if="!newComment.isGlobal && !newComment.replyTo && newComment.hasSelection && newComment.selectedText" class="cd-reply-context cd-reply-context--selection">
        <div class="cd-reply-context__label"><i class="fa fa-i-cursor"></i> Selected text</div>
        <div class="cd-reply-context__quote">{{ (newComment.selectedText || '').substring(0, 100) }}{{ (newComment.selectedText || '').length > 100 ? '…' : '' }}</div>
      </div>

      <textarea
        ref="commentTextarea"
        v-model="newComment.content"
        class="cd-comment-input"
        :placeholder="newComment.replyTo ? 'Write your reply…' : 'Write a comment…'"
        rows="4"
        @keydown.enter.exact.prevent="saveComment"
        @keydown.enter.shift.exact="newComment.content += '\n'"
      />

      <template #footer>
        <button class="cd-dialog-btn cd-dialog-btn--cancel" @click="commentDialogOpen = false">Cancel</button>
        <button class="cd-dialog-btn cd-dialog-btn--primary" :disabled="!newComment.content.trim() || savingComment" @click="saveComment">
          <b-spinner v-if="savingComment" small />
          <span v-else>{{ newComment.replyTo ? 'Send Reply' : 'Save Comment' }}</span>
        </button>
      </template>
    </main-dialog>

    <!-- Global Comments Dialog -->
    <main-dialog
      :show="globalCommentsModal"
      title="Discussion"
      max-width="600px"
      @close="globalCommentsModal = false"
    >
      <div class="gc-chat">
        <!-- Messages -->
        <div class="gc-messages" ref="chatMessages">
          <div v-if="globalComments.length === 0" class="gc-empty">
            <i class="fa fa-comments"></i>
            <p>No messages yet. Start the conversation!</p>
          </div>

          <div v-for="comment in globalComments" :key="comment.id" class="gc-thread">
            <div class="gc-msg" :class="{ 'is-deleting': deletingCommentId === comment.id }">
              <div class="gc-avatar">{{ (comment.author || 'U').charAt(0).toUpperCase() }}</div>
              <div class="gc-msg-body">
                <div class="gc-msg-meta">
                  <span class="gc-author">{{ comment.author || 'Unknown' }}</span>
                  <span class="gc-time">{{ formatDate(comment.time) }}</span>
                  <div class="gc-actions">
                    <button class="gc-action-btn" @click="replyToGlobalComment(comment)" title="Reply"><i class="fa fa-reply"></i></button>
                    <button v-if="canDeleteComment(comment)" class="gc-action-btn gc-action-btn--danger" @click="confirmDeleteGlobalComment(comment)" title="Delete"><i class="fa fa-trash"></i></button>
                  </div>
                </div>
                <div class="gc-bubble">{{ comment.content }}</div>
              </div>
            </div>

            <div v-if="comment.replies && comment.replies.length > 0" class="gc-replies">
              <div v-for="reply in comment.replies" :key="reply.id" class="gc-reply" :class="{ 'is-deleting': deletingCommentId === reply.id }">
                <div class="gc-avatar gc-avatar--sm">{{ (reply.author || 'U').charAt(0).toUpperCase() }}</div>
                <div class="gc-msg-body">
                  <div class="gc-msg-meta">
                    <span class="gc-author">{{ reply.author || 'Unknown' }}</span>
                    <span class="gc-time">{{ formatDate(reply.time) }}</span>
                    <div class="gc-actions">
                      <button v-if="canDeleteComment(reply)" class="gc-action-btn gc-action-btn--danger" @click="confirmDeleteGlobalComment(reply)" title="Delete"><i class="fa fa-trash"></i></button>
                    </div>
                  </div>
                  <div class="gc-bubble gc-bubble--reply">{{ reply.content }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Input -->
        <div class="gc-input-area">
          <div v-if="replyingToGlobalComment" class="gc-reply-banner">
            <i class="fa fa-reply"></i>
            Replying to <strong>{{ replyingToGlobalComment.author }}</strong>
            <button class="gc-cancel-reply" @click="replyingToGlobalComment = null"><i class="fa fa-times"></i></button>
          </div>
          <div class="gc-input-row">
            <textarea
              ref="globalCommentTextarea"
              v-model="globalCommentText"
              class="gc-textarea"
              placeholder="Write a message… (Enter to send, Shift+Enter for new line)"
              rows="2"
              @keydown.enter.exact.prevent="sendGlobalComment"
              @keydown.enter.shift.exact="globalCommentText += '\n'"
            />
            <button
              class="gc-send-btn"
              @click="sendGlobalComment"
              :disabled="!globalCommentText.trim() || sendingGlobalComment"
            >
              <b-spinner v-if="sendingGlobalComment" small />
              <i v-else class="fa fa-paper-plane"></i>
            </button>
          </div>
        </div>
      </div>
    </main-dialog>

    <!-- New Variant Modal -->
    <main-dialog
      :show="showNewVariantModal"
      title="Create New Variant"
      @ok="newVariantSubmit"
      @close="showNewVariantModal = false"
      ok-title="Create"
      ok-variant="success"
    >
      <b-form-group label="Variant Name" label-for="variant-name">
        <b-form-input
          id="variant-name"
          v-model="newVariantName"
          placeholder="Enter variant name..."
          @keyup.enter="newVariantSubmit"
        />
      </b-form-group>
    </main-dialog>

    <!-- Rename Section Modal -->
    <main-dialog
      :show="renameSectionDialog"
      title="Rename Section"
      @close="renameSectionDialog = false"
      @ok="submitRenameSection"
      ok-title="Rename"
    >
      <b-form-group label="Section Name" label-for="section-name">
        <b-form-input
          id="section-name"
          v-model="renameSectionName"
          placeholder="Enter section name..."
          @keyup.enter="submitRenameSection"
        />
      </b-form-group>
    </main-dialog>

  </b-card>
</template>

<script>
import { Editor, EditorContent, BubbleMenu } from "@tiptap/vue-2";
import StarterKit from "@tiptap/starter-kit";
import Highlight from "@tiptap/extension-highlight";
import TextAlign from "@tiptap/extension-text-align";
import { Color } from "@tiptap/extension-color";
import TextStyle from "@tiptap/extension-text-style";
import Image from "@tiptap/extension-image";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import FontFamily from "@tiptap/extension-font-family";
import EditorSelect from "./DocumentEditor/EditorSelect.vue";
import Vue, { nextTick } from "vue";
import _ from "lodash";
import SourceTab from "./DocumentEditor/SourceTab.vue";
import FieldsTab from "./DocumentEditor/FieldsTab.vue";
import ContextMenu from "./DocumentEditor/ContextMenu.vue";
import MainDialog from "@/components/MainDialog.vue";
import loadExtensions from "./DocumentEditor/extensions";
import { evaluateExpressionSync, listTransforms } from "@/helpers/jexlFormatter";

export default {
  name: "IdentityField_Text_Editor",
  components: {
    EditorContent,
    BubbleMenu,
    Editor,
    EditorSelect,
    SourceTab,
    FieldsTab,
    ContextMenu,
    MainDialog,
  },

  data: function () {
    return {
      showLeftSidebar: true,
      showRightSidebar: true,
      loadingDocument: true,
      currentPage: 1,
      totalPages: 1,
      savingTemplate: false,
      isReadyForAutosave: false,
      autosavingTemplate: false,
      autoSave: true,
      hasUnsavedChanges: false,
      lastSavedContent: '',
      // templates: null,
      // template: null,
      componentsSettings: [],
      ready: false,
      contract: null,

      value: "", //`<p>{{ if .isGood }} Good {{ end }}</p><p><strong>{{ .price }}</strong></p><p>{{ if or .isPaid .isSent }} {{ if .isGood }} {{ .vat }} {{ end }} {{ end }}</p><p>Me: {{ .user.name }} {{ .user.lastname }}</p><p>{{ range .items }} {{ .title }}: {{ .amount }}x {{ .price }} = {{ .totalPrice }} {{ end }}</p><p>Waht is your namey?</p>`,
      version: 0,
      errors: [],

      newVariantName: "",
      showNewVariantModal: false,

      showDocDropdown: false,
      docSearchText: "",
      docList: [],
      docDropdownStyle: {},

      addSourceSidebar: false,
      data: null,
      documentFields: null,
      fieldDefinitions: {},
      fields: [],
      groupLabels: [],
      labels: [],
      sourceList: [
        {
          text: "Component",
          value: "component",
        },
      ],
      sources: [], // []
      sourceData: [],
      fieldValueOptions: {},

      componentsList: [],

      templateTransformed: null,
      editor: null,
      textColor: null,
      fontFamily: null,
      fonts: [
        "Arial",
        "Aptos",
        "Courier New",
        "Georgia",
        "Inter",
        "Comic Sans",
        "Serif",
        "Monospace",
        "Cursive",
      ],
      marginPresets: [
        { label: "None", value: { top: 0, right: 0, bottom: 0, left: 0 } },
        { label: "Narrow (10px)", value: { top: 10, right: 10, bottom: 10, left: 10 } },
        { label: "Normal (20px)", value: { top: 20, right: 20, bottom: 20, left: 20 } },
        { label: "Moderate (30px)", value: { top: 30, right: 30, bottom: 30, left: 30 } },
        { label: "Wide (40px)", value: { top: 40, right: 40, bottom: 40, left: 40 } },
        { label: "Extra Wide (60px)", value: { top: 60, right: 60, bottom: 60, left: 60 } },
      ],
      image: null,
      pageNumberFormat: '{{ .page }} / {{ .pages }}',
      lineHeightPresets: [
        { label: 'Single (1.0)', value: '1' },
        { label: '1.15', value: '1.15' },
        { label: '1.5', value: '1.5' },
        { label: 'Double (2.0)', value: '2' },
        { label: '2.5', value: '2.5' },
        { label: 'Triple (3.0)', value: '3' },
      ],


      currentSettings: null,
      currentSelection: null,
      savedFontSizeSelection: null,

      tableBorderWidth: '1',
      tableBorderStyle: 'solid',
      tableBorderColor: '#000000',

      contextMenu: {
        visible: false,
        x: 0,
        y: 0,
        hasSelection: false,
        isConditionSelected: false,
        conditionSelectionOptions: null,
        canMerge: false,
      },

      rootSource: null,
      rootSourceValue: null,
      rootSourceTag: null,
      rootSourceComponentFields: null,
      rootSourceValueLabel: null, // "uuid"

      newComment: {
        content: '',
        variant: 'default',
        isGlobal: false,
        hasSelection: false,
        selectedText: '',
        from: null,
        to: null,
        replyTo: null,
      },

      commentDialogOpen: false,
      savingComment: false,

      hoveredCommentId: null,
      flashCommentId: null,
      deletingCommentId: null,

      globalCommentsModal: false,
      globalCommentText: '',
      sendingGlobalComment: false,
      replyingToGlobalComment: null,

      groupEditor: {
        show: false,
        value: null,
        label: null,
      },

      fieldEditor: {
        label: null,
        field: null,
        source: "text",
        format: "",
        sources: [],
        value: null,
        settings: null,
        data: null,
        tooltipTarget: null,
      },
      fieldEditorFormatMode: 'visual',
      fieldEditorVisualFormat: {
        property: '',
        separator: ', ',
        displayAs: 'inline',
        objectProperty: '',
        textTransform: '',
        numberFormat: '',
        dateFormat: '',
      },
      fieldEditorDummyElement: null,
      fieldEditorRect: null,

      createFilePopup: false,
      printFile: false,
      printingPreview: false,
      formattingDocument: false,
      formattingProgress: '',
      isInitialFormat: true,
      outputFileName: null,
      textEditorField: {
        name: "html",
        key: "html",
        type: "document",
        module: "text",
        roles: {
          see: [],
          edit: [],
        },
        constraints: {
          default: null,
        },
      },

      tabPosition: {
        sources: "top",
        fields: "top",
      },

      sectionNumbering: 'per-section',
      currentSectionName: '',
      currentSectionPage: 1,
      currentSectionTotalPages: 1,
      sectionCount: 1,

      renameSectionDialog: false,
      renameSectionIndex: null,
      renameSectionName: '',

      revisions: [],
      loadingRevisions: false,
      reviewingRevision: null,
    };
  },

  computed: {
    isGenerateMode() {
      return this.$route.query.mode === "generate";
    },
    mode: {
      get() {
        const m = this.$route.query.mode || "editor";
        if (m === "generate") return "document";
        return m;
      },

      set(value) {
        this.$router.replace({
          query: { ...this.$route.query, mode: value || undefined },
        });
      },
    },
    content: {
      get() {
        return this.value;
      },

      set(val) {
        this.value = val;
        this.$emit("input", val);
      },
    },

    documentVariant: {
      get() {
        return this.$route.query.variant || "default";
      },

      set(value) {
        this.$router.replace({
          query: { ...this.$route.query, variant: value || undefined },
        });
      },
    },

    currentDocName() {
      const doc = this.$route.query.document;
      return doc ? doc.replace(/^\//, "") : "";
    },

    filteredDocList() {
      if (!this.docSearchText) return this.docList;
      const search = this.docSearchText.toLowerCase();
      return this.docList.filter((d) => d.name.toLowerCase().includes(search));
    },

    defaultColor() {
      return "#000";
    },

    fontSize: {
      get() {
        return this.editor.getAttributes("textStyle").fontSize || "12px";
      },

      set(val) {
        if (val > 100) {
          val = 100;
        }

        this.editor.commands.setFontSize(val).run();
      },
    },

    fontSizeDisplay() {
      const raw = this.editor.getAttributes("textStyle").fontSize || "12px";
      return parseInt(raw, 10) || 12;
    },

    margins: {
      get() {
        const { margin } = this.editor.getAttributes("main");
        return this.parseMargins(margin);
      },

      set(val) {
        const m = typeof val === 'object' ? val : this.parseMargins(val);
        this.editor.commands.setDocumentMargins(m);
        this.$nextTick(() => this.debouncedAutoPageBreak());
      },
    },

    currentPageNumberFormat() {
      if (!this.editor?.state) return null;
      let fmt = null;
      this.editor.state.doc.descendants(node => {
        if (!fmt && node.type.name === 'pageNumber') fmt = node.attrs.format;
      });
      return fmt;
    },

    currentLineHeight() {
      if (!this.editor) return null;
      return this.editor.getAttributes('paragraph').lineHeight || null;
    },

    currentTextPadding() {
      if (!this.editor) return { top: 0, right: 0, bottom: 0, left: 0 };
      const raw = this.editor.getAttributes('paragraph').textPadding;
      return this.parseMargins(raw);
    },

    currentSectionBackground() {
      const attrs = this.getCurrentSectionAttrs();
      return attrs?.backgroundImage || null;
    },

    hasPageHeader() {
      if (!this.editor?.state) return false;
      void this.totalPages;
      const attrs = this.getCurrentSectionAttrs();
      if (!attrs) return false;
      const sectionNode = this._getCurrentSectionNode();
      if (!sectionNode) return false;
      let found = false;
      sectionNode.forEach(m => {
        if (m.type.name === 'main' && m.child(0)?.type.name === 'pageHeader') found = true;
      });
      return found;
    },

    headerAttrs() {
      const d = { paddingTop: 0, paddingRight: 0, paddingBottom: 0, paddingLeft: 0, backgroundColor: null };
      if (!this.editor?.state || !this.hasPageHeader) return d;
      void this.totalPages;
      const sectionNode = this._getCurrentSectionNode();
      if (!sectionNode) return d;
      let found = null;
      sectionNode.forEach(m => {
        if (m.type.name === 'main' && !found) {
          m.forEach(c => { if (c.type.name === 'pageHeader' && !found) found = c.attrs; });
        }
      });
      return found ? { ...d, ...found } : d;
    },

    footerAttrs() {
      const d = { paddingTop: 0, paddingRight: 0, paddingBottom: 0, paddingLeft: 0, backgroundColor: null };
      if (!this.editor?.state || !this.hasPageFooter) return d;
      void this.totalPages;
      const sectionNode = this._getCurrentSectionNode();
      if (!sectionNode) return d;
      let found = null;
      sectionNode.forEach(m => {
        if (m.type.name === 'main' && !found) {
          m.forEach(c => { if (c.type.name === 'pageFooter' && !found) found = c.attrs; });
        }
      });
      return found ? { ...d, ...found } : d;
    },

    hasPageFooter() {
      if (!this.editor?.state) return false;
      void this.totalPages;
      const attrs = this.getCurrentSectionAttrs();
      if (!attrs) return false;
      const sectionNode = this._getCurrentSectionNode();
      if (!sectionNode) return false;
      let found = false;
      sectionNode.forEach(m => {
        if (m.type.name === 'main' && m.lastChild?.type.name === 'pageFooter') found = true;
      });
      return found;
    },

    editorSections() {
      if (!this.editor || !this.editor.view) return [];
      void this.totalPages;
      void this.sectionCount;
      const sectionEls = this.editor.view.dom.querySelectorAll('section.document-section');
      return Array.from(sectionEls).map((el, idx) => ({
        name: el.getAttribute('data-name') || `Document ${idx + 1}`,
        pageCount: el.querySelectorAll('main.page').length,
        element: el,
      }));
    },

    tableCellBorders() {
      const attrs = this.editor.getAttributes("tableCell");
      return {
        top: attrs.borderTop || "1px solid #000",
        right: attrs.borderRight || "1px solid #000",
        bottom: attrs.borderBottom || "1px solid #000",
        left: attrs.borderLeft || "1px solid #000",
      };
    },

    tableCellBgColor() {
      const attrs = this.editor.getAttributes("tableCell");
      return attrs.backgroundColor || null;
    },

    enablePages() {
      const { enable_pages = false } = this.constraints;

      return enable_pages;
    },

    independentFields() {
      return Object.keys(this.data)
        .filter((key) => {
          return !_.isObject(this.data[key]);
        })
        .map((key) => {
          const field = this.fields.find(({ field }) => field === key);
          return { value: key, name: field && field.label ? field.label : key };
        });
    },

    fieldGroups() {
      return Object.keys(this.data)
        .filter((key) => {
          return _.isObject(this.data[key]);
        })
        .map((prefix) => {
          const group = this.groupLabels.find(({ value }) => value === prefix);

          return {
            prefix,
            name: group && group.text ? group.text : prefix,
            fields: Object.keys(this.data[prefix]).map((key) => {
              const field = this.fields.find(({ field }) => field === key);
              return {
                value: `${prefix}.${key}`,
                name: field && field.label ? field.label : key,
              };
            }),
          };
        });
    },

    formatingFieldOptions() {
      const { data } =
        this.sourceData.find(
          (d) =>
            d.tag == this.fieldEditor.source || this.fieldEditor == field.source
        ) || {};
      const { sourceField } = this.fieldEditor.data || {};
      const value = _.get(data, sourceField, null);

      let a = [];

      if (!value) {
        return [];
      }

      if (typeof value == "object") {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            a = Object.keys(value[0]);
          }
        } else {
          a = Object.keys(value);
        }
      }

      return a;
    },

    fieldEditorFieldMappedValue() {
      const { data } = this.fieldEditor || {};
      const { sourceField } = data || {};

      return sourceField;
    },

    fieldEditorIsSourceFromTag() {
      const { source } = this.fieldEditor;

      const f = this.sources.find((s) => s.tag == source);
      if (f) return true;

      return false;
    },

    fieldEditorTagSource() {
      const { source } = this.fieldEditor;

      return this.sources.find((s) => s.tag == source);
    },

    fieldEditorFormatPreview() {
      const expr = this.fieldEditor.format;
      if (!expr) return null;

      const { data } =
        this.sourceData.find(
          (d) =>
            d.tag == this.fieldEditor.source ||
            d.source == this.fieldEditor.source
        ) || {};
      if (!data) return null;

      const { sourceField } = this.fieldEditor.data || {};
      const value = _.get(data, sourceField, null);
      if (value == null) return null;

      const result = evaluateExpressionSync(expr, { value, data });
      if (result == null) return "(invalid expression)";
      if (typeof result === "object") return JSON.stringify(result);
      return String(result);
    },

    fieldEditorSourceValue() {
      if (!this.fieldEditorIsSourceFromTag || !this.fieldEditorFieldMappedValue) return null;
      const { data } =
        this.sourceData.find(
          (d) => d.tag == this.fieldEditor.source || d.source == this.fieldEditor.source
        ) || {};
      if (!data) return null;
      const { sourceField } = this.fieldEditor.data || {};
      return _.get(data, sourceField, null);
    },

    fieldEditorValueType() {
      const val = this.fieldEditorSourceValue;
      if (val == null) return null;
      if (Array.isArray(val)) {
        if (val.length > 0 && typeof val[0] === 'object' && val[0] !== null) return 'array_objects';
        return 'array';
      }
      if (typeof val === 'object') return 'object';
      return 'scalar';
    },

    fieldEditorObjectKeys() {
      const val = this.fieldEditorSourceValue;
      if (val == null) return [];
      if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'object') {
        return Object.keys(val[0]).map(k => ({ text: k, value: k }));
      }
      if (typeof val === 'object' && !Array.isArray(val)) {
        return Object.keys(val).map(k => ({ text: k, value: k }));
      }
      return [];
    },

    fieldEditorValueTypeIcon() {
      const map = { array: 'fa fa-list', array_objects: 'fa fa-table', object: 'fa fa-cube', scalar: 'fa fa-font' };
      return map[this.fieldEditorValueType] || 'fa fa-question';
    },

    fieldEditorValueTypeLabel() {
      const map = { array: 'Array', array_objects: 'Array of objects', object: 'Object', scalar: 'Value' };
      return map[this.fieldEditorValueType] || 'Unknown';
    },

    fieldEditorSourceValueLooksLikeDate() {
      const val = this.fieldEditorSourceValue;
      if (typeof val !== 'string') return false;
      return /^\d{4}-\d{2}-\d{2}/.test(val) || /^\d{2}[./]\d{2}[./]\d{4}/.test(val);
    },

    // getSourceFieldModule() {
    //   if (!this.fieldEditor.settings) return null;
    //   const field = this.fieldEditor.settings.fields.find(
    //     (f) => f.key == this.fieldEditorFieldMappedValue
    //   );
    //   if (!field) return null;
    //   return field.module;
    // },

    // getSourceFieldType() {
    //   if (!this.fieldEditor.settings) return null;
    //   const field = this.fieldEditor.settings.fields.find(
    //     (f) => f.key == this.fieldEditorFieldMappedValue
    //   );
    //   if (!field) return null;
    //   return field.type;
    // },

    // canShowOptionsForField() {
    //   if (
    //     this.getSourceFieldModule == "data" &&
    //     this.getSourceFieldType == "json"
    //   ) {
    //     console.log("fieldEditor", this.fieldEditor);
    //     return true;
    //   }
    // },

    sourceTabBinds() {
      // <source-tab
      //     :sources="sources"
      //     :tab-position="tabPosition"
      //     :can-edit="$can('edit_document_template')"
      //     :get-source-value="sourceDataValue"
      //     :on-fetch="fetchSourceData"
      //     @open-add-source="addSourceSidebar = true"
      //     @change-tab-position="
      //       (position) => (tabPosition.sources = position)
      //     "
      //     @remove-source="removeSource"
      //     @update-source="
      //       ({ source, value }) => sourceDataSelected(source, value)
      //     "
      //   />
      return {
        props: {
          sources: this.sources,
          tabPosition: this.tabPosition.sources,
          canEdit: this.$can("edit_document protokol-media"),
          getSourceValue: this.sourceDataValue,
          onFetch: this.fetchSourceData,
        },
        on: {
          "open-add-source": () => (this.addSourceSidebar = true),
          "change-tab-position": (position) =>
            (this.tabPosition.sources = position),
          "remove-source": this.removeSource,
          "update-source": ({ source, value }) =>
            this.sourceDataSelected(source, value),
        },
      };
    },

    fieldsTabBinds() {
      // <fields-tab
      //   :source-groups="fieldGroups"
      //   :independent-fields="independentFields"
      //   :can-edit="$can('edit_document_template')"
      //   :get-field-value="getFieldValue"
      //   :get-field-value-options="getFieldValueOptions"
      //   :is-field-populated="isFieldPopulated"
      //   @field-click="({ event, field }) => onFieldClick(event, field)"
      //   @update-field-value="
      //     ({ field, value }) => (data[field.value] = value)
      //   "
      //   @change-tab-position="(position) => (tabPosition.fields = position)"
      //   @edit-group="editGroup"
      // />
      return {
        props: {
          fieldGroups: this.fieldGroups,
          independentFields: this.independentFields,
          tabPosition: this.tabPosition.fields,
          canEdit: this.$can("edit_document protokol-media"),
          getFieldsOfTheGroup: this.getFieldsOfTheGroup,
          getFieldGroupLabel: this.getFieldGroupLabel,
          getFieldValue: this.getFieldValue,
          getFieldValueOptions: this.getFieldValueOptions,
          isFieldPopulated: this.isFieldPopulated,
        },
        on: {
          "field-click": ({ event, field }) => this.onFieldClick(event, field),
          "update-field-value": ({ field, value }) =>
            (this.data[field.value] = value),
          "change-tab-position": (position) =>
            (this.tabPosition.fields = position),
          "edit-group": this.editGroup,
        },
      };
    },

    storedDoc() {
      return this.$store.getters["documents/document"];
    },

    currentVariantData() {
      if (!this.storedDoc) return null;
      return this.storedDoc.variants.find(
        (v) => v.name === this.documentVariant
      );
    },

    documentComments() {
      if (!this.currentVariantData || !this.currentVariantData.comments) return [];
      if (!this.editor || !this.editor.state) return [];
      
      // Get comments from the current variant
      const allComments = this.currentVariantData.comments.map(comment => {
        const from = parseInt(comment.from);
        const to = parseInt(comment.to);
        
        // Extract plain text using ProseMirror's textBetween method
        let text = '';
        try {
          text = this.editor.state.doc.textBetween(from, to, ' ');
        } catch (error) {
          console.error('Error extracting text for comment:', error, comment);
          text = '';
        }
        
        return {
          id: comment.id,
          content: comment.content,
          text: text,
          from: from,
          to: to,
          author: comment.createdBy ? comment.createdBy.name : 'Unknown',
          authorUuid: comment.createdBy ? comment.createdBy.uuid : null,
          time: comment.createdBy ? comment.createdBy.time : null,
          replyTo: comment.replyTo || null,
          replies: []
        };
      });
      
      // Organize comments with their replies
      const topLevelComments = [];
      const commentMap = {};
      
      // First pass: create a map of all comments
      allComments.forEach(comment => {
        commentMap[comment.id] = comment;
      });
      
      // Second pass: organize into tree structure
      allComments.forEach(comment => {
        if (comment.replyTo && commentMap[comment.replyTo]) {
          // This is a reply, add it to the parent's replies array
          commentMap[comment.replyTo].replies.push(comment);
        } else {
          // This is a top-level comment
          topLevelComments.push(comment);
        }
      });
      
      return topLevelComments;
    },

    globalComments() {
      if (!this.storedDoc || !this.storedDoc.comments) return [];
      
      // Get all global comments (from root level comments array)
      const allComments = this.storedDoc.comments.map(comment => {
        return {
          id: comment.id,
          content: comment.content,
          author: comment.createdBy ? comment.createdBy.name : 'Unknown',
          authorUuid: comment.createdBy ? comment.createdBy.uuid : null,
          time: comment.createdBy ? comment.createdBy.time : null,
          replyTo: comment.replyTo || null,
          replies: []
        };
      });
      
      // Organize comments with their replies
      const topLevelComments = [];
      const commentMap = {};
      
      // First pass: create a map of all comments
      allComments.forEach(comment => {
        commentMap[comment.id] = comment;
      });
      
      // Second pass: organize into tree structure
      allComments.forEach(comment => {
        if (comment.replyTo && commentMap[comment.replyTo]) {
          // This is a reply, add it to the parent's replies array
          commentMap[comment.replyTo].replies.push(comment);
        } else {
          // This is a top-level comment
          topLevelComments.push(comment);
        }
      });
      
      return topLevelComments;
    },

    variantOptions() {
      if (!this.storedDoc || !this.storedDoc.variants) return [];
      return this.storedDoc.variants.map(v => ({
        value: v.name,
        text: v.name
      }));
    },

    totalGlobalCommentsCount() {
      if (!this.storedDoc || !this.storedDoc.comments) return 0;
      return this.storedDoc.comments.length;
    },

    fieldEditorPopoverStyle() {
      if (!this.fieldEditorRect) return { display: 'none' };
      const r = this.fieldEditorRect;
      return {
        position: 'fixed',
        top: (r.bottom + 8) + 'px',
        left: Math.max(8, r.left + (r.width / 2) - 160) + 'px',
      };
    },
  },

  watch: {
    currentPageNumberFormat(fmt) {
      if (fmt && fmt !== this.pageNumberFormat) {
        this.pageNumberFormat = fmt;
      }
    },
    content(val, prev) {
      const wrapped = this.wrapContentInSection(val);
      const div = document.createElement("div");
      div.innerHTML = wrapped;

      if (div.innerHTML !== this.editor.getHTML()) {
        this.editor.commands.setContent(wrapped, false, {
          preserveWhitespace: "full",
        });
      }

      if (this.isReadyForAutosave && !this.reviewingRevision) {
        this.hasUnsavedChanges = true;
        if (this.autoSave) this.debouncedSave();
      }
      this.isReadyForAutosave = true;
    },
    mode(val) {
      if (val === "document") {
        nextTick(() => {
          this.setupDocumentPreview();
          this.$nextTick(() => {
            this.reflowPreviewPages();
            // Recompute the page indicator against the now-visible preview pages
            // (the editor is display:none in this mode, so its rects are all 0).
            if (this._pageScrollHandler) this._pageScrollHandler();
          });
        });
      } else if (val === "editor") {
        this.$nextTick(() => {
          if (this._pageScrollHandler) this._pageScrollHandler();
        });
      }
    },
    "$route.query.document"(newDoc, oldDoc) {
      if (newDoc && newDoc !== oldDoc) {
        this.loadingDocument = true;
        this.revisions = [];
        const docName = newDoc.replace(/^\//, "");
        this.$store.dispatch("documents/fetchDocument", { path: docName })
          .then(() => { this.loadingDocument = false; })
          .catch(() => { this.loadingDocument = false; });
      }
    },
    storedDoc(doc) {
      this.loadDoc();
    },
    documentVariant(v) {
      this.loadDoc();
    },
  },

  methods: {
    setCellBorderAttrs(attrs) {
      this.editor.chain().focus().command(({ tr, state }) => {
        const { selection } = state;
        const isCellSelection = selection.constructor.name === 'CellSelection';
        if (isCellSelection) {
          selection.forEachCell((node, pos) => {
            Object.entries(attrs).forEach(([key, value]) => {
              tr.setNodeMarkup(pos, undefined, { ...node.attrs, [key]: value });
            });
          });
        } else {
          const $pos = selection.$from;
          for (let d = $pos.depth; d > 0; d--) {
            const node = $pos.node(d);
            if (node.type.name === 'tableCell' || node.type.name === 'tableHeader') {
              const pos = $pos.before(d);
              Object.entries(attrs).forEach(([key, value]) => {
                tr.setNodeMarkup(pos, undefined, { ...node.attrs, [key]: value });
              });
              break;
            }
          }
        }
        return true;
      }).run();
    },

    applyBorderToSide(side) {
      const val = `${this.tableBorderWidth}px ${this.tableBorderStyle} ${this.tableBorderColor}`;
      if (side === 'all') {
        this.setCellBorderAttrs({ borderTop: val, borderRight: val, borderBottom: val, borderLeft: val });
      } else {
        const attr = `border${side.charAt(0).toUpperCase() + side.slice(1)}`;
        this.setCellBorderAttrs({ [attr]: val });
      }
    },

    removeBorderFromSide(side) {
      if (side === 'all') {
        this.setCellBorderAttrs({ borderTop: 'none', borderRight: 'none', borderBottom: 'none', borderLeft: 'none' });
      } else {
        const attr = `border${side.charAt(0).toUpperCase() + side.slice(1)}`;
        this.setCellBorderAttrs({ [attr]: 'none' });
      }
    },

    setTableCellBgColor(color) {
      this.setCellBorderAttrs({ backgroundColor: color || null });
    },

    syncBorderControlsFromCell() {
      const attrs = this.editor.getAttributes("tableCell");
      if (!attrs) return;
      const border = attrs.borderTop || attrs.borderRight || attrs.borderBottom || attrs.borderLeft || "1px solid #000";
      if (border && border !== 'none') {
        const parts = border.match(/^(\d+)px\s+(solid|dashed|dotted|double|none)\s+(.+)$/);
        if (parts) {
          this.tableBorderWidth = parts[1];
          this.tableBorderStyle = parts[2];
          this.tableBorderColor = parts[3];
        }
      }
    },

    parseMargins(value) {
      if (!value) return { top: 10, right: 10, bottom: 10, left: 10 };
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        return { top: parseInt(value.top, 10) || 0, right: parseInt(value.right, 10) || 0, bottom: parseInt(value.bottom, 10) || 0, left: parseInt(value.left, 10) || 0 };
      }
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          if (typeof parsed === 'object' && parsed !== null) return this.parseMargins(parsed);
        } catch (e) { /* not JSON */ }
        const num = parseInt(value, 10);
        if (!isNaN(num)) return { top: num, right: num, bottom: num, left: num };
      }
      if (typeof value === 'number') return { top: value, right: value, bottom: value, left: value };
      return { top: 10, right: 10, bottom: 10, left: 10 };
    },

    isMarginPreset(preset) {
      const m = this.margins;
      return m.top === preset.top && m.right === preset.right && m.bottom === preset.bottom && m.left === preset.left;
    },

    updateMarginSide(side, value) {
      const m = { ...this.margins };
      m[side] = parseInt(value) || 0;
      this.margins = m;
    },

    applyMarginsToAllPages() {
      this.editor.commands.setAllDocumentMargins(this.margins);
      this.$nextTick(() => this.debouncedAutoPageBreak());
    },

    getCurrentSectionAttrs() {
      if (!this.editor?.state) return null;
      const node = this._getCurrentSectionNode();
      return node?.attrs || null;
    },

    _getCurrentSectionNode() {
      if (!this.editor?.state) return null;
      const { $from } = this.editor.state.selection;
      for (let d = $from.depth; d >= 0; d--) {
        if ($from.node(d).type.name === 'section') {
          return $from.node(d);
        }
      }
      return null;
    },

    triggerBackgroundImageUpload() {
      document.getElementById("backgroundImageImport").click();
    },

    backgroundImageChoosen(val) {
      if (!val) return;
      const reader = new FileReader();
      reader.readAsDataURL(val);
      reader.onload = () => {
        this.editor.commands.setSectionBackground(reader.result);
      };
    },

    removeBackgroundImage() {
      this.editor.commands.setSectionBackground(null);
    },

    updateAllPageNumberFormats() {
      const fmt = this.pageNumberFormat || '{{ .page }} / {{ .pages }}';
      const { state } = this.editor;
      const tr = state.tr;
      state.doc.descendants((node, pos) => {
        if (node.type.name === 'pageNumber' && node.attrs.format !== fmt) {
          tr.setNodeMarkup(tr.mapping.map(pos), undefined, { ...node.attrs, format: fmt });
        }
      });
      if (tr.docChanged) this.editor.view.dispatch(tr);
    },

    insertPageNumberWithFormat() {
      const fmt = this.pageNumberFormat || '{{ .page }} / {{ .pages }}';
      const node = this.editor.schema.nodes.pageNumber.create({ format: fmt });
      this.editor.view.dispatch(this.editor.state.tr.replaceSelectionWith(node));
    },

    updateTextPadding(side, value) {
      const current = { ...this.currentTextPadding };
      current[side] = parseInt(value) || 0;
      this.editor.commands.setTextPadding(current);
    },

    setHeaderFooterAttr(nodeType, attr, value) {
      const parsed = attr === 'backgroundColor' ? value : (parseInt(value) || 0);
      const { state } = this.editor;
      const { $from } = state.selection;
      let sectionPos = null, sectionNode = null;
      for (let d = $from.depth; d >= 0; d--) {
        if ($from.node(d).type.name === 'section') {
          sectionPos = $from.before(d);
          sectionNode = $from.node(d);
          break;
        }
      }
      if (!sectionNode) return;

      const tr = state.tr;
      sectionNode.forEach((mainNode, mainOffset) => {
        if (mainNode.type.name !== 'main') return;
        mainNode.forEach((child, childOffset) => {
          if (child.type.name === nodeType) {
            const pos = tr.mapping.map(sectionPos + 1 + mainOffset + 1 + childOffset);
            tr.setNodeMarkup(pos, undefined, { ...child.attrs, [attr]: parsed });
          }
        });
      });
      if (tr.docChanged) this.editor.view.dispatch(tr);
    },


    getPageContentChildren(pageEl) {
      return Array.from(pageEl.children).filter(el =>
        !el.classList.contains('page-header-overlay') &&
        !el.classList.contains('page-footer-overlay') &&
        !el.classList.contains('page-header-content') &&
        !el.classList.contains('page-footer-content')
      );
    },

    getEffectiveMaxHeight(pageEl, maxPageHeight) {
      const paddingBottom = parseFloat(window.getComputedStyle(pageEl).paddingBottom) || 0;
      const footer = pageEl.querySelector('.page-footer-content');
      let footerSpace = 0;
      if (footer) {
        const cs = window.getComputedStyle(footer);
        footerSpace = footer.getBoundingClientRect().height
          + (parseFloat(cs.marginTop) || 0)
          + (parseFloat(cs.marginBottom) || 0);
      }
      return maxPageHeight - paddingBottom - footerSpace;
    },

    formatDate(dateString) {
      if (!dateString || dateString === '0001-01-01T00:00:00Z') return 'N/A';
      
      const date = new Date(dateString);
      const options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      };
      
      return date.toLocaleDateString('en-US', options);
    },

    async toggleRevisions() {
      if (this.revisions.length > 0) return;
      await this.loadRevisions();
    },

    async loadRevisions() {
      if (!this.storedDoc) return;
      this.loadingRevisions = true;
      try {
        const docName = this.$route.query.document.replace(/^\//, "");
        const { data } = await this.$mediaHttp.get(`/v2/dms/documents/revisions/${docName}`);
        this.revisions = (data || []).sort((a, b) => new Date(b.updatedBy.time) - new Date(a.updatedBy.time));
      } catch (err) {
        console.error("Failed to load revisions:", err);
        this.revisions = [];
      } finally {
        this.loadingRevisions = false;
      }
    },

    async previewRevision(rev) {
      try {
        const docName = this.$route.query.document.replace(/^\//, "");
        const { data } = await this.$mediaHttp.get(`/v2/dms/documents/revision/${docName}`, {
          params: { version: rev.version },
        });
        this.reviewingRevision = rev;
        this.isReadyForAutosave = false;
        this.editor.setEditable(false);

        const variant = data.variants.find(v => v.name === this.documentVariant) || data.variants[0];
        if (variant) {
          const wrappedContent = this.wrapContentInSection(variant.content);
          this.editor.commands.setContent(wrappedContent, false);
          this.$nextTick(() => this.runAutoPageBreak());
        }
      } catch (err) {
        console.error("Failed to load revision:", err);
      }
    },

    exitRevisionPreview() {
      this.reviewingRevision = null;
      this.editor.setEditable(true);
      this.loadDoc();
    },

    restoreRevision() {
      if (!this.reviewingRevision) return;
      var revisionHTML = this.editor.getHTML();
      this.reviewingRevision = null;
      this.editor.setEditable(true);
      this.editor.commands.setContent(revisionHTML, false);
      this.content = this.editor.getHTML();
      this.isReadyForAutosave = true;
    },

    scrollToComment(comment) {
      if (!this.editor || !comment.from || !comment.to) return;
      
      // Set selection to the comment range and scroll into view
      this.editor.chain()
        .focus()
        .setTextSelection({ from: comment.from, to: comment.to })
        .run();
      
      // Flash highlight effect using CSS
      this.flashCommentId = comment.id;
      setTimeout(() => {
        this.flashCommentId = null;
      }, 1500);
    },

    highlightComment(comment, highlight) {
      if (!this.editor || !comment.from || !comment.to) return;
      
      // Store the hovered comment ID to apply CSS styling
      if (highlight) {
        this.hoveredCommentId = comment.id;
      } else {
        this.hoveredCommentId = null;
      }
    },

    openAddCommentModal() {
      console.log('openAddCommentModal called');
      // Get the current selection from the editor
      const { from, to } = this.editor.state.selection;
      const hasSelection = from !== to;
      
      // Reset the comment form
      // When called from toolbar, it's a global comment
      this.newComment = {
        content: '',
        variant: this.documentVariant,
        isGlobal: true, // Global by default when called from toolbar
        hasSelection: hasSelection,
        from: hasSelection ? from : null,
        to: hasSelection ? to : null,
        replyTo: null,
      };
      
      console.log('Opening modal, newComment:', this.newComment);
      this.commentDialogOpen = true;
    },

    openAddCommentModalFromContext() {
      console.log('openAddCommentModalFromContext called');
      // Get the current selection from the editor
      const { from, to } = this.editor.state.selection;
      const hasSelection = from !== to;
      var selectedText = hasSelection ? this.editor.state.doc.textBetween(from, to, ' ') : '';
      
      // Reset the comment form
      // When called from context menu, it's a variant-specific comment with selection
      this.newComment = {
        content: '',
        variant: this.documentVariant,
        isGlobal: false, // NOT global when from context menu
        hasSelection: hasSelection,
        selectedText: selectedText,
        from: hasSelection ? from : null,
        to: hasSelection ? to : null,
        replyTo: null,
      };
      
      console.log('Opening modal from context, newComment:', this.newComment);
      this.commentDialogOpen = true;
    },

    openReplyCommentModal(comment) {
      console.log('openReplyCommentModal called for comment:', comment);
      
      // When replying to a comment, use the same selection range as the original comment
      this.newComment = {
        content: '',
        variant: this.documentVariant,
        isGlobal: false,
        hasSelection: true,
        selectedText: '',
        from: comment.from,
        to: comment.to,
        replyTo: comment,
      };
      
      console.log('Opening reply modal, newComment:', this.newComment);
      this.commentDialogOpen = true;
    },

    handleCommentModalOk(bvModalEvent) {
      console.log('handleCommentModalOk called');
      // Prevent modal from closing
      bvModalEvent.preventDefault();
      // Call the save method
      this.saveComment();
    },

    async saveComment() {
      console.log('saveComment called', this.newComment);
      console.log('this.$mediaHttp:', this.$mediaHttp);
      if (this.savingComment) return;
      this.savingComment = true;
      try {
        
        if (!this.newComment.content.trim()) {
          console.log('Validation failed: empty comment');
          this.$bvToast.toast('Please enter a comment', {
            title: 'Validation Error',
            variant: 'warning',
            solid: true
          });
          return;
        }

        if (!this.storedDoc) {
          console.log('Validation failed: no document');
          this.$bvToast.toast('Document not loaded', {
            title: 'Error',
            variant: 'danger',
            solid: true
          });
          return;
        }

        console.log('Creating payload...');
        const payload = {
          path: this.storedDoc.name,
          content: this.newComment.content,
        };

        // Add variant and range only if not global
        if (!this.newComment.isGlobal) {
          console.log('Adding variant to payload');
          payload.variant = this.newComment.variant;
          
          if (this.newComment.hasSelection && this.newComment.from !== null && this.newComment.to !== null) {
            console.log('Adding selection range to payload');
            // Convert to string if they are numbers
            payload.from = String(this.newComment.from);
            payload.to = String(this.newComment.to);
          }
        }

        // Add replyTo if this is a reply
        if (this.newComment.replyTo) {
          console.log('Adding replyTo to payload');
          payload.replyTo = this.newComment.replyTo.id || this.newComment.replyTo;
        }

        console.log('Sending comment payload:', payload);
        const response = await this.$mediaHttp.post('/v2/dms/documents/document/comment', payload);
        console.log('Comment sent successfully', response);

        // Immediately update the frontend with the new comment
        // Create a temporary comment object with expected structure
        const newCommentObj = {
          id: (response.data && response.data.id) || `temp-${Date.now()}`, // Use response ID or temporary ID
          content: this.newComment.content,
          from: this.newComment.from,
          to: this.newComment.to,
          replyTo: this.newComment.replyTo || null,
          createdBy: {
            name: (this.$store.state.user && this.$store.state.user.name) || 'You',
            uuid: (this.$store.state.user && this.$store.state.user.uuid) || '',
            time: new Date().toISOString()
          }
        };

        // Update the storedDoc immediately
        const updatedDoc = { ...this.storedDoc };
        const variantIndex = updatedDoc.variants.findIndex(v => v.name === this.documentVariant);
        
        if (variantIndex !== -1) {
          if (!updatedDoc.variants[variantIndex].comments) {
            updatedDoc.variants[variantIndex].comments = [];
          }
          updatedDoc.variants[variantIndex].comments.push(newCommentObj);
          
          // Update the store
          this.$store.commit('documents/SET_DOCUMENT', updatedDoc);
          
          // Apply the comment mark immediately
          this.$nextTick(() => {
            this.applyCommentMarks([newCommentObj]);
            
            // Automatically highlight the newly added comment
            this.scrollToComment(newCommentObj);
          });
        }

        this.$bvToast.toast('Comment added successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });

        this.commentDialogOpen = false;
        this.savingComment = false;

        // Optionally reload in background to sync with backend
        setTimeout(async () => {
          await this.$store.dispatch('documents/fetchDocument', this.storedDoc.name);
        }, 3000);

      } catch (error) {
        console.error('Error adding comment:', error);
        this.savingComment = false;
        const errorMessage = error.response && error.response.data && error.response.data.message 
          ? error.response.data.message 
          : 'Failed to add comment';
        this.$bvToast.toast(errorMessage, {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      }
    },

    canDeleteComment(comment) {
      const isOwner = this.$can('edit_document protokol-media');
      const isAuthor = comment.authorUuid && this.$store.state.user && 
                       comment.authorUuid === this.$store.state.user.uuid;
      
      return isOwner || isAuthor;
    },

    confirmDeleteComment(comment) {
      this.$mainDialog.msgBoxConfirm(
        `Are you sure you want to delete this comment?`,
        {
          title: 'Delete Comment',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Delete',
          cancelTitle: 'Cancel',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true
        }
      )
      .then(value => {
        if (value) {
          this.deleteComment(comment);
        }
      })
      .catch(err => {
        console.error('Error in confirmation dialog:', err);
      });
    },

    async deleteComment(comment) {
      try {
        if (!this.storedDoc) {
          this.$bvToast.toast('Document not loaded', {
            title: 'Error',
            variant: 'danger',
            solid: true
          });
          return;
        }

        // Set loading state
        this.deletingCommentId = comment.id;

        const payload = {
          path: this.storedDoc.name,
          commentId: comment.id,
          variant: this.documentVariant, // Always send variant for variant-specific comments
        };

        await this.$mediaHttp.delete('/v2/dms/documents/document/comment', {
          data: payload
        });

        // Immediately update the frontend by removing the comment
        const updatedDoc = { ...this.storedDoc };
        const variantIndex = updatedDoc.variants.findIndex(v => v.name === this.documentVariant);
        
        if (variantIndex !== -1 && updatedDoc.variants[variantIndex].comments) {
          // Remove the comment from the array
          updatedDoc.variants[variantIndex].comments = 
            updatedDoc.variants[variantIndex].comments.filter(c => c.id !== comment.id);
          
          // Update the store
          this.$store.commit('documents/SET_DOCUMENT', updatedDoc);
          
          // Reload the document content to remove comment marks
          this.$nextTick(() => {
            this.loadDoc();
          });
        }

        this.$bvToast.toast('Comment deleted successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });

        // Clear loading state
        this.deletingCommentId = null;

        // Optionally reload in background to sync with backend
        setTimeout(async () => {
          await this.$store.dispatch('documents/fetchDocument', this.storedDoc.name);
        }, 3000);

      } catch (error) {
        // Clear loading state on error
        this.deletingCommentId = null;
        
        console.error('Error deleting comment:', error);
        const errorMessage = error.response && error.response.data && error.response.data.message 
          ? error.response.data.message 
          : 'Failed to delete comment';
        this.$bvToast.toast(errorMessage, {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      }
    },

    // Global Comments Methods
    replyToGlobalComment(comment) {
      this.replyingToGlobalComment = comment;
      // Focus on the textarea
      this.$nextTick(() => {
        const textarea = this.$refs.globalCommentTextarea;
        if (textarea) {
          textarea.focus();
        }
      });
    },

    async sendGlobalComment() {
      console.log('sendGlobalComment called');
      try {
        if (!this.globalCommentText.trim()) {
          this.$bvToast.toast('Please enter a comment', {
            title: 'Validation Error',
            variant: 'warning',
            solid: true
          });
          return;
        }

        if (!this.storedDoc) {
          this.$bvToast.toast('Document not loaded', {
            title: 'Error',
            variant: 'danger',
            solid: true
          });
          return;
        }

        this.sendingGlobalComment = true;

        const payload = {
          path: this.storedDoc.name,
          content: this.globalCommentText,
          // Global comments don't have variant or selection range
        };

        // Add replyTo if replying to another global comment
        if (this.replyingToGlobalComment) {
          payload.replyTo = this.replyingToGlobalComment.id;
        }

        console.log('Sending global comment payload:', payload);
        const response = await this.$mediaHttp.post('/v2/dms/documents/document/comment', payload);
        console.log('Global comment sent successfully', response);

        // Immediately update the frontend with the new global comment
        const newCommentObj = {
          id: (response.data && response.data.id) || `temp-${Date.now()}`,
          content: this.globalCommentText,
          replyTo: this.replyingToGlobalComment ? this.replyingToGlobalComment.id : null,
          createdBy: {
            name: (this.$store.state.user && this.$store.state.user.name) || 'You',
            uuid: (this.$store.state.user && this.$store.state.user.uuid) || '',
            time: new Date().toISOString()
          }
        };

        // Update the storedDoc immediately
        const updatedDoc = { ...this.storedDoc };
        if (!updatedDoc.comments) {
          updatedDoc.comments = [];
        }
        updatedDoc.comments.push(newCommentObj);
        
        // Update the store
        this.$store.commit('documents/SET_DOCUMENT', updatedDoc);

        this.$bvToast.toast('Comment added successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });

        // Clear the form
        this.globalCommentText = '';
        this.replyingToGlobalComment = null;

        // Scroll chat to bottom
        this.$nextTick(() => {
          const chatMessages = this.$refs.chatMessages;
          if (chatMessages) {
            chatMessages.scrollTop = chatMessages.scrollHeight;
          }
        });

        // Optionally reload in background to sync with backend
        setTimeout(async () => {
          await this.$store.dispatch('documents/fetchDocument', this.storedDoc.name);
        }, 3000);

      } catch (error) {
        console.error('Error adding global comment:', error);
        const errorMessage = error.response && error.response.data && error.response.data.message 
          ? error.response.data.message 
          : 'Failed to add comment';
        this.$bvToast.toast(errorMessage, {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.sendingGlobalComment = false;
      }
    },

    confirmDeleteGlobalComment(comment) {
      this.$mainDialog.msgBoxConfirm(
        `Are you sure you want to delete this comment?`,
        {
          title: 'Delete Comment',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Delete',
          cancelTitle: 'Cancel',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true
        }
      )
      .then(value => {
        if (value) {
          this.deleteGlobalComment(comment);
        }
      })
      .catch(err => {
        console.error('Error in confirmation dialog:', err);
      });
    },

    async deleteGlobalComment(comment) {
      try {
        if (!this.storedDoc) {
          this.$bvToast.toast('Document not loaded', {
            title: 'Error',
            variant: 'danger',
            solid: true
          });
          return;
        }

        // Set loading state
        this.deletingCommentId = comment.id;

        const payload = {
          path: this.storedDoc.name,
          commentId: comment.id,
          // Global comments don't have variant
        };

        await this.$mediaHttp.delete('/v2/dms/documents/document/comment', {
          data: payload
        });

        // Immediately update the frontend by removing the global comment
        const updatedDoc = { ...this.storedDoc };
        
        if (updatedDoc.comments) {
          // Remove the comment and any replies to it
          updatedDoc.comments = updatedDoc.comments.filter(c => {
            // Remove the comment itself or any comment that replies to it
            return c.id !== comment.id && c.replyTo !== comment.id;
          });
          
          // Update the store
          this.$store.commit('documents/SET_DOCUMENT', updatedDoc);
        }

        this.$bvToast.toast('Comment deleted successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });

        // Clear loading state
        this.deletingCommentId = null;

        // Optionally reload in background to sync with backend
        setTimeout(async () => {
          await this.$store.dispatch('documents/fetchDocument', this.storedDoc.name);
        }, 3000);

      } catch (error) {
        // Clear loading state on error
        this.deletingCommentId = null;
        
        console.error('Error deleting global comment:', error);
        const errorMessage = error.response && error.response.data && error.response.data.message 
          ? error.response.data.message 
          : 'Failed to delete comment';
        this.$bvToast.toast(errorMessage, {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      }
    },

    wrapContentInSection(html) {
      if (!html || html.trim() === '') return '';

      const container = document.createElement('div');
      container.innerHTML = html;

      if (container.querySelector(':scope > section')) {
        return html;
      }

      const section = document.createElement('section');
      section.className = 'document-section';
      section.setAttribute('data-name', 'Document');

      while (container.firstChild) {
        section.appendChild(container.firstChild);
      }
      container.appendChild(section);

      return container.innerHTML;
    },

    loadDoc() {
      if (!this.storedDoc) {
        this.loadingDocument = false;
        return;
      }

      this.isReadyForAutosave = false;

      const myVariant = this.storedDoc.variants.find(
        (v) => v.name === this.documentVariant
      );

      if (!myVariant) {
        this.content = "";
        this.editor.commands.setContent("", false);
        this.version = 0;
        this.fields = [];
        this.sources = [];
        this.lastSavedContent = "";
      } else {
        const wrappedContent = this.wrapContentInSection(myVariant.content);
        this.content = wrappedContent;
        this.editor.commands.setContent(wrappedContent, false);
        this.version = myVariant.version;
        this.fields = myVariant.fields || [];
        this.sources = myVariant.sources || [];
        this.lastSavedContent = wrappedContent;

        console.log('Document variant loaded, comments:', myVariant.comments);

        this.$nextTick(() => {
          this.applyCommentMarks(myVariant.comments || []);
        });
      }

      this.hasUnsavedChanges = false;
      this.setupDocumentPreview();
      this.fieldsSetup();
      this.loadingDocument = false;
      this.isInitialFormat = true;
      this.$nextTick(() => {
        // Editor is connected and content is loaded here — (re)inject the
        // canonical document stylesheet so edit mode renders from the shared
        // source (the early mount-time call may have run before connection).
        this.applyEditorDocumentCSS();
        this.runAutoPageBreak();
      });
    },

    applyCommentMarks(comments) {
      if (!this.editor || !comments || comments.length === 0) return;
      
      // Apply each comment as a mark in the editor
      comments.forEach(comment => {
        const from = parseInt(comment.from);
        const to = parseInt(comment.to);
        
        if (!isNaN(from) && !isNaN(to) && from < to) {
          try {
            // Get the text that should be highlighted
            const commentText = this.editor.state.doc.textBetween(from, to, ' ');
            
            console.log(`Applying comment mark: from=${from}, to=${to}, text="${commentText}"`);
            
            // Apply the comment mark to the text range using the positions directly
            // as they are already in ProseMirror format
            this.editor
              .chain()
              .setTextSelection({ from, to })
              .setComment({ content: comment.content, commentId: comment.id })
              .run();
          } catch (error) {
            console.error('Error applying comment mark:', error, comment);
          }
        }
      });
      
      // Clear selection after applying all marks
      this.editor.commands.setTextSelection(0);
    },
    check(val) {
      if (val !== null) {
        val = val.replace(/(<([^>]+)>)/gi, "");
      } else {
        val = "";
      }

      this.errors = [];

      if (val == "" && this.constraints.required) {
        this.errors.push(this.translator("identityField.requiredField"));
      }

      if (this.constraints.length !== undefined) {
        if (
          this.constraints.length.max !== null &&
          this.constraints.length.max > 0
        ) {
          if (val.length > this.constraints.length.max && val.length > 0) {
            this.errors.push(
              this.translator("identityField.cantBeMoreThenChars", {
                max: this.constraints.length.max,
              })
            );
          }
        }

        if (
          this.constraints.length.min !== null &&
          this.constraints.length.min > 0
        ) {
          if (val.length < this.constraints.length.min && val.length > 0) {
            this.errors.push(
              this.translator("identityField.cantBeLessThenChars", {
                min: this.constraints.length.min,
              })
            );
          }
        }
      }

      if (this.constraints.not_allowed_words !== undefined) {
        let notAllowedWords = this.constraints.not_allowed_words.split(",");
        notAllowedWords.forEach((word) => {
          word = word.trim();

          if (word != "") {
            if (val.indexOf(word) !== -1) {
              this.errors.push(
                this.translator("identityField.wordNotAllowed", {
                  word: word,
                })
              );
            }
          }
        });
      }

      if (
        this.constraints.regex !== undefined ||
        this.constraints.regex !== ""
      ) {
        let re = new RegExp(this.constraints.regex);
        if (!re.test(val)) {
          this.errors.push(this.translator("identityField.invalidInput"));
        }
      }

      this.$emit("error", {
        field: this.field.key,
        error: this.errors.length > 0,
      });
    },

    fieldEditorFieldMapped(value) {
      const field = this.fieldEditor;
      field.data = Object.assign({}, field.data || {}, { sourceField: value });
      this.$nextTick(() => this.syncVisualFromExpression());
    },

    buildVisualExpression() {
      const vf = this.fieldEditorVisualFormat;
      const type = this.fieldEditorValueType;
      let expr = '';

      if (type === 'array_objects') {
        if (vf.property) {
          expr = 'value|map("' + vf.property + '")';
          if (vf.displayAs === 'inline') {
            expr += '|join("' + (vf.separator || ', ') + '")';
          } else if (vf.displayAs === 'list') {
            expr += '|join("\\n")';
          } else if (vf.displayAs === 'numbered') {
            expr += '|join("\\n")';
          }
        }
      } else if (type === 'array') {
        if (vf.displayAs === 'inline') {
          expr = 'value|join("' + (vf.separator || ', ') + '")';
        } else if (vf.displayAs === 'list') {
          expr = 'value|join("\\n")';
        } else if (vf.displayAs === 'numbered') {
          expr = 'value|join("\\n")';
        }
      } else if (type === 'object') {
        if (vf.objectProperty) {
          expr = 'value|get("' + vf.objectProperty + '")';
        }
      } else if (type === 'scalar') {
        const parts = [];
        if (vf.textTransform) parts.push(vf.textTransform);
        if (vf.dateFormat) parts.push('formatDate("' + vf.dateFormat + '")');
        if (vf.numberFormat === 'round') parts.push('round');
        else if (vf.numberFormat === 'toFixed2') parts.push('toFixed(2)');
        else if (vf.numberFormat === 'currency') parts.push('currency("")');
        else if (vf.numberFormat === 'percent') parts.push('percent');
        if (parts.length) expr = 'value|' + parts.join('|');
      }

      this.fieldEditor.format = expr;
    },

    syncVisualFromExpression() {
      const expr = this.fieldEditor.format || '';
      const vf = this.fieldEditorVisualFormat;
      vf.property = '';
      vf.separator = ', ';
      vf.displayAs = 'inline';
      vf.objectProperty = '';
      vf.textTransform = '';
      vf.numberFormat = '';
      vf.dateFormat = '';

      if (!expr) {
        this.fieldEditorFormatMode = 'visual';
        return;
      }

      const mapMatch = expr.match(/value\|map\("([^"]+)"\)/);
      const joinMatch = expr.match(/join\("([^"]*)"\)/);
      const getMatch = expr.match(/value\|get\("([^"]+)"\)/);
      const dateMatch = expr.match(/formatDate\("([^"]+)"\)/);

      if (mapMatch) {
        vf.property = mapMatch[1];
        if (joinMatch) {
          const sep = joinMatch[1];
          if (sep === '\\n') {
            vf.displayAs = 'list';
          } else {
            vf.displayAs = 'inline';
            vf.separator = sep;
          }
        }
        this.fieldEditorFormatMode = 'visual';
      } else if (/^value\|join\(/.test(expr)) {
        if (joinMatch) {
          const sep = joinMatch[1];
          if (sep === '\\n') {
            vf.displayAs = 'list';
          } else {
            vf.displayAs = 'inline';
            vf.separator = sep;
          }
        }
        this.fieldEditorFormatMode = 'visual';
      } else if (getMatch) {
        vf.objectProperty = getMatch[1];
        this.fieldEditorFormatMode = 'visual';
      } else if (/^value\|(upper|lower|capitalize)$/.test(expr)) {
        vf.textTransform = expr.replace('value|', '');
        this.fieldEditorFormatMode = 'visual';
      } else if (/^value\|round$/.test(expr)) {
        vf.numberFormat = 'round';
        this.fieldEditorFormatMode = 'visual';
      } else if (/^value\|toFixed\(2\)$/.test(expr)) {
        vf.numberFormat = 'toFixed2';
        this.fieldEditorFormatMode = 'visual';
      } else if (/^value\|currency\(""\)$/.test(expr)) {
        vf.numberFormat = 'currency';
        this.fieldEditorFormatMode = 'visual';
      } else if (/^value\|percent$/.test(expr)) {
        vf.numberFormat = 'percent';
        this.fieldEditorFormatMode = 'visual';
      } else if (dateMatch) {
        vf.dateFormat = dateMatch[1];
        this.fieldEditorFormatMode = 'visual';
      } else if (expr) {
        this.fieldEditorFormatMode = 'advanced';
      }
    },

    isDocumentField(field) {
      return this.documentFields.indexOf(field) !== -1;
    },

    getFieldSourceValue() {
      return _.get(this.data, this.fieldEditor.field, null);
    },

    fieldSourceValueChanged(val) {
      this.fieldEditor.value = val;
      _.set(this.data, this.fieldEditor.field, val);
    },

    getSettingsForComponent(ref) {
      if (ref) {
        return this.componentsSettings.find((s) => {
          return (
            s.general.name == ref ||
            s.general.uuid == ref ||
            s.general.tag == ref
          );
        });
      }
    },

    async fetchComponentSettings(ref) {
      if (this.getSettingsForComponent(ref)) return;

      const { general, settings, workspaces } = await this.$store.dispatch(
        "fetchComponentsSettings",
        ref
      );
      this.componentsSettings.push({ general, settings, workspaces });
    },

    getComponentSettingsFields(ref, formatter) {
      const { settings } = this.getSettingsForComponent(ref) || {};

      if (!settings) return [];
      if (!formatter) return settings.fields || [];

      return formatter(settings.fields || []);
    },

    async fieldEditorSourceChanged() {
      if (this.fieldEditorIsSourceFromTag) {
        switch (this.fieldEditorTagSource.type) {
          case "component":
            await this.fetchComponentSettings(this.fieldEditorTagSource.value);

            this.fieldEditor.settings = Object.assign(
              {},
              this.fieldEditor.settings,
              { fields: this.getFieldEditorComponentFields() }
            );
        }
      }
    },

    getFieldEditorComponentFields() {
      return this.getComponentSettingsFields(
        this.fieldEditorTagSource.value,
        (fields) => {
          return fields.map((f) => {
            return {
              text: f.name,
              key: f.key,
              module: f.module,
              type: f.type,
            };
          });
        }
      );
    },

    getColor: function () {
      if (!this.editor) return "#00000";
      return this.colorInHex(this.editor.getAttributes("textStyle").color);
    },

    getFontFamily: function () {
      const { fontFamily = "Arial" } = this.editor.getAttributes("textStyle");
      return fontFamily || "Arial";
    },

    changeFontFamily(font) {
      this.editor.chain().focus().setFontFamily(font).run();
      this.fontFamily = font;
    },

    colorInHex: function (rgbColor) {
      if (!rgbColor) return "#000000";

      if (rgbColor.startsWith("#")) {
        return rgbColor;
      }

      const values = rgbColor.match(/\d+/g);
      if (!values || values.length !== 3) {
        throw new Error("Invalid RGB color format");
      }

      const hexValues = values.map((value) => {
        const hex = parseInt(value, 10).toString(16);
        return hex.length === 1 ? "0" + hex : hex;
      });

      return "#" + hexValues.join("").toUpperCase();
    },

    colorPicker() {
      document.getElementById("colorPicker").click();
    },

    saveFontSizeSelection() {
      const { from, to } = this.editor.state.selection;
      if (from !== to) {
        this.savedFontSizeSelection = { from, to };
      }
    },

    applyFontSize(val) {
      var size = parseInt(val, 10);
      if (!size || size < 6) size = 6;
      if (size > 100) size = 100;
      if (this.savedFontSizeSelection) {
        this.editor.commands.focus();
        this.editor.commands.setTextSelection(this.savedFontSizeSelection);
        this.editor.commands.setFontSize(size + 'px');
        this.savedFontSizeSelection = null;
      } else {
        this.editor.commands.focus();
        this.editor.commands.setFontSize(size + 'px');
      }
    },

    // The visible document surface to track: the preview shadow DOM in document
    // mode (the editor is display:none there, so its page rects are all 0), the
    // live editor in edit mode. Both live in the same scroll wrapper.
    _activeDocumentPages() {
      if (this.mode === 'document') {
        const host = document.querySelector('#document-preview');
        if (host && host.shadowRoot) {
          const pp = host.shadowRoot.querySelectorAll('main.page');
          if (pp.length) return pp;
        }
      }
      return (this.editor && this.editor.view)
        ? this.editor.view.dom.querySelectorAll('main.page')
        : [];
    },

    setupPageObserver() {
      this.destroyPageObserver();

      const wrapper = this.$refs.editorCanvasWrapper;
      if (!wrapper || !this.editor || !this.editor.view) return;

      const pages = this._activeDocumentPages();
      this.totalPages = pages.length;
      if (!pages.length) return;

      this._pageScrollHandler = () => {
        const wrapperRect = wrapper.getBoundingClientRect();
        const viewportMid = wrapperRect.top + wrapperRect.height * 0.3;
        let closest = 0;
        let closestDist = Infinity;
        let closestPageEl = null;

        const currentPages = this._activeDocumentPages();
        currentPages.forEach((page, i) => {
          const rect = page.getBoundingClientRect();
          const dist = Math.abs(rect.top - viewportMid);
          if (dist < closestDist) {
            closestDist = dist;
            closest = i;
            closestPageEl = page;
          }
        });

        this.currentPage = closest + 1;
        this.totalPages = currentPages.length;

        if (closestPageEl) {
          const section = closestPageEl.closest('section.document-section');
          if (section) {
            this.currentSectionName = section.getAttribute('data-name') || 'Document';
            const sectionPages = section.querySelectorAll('main.page');
            this.currentSectionTotalPages = sectionPages.length;
            let pageInSection = 0;
            for (let j = 0; j < sectionPages.length; j++) {
              if (sectionPages[j] === closestPageEl) {
                pageInSection = j + 1;
                break;
              }
            }
            this.currentSectionPage = pageInSection;
          }
        }

        const sectionEls = this.editor.view.dom.querySelectorAll('section.document-section');
        this.sectionCount = sectionEls.length;
      };

      wrapper.addEventListener('scroll', this._pageScrollHandler, { passive: true });
      this._pageScrollHandler();
    },

    destroyPageObserver() {
      if (this._pageScrollHandler) {
        const wrapper = this.$refs.editorCanvasWrapper;
        if (wrapper) wrapper.removeEventListener('scroll', this._pageScrollHandler);
        this._pageScrollHandler = null;
      }
    },

    manualFormat() {
      if (this.mode !== 'editor') return;
      this.isInitialFormat = true;
      this.runAutoPageBreak();
    },

    togglePageNumbering() {
      this.sectionNumbering = this.sectionNumbering === 'continuous' ? 'per-section' : 'continuous';
    },

    addNewSection() {
      const name = `Document ${this.editorSections.length + 1}`;
      this.editor.commands.addSection(name);
      this.$nextTick(() => {
        this.updateSectionCount();
        this.runAutoPageBreak();
      });
    },

    scrollToSection(sectionIndex) {
      const sectionEls = this.editor.view.dom.querySelectorAll('section.document-section');
      if (sectionEls[sectionIndex]) {
        const firstPage = sectionEls[sectionIndex].querySelector('main.page');
        if (firstPage) {
          firstPage.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    },

    getCurrentSectionIndex() {
      if (!this.editor || !this.editor.view) return 0;
      const pages = this.editor.view.dom.querySelectorAll('main.page');
      const wrapper = this.$refs.editorCanvasWrapper;
      if (!wrapper || !pages.length) return 0;

      const viewTop = wrapper.scrollTop + wrapper.getBoundingClientRect().top + wrapper.clientHeight * 0.3;
      let closestPage = pages[0];
      let closestDist = Infinity;
      for (const page of pages) {
        const dist = Math.abs(page.getBoundingClientRect().top + page.getBoundingClientRect().height / 2 - viewTop);
        if (dist < closestDist) {
          closestDist = dist;
          closestPage = page;
        }
      }

      const section = closestPage.closest('section.document-section');
      if (!section) return 0;
      const sectionEls = this.editor.view.dom.querySelectorAll('section.document-section');
      return Array.from(sectionEls).indexOf(section);
    },

    openRenameSectionDialog() {
      const idx = this.getCurrentSectionIndex();
      const sections = this.editorSections;
      if (idx >= 0 && idx < sections.length) {
        this.renameSectionIndex = idx;
        this.renameSectionName = sections[idx].name;
        this.renameSectionDialog = true;
      }
    },

    submitRenameSection() {
      if (this.renameSectionIndex !== null && this.renameSectionName.trim()) {
        this.editor.commands.renameSection(this.renameSectionIndex, this.renameSectionName.trim());
        this.renameSectionDialog = false;
      }
    },

    mergeWithPreviousSection() {
      const idx = this.getCurrentSectionIndex();
      if (idx < 1) return;
      this.editor.commands.mergeSection(idx);
      this.$nextTick(() => {
        this.updateSectionCount();
        this.runAutoPageBreak();
      });
    },

    deleteCurrentSection() {
      const idx = this.getCurrentSectionIndex();
      if (this.editorSections.length <= 1) return;
      this.editor.commands.deleteSection(idx);
      this.$nextTick(() => {
        this.updateSectionCount();
        this.runAutoPageBreak();
      });
    },

    updateSectionCount() {
      const sectionEls = this.editor.view.dom.querySelectorAll('section.document-section');
      this.sectionCount = sectionEls.length;
    },

    debouncedLightPageBreak: _.debounce(function () {
      this.runLightPageBreak();
    }, 150),

    runLightPageBreak() {
      if (this.mode !== 'editor') return;
      if (this.formattingDocument || this._formattingLock) return;
      if (!this.editor || !this.editor.view) return;

      const sel = this.editor.view.state.selection;
      const cursorNode = this.editor.view.domAtPos(sel.from).node;
      const pages = this.editor.view.dom.querySelectorAll('main.page');
      if (!pages.length) return;

      let activePageIdx = -1;
      for (let i = 0; i < pages.length; i++) {
        if (pages[i].contains(cursorNode)) {
          activePageIdx = i;
          break;
        }
      }
      if (activePageIdx === -1) return;

      const A4_RATIO = 297 / 210;
      const pageRect0 = pages[0].getBoundingClientRect();
      const maxPageHeight = pageRect0.width * A4_RATIO;
      const savedSelection = this.editor.state.selection;

      const pageEl = pages[activePageIdx];
      const effectiveMaxHeight = this.getEffectiveMaxHeight(pageEl, maxPageHeight);
      const pageRect = pageEl.getBoundingClientRect();
      const children = this.getPageContentChildren(pageEl);

      // Check for overflow — split if needed
      if (children.length > 1) {
        for (let j = 0; j < children.length; j++) {
          const childRect = children[j].getBoundingClientRect();
          if (childRect.bottom - pageRect.top > effectiveMaxHeight) {
            const splitAt = j === 0 ? 1 : j;
            this._formattingLock = true;
            try {
              this.performPageSplit(activePageIdx, splitAt);
            } finally {
              this._formattingLock = false;
              this._restoreSelection(savedSelection);
            }
            return;
          }
        }
      }

      // Check for compact — pull from next page if space available (same section only)
      const nextPage = pages[activePageIdx + 1];
      if (nextPage && pageEl.closest('section.document-section') === nextPage.closest('section.document-section')) {
        const nextChildren = this.getPageContentChildren(nextPage);
        const nextFirstChild = nextChildren[0];
        if (nextFirstChild) {
          const lastChild = children[children.length - 1];
          const usedHeight = lastChild
            ? lastChild.getBoundingClientRect().bottom - pageRect.top
            : 0;
          const nextBlockHeight = nextFirstChild.getBoundingClientRect().height;
          const marginBuffer = 10;
          if (usedHeight + nextBlockHeight + marginBuffer <= effectiveMaxHeight) {
            this._formattingLock = true;
            try {
              this.pullFirstBlockFromNextPage(activePageIdx);
            } finally {
              this._formattingLock = false;
              this._restoreSelection(savedSelection);
            }
            return;
          }
        }
      }
    },

    _restoreSelection(savedSelection) {
      try {
        const maxPos = this.editor.state.doc.content.size;
        const from = Math.min(savedSelection.from, maxPos);
        const to = Math.min(savedSelection.to, maxPos);
        this.editor.commands.setTextSelection({ from, to });
      } catch (e) {}
    },

    debouncedAutoPageBreak: _.debounce(function () {
      this.runAutoPageBreak();
    }, 500),

    async runAutoPageBreak() {
      if (this.mode !== 'editor') return;
      if (this.formattingDocument || this._formattingLock) return;
      if (!this.editor || !this.editor.view) return;

      const savedSelection = this.editor.state.selection;

      this._formattingLock = true;
      this.formattingProgress = 'Analyzing pages...';
      this.formattingDocument = true;

      try {
        // Wait for embedded web fonts before measuring, so on-load pagination
        // uses the real glyph metrics (not the swap-fallback) and lands on the
        // same page breaks as the preview and the PDF.
        await this._loadDocumentFonts();

        const MAX_ITERATIONS = 500;

        for (let iter = 0; iter < MAX_ITERATIONS; iter++) {
          if (iter % 10 === 0 && iter > 0) {
            await new Promise(resolve => setTimeout(resolve, 0));
          }
          await this.$nextTick();
          await new Promise(resolve => requestAnimationFrame(resolve));

          if (!this.editor || !this.editor.view) break;

          const pages = this.editor.view.dom.querySelectorAll('main.page');
          if (!pages.length) break;

          this.formattingProgress = `Formatting document... (${pages.length} ${pages.length === 1 ? 'page' : 'pages'})`;

          const A4_RATIO = 297 / 210;
          const pageRect0 = pages[0].getBoundingClientRect();
          const maxPageHeight = pageRect0.width * A4_RATIO;

          if (this.compactPages(maxPageHeight)) {
            continue;
          }

          let didSplit = false;
          for (let i = 0; i < pages.length; i++) {
            const pageEl = pages[i];
            const pageRect = pageEl.getBoundingClientRect();
            const children = this.getPageContentChildren(pageEl);
            if (children.length <= 1) continue;

            const effectiveMaxHeight = this.getEffectiveMaxHeight(pageEl, maxPageHeight);
            let overflowIdx = -1;
            for (let j = 0; j < children.length; j++) {
              const childRect = children[j].getBoundingClientRect();
              if (childRect.bottom - pageRect.top > effectiveMaxHeight) {
                overflowIdx = j;
                break;
              }
            }

            if (overflowIdx === -1) continue;
            if (overflowIdx === 0) overflowIdx = 1;

            while (overflowIdx > 1) {
              const lastKeep = children[overflowIdx - 1];
              const tag = lastKeep.tagName;
              const isHeading = /^H[1-6]$/.test(tag);
              const isShortCentered = tag === 'P'
                && (lastKeep.style.textAlign === 'center' || window.getComputedStyle(lastKeep).textAlign === 'center')
                && (lastKeep.textContent || '').trim().length < 80;
              const isEmpty = tag === 'P' && (lastKeep.textContent || '').trim().length === 0;
              if (isHeading || isShortCentered || isEmpty) {
                overflowIdx--;
              } else {
                break;
              }
            }

            this.performPageSplit(i, overflowIdx);
            didSplit = true;
            break;
          }

          if (!didSplit) break;
        }
      } finally {
        this.formattingProgress = '';
        this.isInitialFormat = false;

        try {
          if (this.editor && this.editor.view) {
            this.editor.commands.calculatePages();
            this.content = this.editor.getHTML();
            try {
              const maxPos = this.editor.state.doc.content.size;
              const from = Math.min(savedSelection.from, maxPos);
              const to = Math.min(savedSelection.to, maxPos);
              this.editor.commands.setTextSelection({ from, to });
              this.editor.commands.focus();
            } catch (e) {
              this.editor.commands.focus();
            }
            this.$nextTick(() => this.setupPageObserver());
          }
        } finally {
          this._formattingLock = false;
          this.formattingDocument = false;
        }
      }
    },

    compactPages(maxPageHeight) {
      const pages = this.editor.view.dom.querySelectorAll('main.page');
      if (pages.length < 2) return false;

      for (let i = 0; i < pages.length - 1; i++) {
        const currentPage = pages[i];
        const nextPage = pages[i + 1];

        if (currentPage.closest('section.document-section') !== nextPage.closest('section.document-section')) continue;

        const nextChildren = this.getPageContentChildren(nextPage);
        const nextFirstChild = nextChildren[0];
        if (!nextFirstChild) continue;

        const effectiveMaxHeight = this.getEffectiveMaxHeight(currentPage, maxPageHeight);
        const pageRect = currentPage.getBoundingClientRect();
        const children = this.getPageContentChildren(currentPage);
        const lastChild = children[children.length - 1];
        const lastChildMargin = lastChild
          ? parseFloat(window.getComputedStyle(lastChild).marginBottom) || 0
          : 0;
        const nextBlockMargin = parseFloat(window.getComputedStyle(nextFirstChild).marginTop) || 0;
        const usedHeight = lastChild
          ? lastChild.getBoundingClientRect().bottom - pageRect.top + lastChildMargin
          : 0;
        const nextBlockHeight = nextFirstChild.getBoundingClientRect().height + nextBlockMargin;

        if (usedHeight + nextBlockHeight <= effectiveMaxHeight) {
          this.pullFirstBlockFromNextPage(i);
          return true;
        }
      }

      return false;
    },

    doesBlockFitOnPage(pageEl, blockEl, effectiveMaxHeight) {
      const measuredPage = pageEl.cloneNode(true);
      measuredPage.style.position = 'absolute';
      measuredPage.style.left = '-10000px';
      measuredPage.style.top = '0';
      measuredPage.style.visibility = 'hidden';
      measuredPage.style.pointerEvents = 'none';
      measuredPage.style.margin = '0';
      measuredPage.style.width = `${pageEl.getBoundingClientRect().width}px`;

      measuredPage.appendChild(blockEl.cloneNode(true));
      document.body.appendChild(measuredPage);

      const contentHeight = this.getPageContentHeight(measuredPage);
      document.body.removeChild(measuredPage);

      return contentHeight <= effectiveMaxHeight - 2;
    },

    getPageContentHeight(pageEl) {
      const pageRect = pageEl.getBoundingClientRect();
      const children = this.getPageContentChildren(pageEl);
      if (!children.length) return 0;

      return children.reduce((height, child) => {
        const rect = child.getBoundingClientRect();
        const styles = window.getComputedStyle(child);
        const marginBottom = parseFloat(styles.marginBottom) || 0;
        return Math.max(height, rect.bottom - pageRect.top + marginBottom);
      }, 0);
    },

    pullFirstBlockFromNextPage(pageIndex) {
      const { state } = this.editor;
      const mainNodes = this.getMainNodes();
      if (pageIndex < 0 || pageIndex + 1 >= mainNodes.length) return;
      if (mainNodes[pageIndex].sectionIndex !== mainNodes[pageIndex + 1].sectionIndex) return;

      const { node: currentMain, pos: currentMainPos } = mainNodes[pageIndex];
      const { node: nextMain, pos: nextMainPos } = mainNodes[pageIndex + 1];

      let firstContentChild = null, firstContentOffset = 0;
      nextMain.forEach((child, offset) => {
        if (!firstContentChild && child.type.name !== 'pageHeader' && child.type.name !== 'pageFooter') {
          firstContentChild = child;
          firstContentOffset = offset;
        }
      });
      if (!firstContentChild) return;

      const footerNode = currentMain.lastChild?.type.name === 'pageFooter' ? currentMain.lastChild : null;
      const insertPos = footerNode
        ? currentMainPos + 1 + currentMain.content.size - footerNode.nodeSize
        : currentMainPos + 1 + currentMain.content.size;
      const tr = state.tr;

      tr.insert(insertPos, firstContentChild);

      const childFrom = tr.mapping.map(nextMainPos + 1 + firstContentOffset);
      tr.delete(childFrom, childFrom + firstContentChild.nodeSize);

      let nextContentCount = 0;
      nextMain.forEach(c => {
        if (c.type.name !== 'pageHeader' && c.type.name !== 'pageFooter') nextContentCount++;
      });
      if (nextContentCount <= 1) {
        const mappedNextMainPos = tr.mapping.map(nextMainPos);
        const nodeAtPos = tr.doc.nodeAt(mappedNextMainPos);
        if (nodeAtPos) {
          tr.delete(mappedNextMainPos, mappedNextMainPos + nodeAtPos.nodeSize);
        }
      }

      this.editor.view.dispatch(tr);
    },

    getMainNodes() {
      const mainNodes = [];
      let sectionIdx = 0;

      this.editor.state.doc.forEach((sectionNode, sectionOffset) => {
        if (sectionNode.type.name === 'section') {
          sectionNode.forEach((mainNode, mainOffset) => {
            if (mainNode.type.name === 'main') {
              mainNodes.push({
                node: mainNode,
                pos: sectionOffset + 1 + mainOffset,
                sectionIndex: sectionIdx,
                sectionPos: sectionOffset,
              });
            }
          });
          sectionIdx++;
        }
      });

      return mainNodes;
    },

    performPageSplit(pageIndex, overflowChildIndex) {
      const { state } = this.editor;
      const mainNodes = this.getMainNodes();

      if (pageIndex >= mainNodes.length) return;

      const { node: currentMain, pos: currentMainPos, sectionIndex } = mainNodes[pageIndex];
      const hasNextPage = pageIndex + 1 < mainNodes.length
        && mainNodes[pageIndex + 1].sectionIndex === sectionIndex;

      const contentChildren = [];
      let headerNode = null, footerNode = null;
      currentMain.forEach((child, childOffset) => {
        if (child.type.name === 'pageHeader') { headerNode = child; return; }
        if (child.type.name === 'pageFooter') { footerNode = child; return; }
        contentChildren.push({ child, offset: childOffset });
      });

      if (overflowChildIndex >= contentChildren.length) return;

      const nodesToMove = contentChildren.slice(overflowChildIndex).map(c => c.child);
      const splitOffset = contentChildren[overflowChildIndex].offset;
      const splitPos = currentMainPos + 1 + splitOffset;

      if (nodesToMove.length === 0) return;

      const deleteEnd = footerNode
        ? currentMainPos + 1 + currentMain.content.size - footerNode.nodeSize
        : currentMainPos + 1 + currentMain.content.size;
      const tr = state.tr;

      tr.delete(splitPos, deleteEnd);

      if (hasNextPage) {
        const nextMain = mainNodes[pageIndex + 1];
        let insertAfterHeader = tr.mapping.map(nextMain.pos + 1);
        const nextFirstChild = nextMain.node.child(0);
        if (nextFirstChild?.type.name === 'pageHeader') {
          insertAfterHeader = tr.mapping.map(nextMain.pos + 1 + nextFirstChild.nodeSize);
        }
        for (let i = nodesToMove.length - 1; i >= 0; i--) {
          tr.insert(insertAfterHeader, nodesToMove[i]);
        }
      } else {
        const mainType = state.schema.nodes.main;
        const newChildren = [];
        if (headerNode) newChildren.push(headerNode.copy(headerNode.content));
        newChildren.push(...nodesToMove);
        if (footerNode) newChildren.push(footerNode.copy(footerNode.content));
        const newPage = mainType.create({ firstPage: false }, newChildren);
        const insertPos = tr.mapping.map(currentMainPos + currentMain.nodeSize);
        tr.insert(insertPos, newPage);
      }

      this.editor.view.dispatch(tr);
    },

    triggerImageImport() {
      document.getElementById("imageImport").click();
    },

    imageChoosen(val) {
      const reader = new FileReader();
      reader.onloadend = () => {
        this.editor.commands.setImage({
          src: reader.result,
        });
      };

      reader.onerror = () => {};
      reader.readAsDataURL(val);
    },

    // async createAndPrint() {
    //   try {
    //     const { data } = await this.$mediaHttp.post(
    //       `/v3/dms/media/pdf/html2pdf`,
    //       {
    //         input_html: this.template.document,
    //         output_file_name: this.outputFileName,
    //         output_path: "dokumenti", // user should choose
    //         data: this.data, // placeholders
    //         output_pdf: true,
    //       }
    //     );

    //     const blob = new Blob([data], { type: "application/pdf" });
    //     const url = URL.createObjectURL(blob); // Create a Blob UR

    //     window.open(url, "_blank");
    //   } catch (err) {
    //     this.$bvToast.toast("Neočekivana greška", {
    //       variant: "danger",
    //     });
    //   }
    // },

    toggleDocDropdown() {
      if (this.showDocDropdown) {
        this.showDocDropdown = false;
        return;
      }
      const btn = this.$refs.docNameBtn;
      if (btn) {
        const rect = btn.getBoundingClientRect();
        this.docDropdownStyle = {
          position: "fixed",
          top: rect.bottom + 6 + "px",
          left: rect.left + "px",
        };
      }
      this.showDocDropdown = true;
      this.$nextTick(() => {
        if (this.$refs.docSearchInput) this.$refs.docSearchInput.focus();
      });
    },

    async navigateBack() {
      if (this.hasUnsavedChanges && !this.autoSave) {
        const confirmed = await this.$mainDialog.msgBoxConfirm(
          'You have unsaved changes. Do you want to leave without saving?',
          { title: 'Unsaved Changes', okTitle: 'Leave', cancelTitle: 'Stay', noStacking: true }
        );
        if (!confirmed) return;
      }
      const path = this.$route.path;
      const docsPath = path.replace(/\/editor$/, "/documents");
      this.$router.push({ path: docsPath, query: { lib: this.$route.query.lib } });
    },

    async loadDocList() {
      try {
        const docs = await this.$store.dispatch("documents/fetchDocuments", {
          searchText: "",
          recursive: true,
        });
        this.docList = (docs || []).filter((d) => !d.isDir);
      } catch (e) {
        this.docList = [];
      }
    },

    searchDocuments: _.debounce(function () {
      // filtering is handled by the filteredDocList computed
    }, 200),

    async switchDocument(doc) {
      if (this.hasUnsavedChanges && !this.autoSave) {
        const confirmed = await this.$mainDialog.msgBoxConfirm(
          'You have unsaved changes. Do you want to switch documents without saving?',
          { title: 'Unsaved Changes', okTitle: 'Switch', cancelTitle: 'Stay', noStacking: true }
        );
        if (!confirmed) return;
      }
      this.showDocDropdown = false;
      this.docSearchText = "";
      this.hasUnsavedChanges = false;
      this.$router.push({
        path: this.$route.path,
        query: {
          ...this.$route.query,
          document: doc.name.startsWith("/") ? doc.name : "/" + doc.name,
        },
      });
    },

    getPrintData() {
      const printData = _.cloneDeep(this.data) || {};
      const html = this.editor ? this.editor.getHTML() || "" : "";
      const regex = /\{\{\s*\.([^}]+?)\s*\}\}/g;
      let match;
      const RESERVED_KEYWORDS = ['page', 'pages'];
      while ((match = regex.exec(html)) !== null) {
        const path = match[1].trim();
        if (RESERVED_KEYWORDS.includes(path)) continue;
        if (!_.get(printData, path)) {
          const fieldDef = this.fields.find((f) => f.field === path);
          const label = fieldDef && fieldDef.label ? fieldDef.label : path.split(".").pop();
          _.set(printData, path, label);
        }
      }

      return printData;
    },

    async printPreview() {
      if (this.printingPreview) return;

      this.printingPreview = true;
      try {
        this.editor.commands.calculatePages();

        // Ensure embedded fonts are loaded before the offscreen print-measurement
        // pass (inside buildPrintableHTML) so it breaks pages with the real glyph
        // metrics the server will also use — keeping the PDF == the preview.
        await this._loadDocumentFonts();

        const { data } = await this.$mediaHttp.post(
          `/v3/dms/media/pdf/html2pdf`,
          {
            input_html: this.buildPrintableHTML(),
            output_file_name: "preview",
            output_pdf: true,
          },
          {
            responseType: "arraybuffer",
          }
        );

        const blob = new Blob([data], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 30000);
      } catch (err) {
        this.$bvToast.toast("Neočekivana greška", {
          variant: "danger",
        });
        console.error("[DocumentEditor] printPreview error:", err);
      } finally {
        this.printingPreview = false;
      }
    },

    buildPrintableHTML() {
      const html = this.getPrintableEditorHTML();

      return `
<!doctype html>
<html>
  <head>
    <meta charset="UTF-8">
    <link rel="stylesheet" href="${this.getDocumentWebfontHref()}">
    <style>
      @page {
        size: A4;
        margin: 0;
      }

      /* Embedded document fonts (absolute URLs) so the server-side renderer uses
         the real glyphs — identical to what the editor/preview render. */
      ${this.getPrintFontFaceCSS()}

      /* Canonical document stylesheet — the SAME one the preview and the
         print-measurement pass use, so the rendered PDF paginates exactly like
         the on-screen preview. */
      ${this.getDocumentCSS('html, body')}

      /* ─── Print-only page rules (fixed A4 box + hard page breaks) ─── */
      main.page {
        /* The PDF server's effective printable height is ~285mm, not the full
           297mm of A4 (its @page margin:0 isn't fully honored — it reserves
           ~12mm). A 297mm-tall page box therefore overflows the server's print
           area and it emits a blank page after each full page. Pin the box to
           285mm (overriding the shared min-height:297mm too, else it wins) so it
           fits the server's print area exactly — no blank pages. Verified: 297mm
           -> 11 pages, 285mm -> 9 (matches the editor/preview). */
        height: 285mm;
        min-height: 285mm;
        overflow: hidden;
      }
      /* Separate pages with a break BEFORE each page after the first — NOT
         break-after. break-after on an element that is exactly the page height
         makes Chromium emit a trailing BLANK page after every full page. A
         break-before on the following page achieves the same separation with no
         blank pages. Covers both pages within a section and section boundaries;
         the very first page of the document gets no break. */
      section.document-section main.page + main.page {
        break-before: page;
        page-break-before: always;
      }
      section.document-section + section.document-section {
        break-before: page;
        page-break-before: always;
      }
    </style>
  </head>
  <body>${html}</body>
</html>`;
    },

    getPrintableEditorHTML() {
      const previewEl = document.querySelector('#document-preview');
      const shadowRoot = previewEl && previewEl.shadowRoot;

      if (!shadowRoot) {
        return this._getPrintableEditorHTMLFallback();
      }

      const container = document.createElement("div");
      container.innerHTML = shadowRoot.innerHTML;

      // Remove preview-only chrome (style tags, font links, field highlights)
      container.querySelectorAll('style, link[rel="stylesheet"]').forEach(el => el.remove());

      // Restore raw template syntax from decorated preview spans, then
      // substitute with actual data values for printing.
      const RESERVED_KEYWORDS = ['page', 'pages'];
      const printData = this.getPrintData();

      container.querySelectorAll('[data-clause="print"]').forEach(span => {
        const ref = span.getAttribute('data-ref') || '';
        const name = ref.replace(/^\./, '');
        if (RESERVED_KEYWORDS.includes(name)) {
          span.replaceWith('{{ .' + name + ' }}');
        } else {
          const value = _.get(printData, name, null);
          span.replaceWith(value != null ? String(value) : '{{ ' + ref + ' }}');
        }
      });

      container.querySelectorAll('[data-clause="if"]').forEach(span => {
        const refs = span.getAttribute('data-refs') || '';
        const modifier = span.getAttribute('data-ref-modifier') || '';
        const modStr = modifier ? modifier + ' ' : '';
        const opening = '{{ if ' + modStr + refs + ' }}';
        const closing = '{{ end }}';
        span.insertAdjacentText('afterbegin', opening);
        span.insertAdjacentText('beforeend', closing);
        const parent = span.parentNode;
        while (span.firstChild) parent.insertBefore(span.firstChild, span);
        span.remove();
      });

      container.querySelectorAll('[data-clause="range"]').forEach(span => {
        const ref = span.getAttribute('data-ref') || '';
        const opening = '{{ range ' + ref + ' }}';
        const closing = '{{ end }}';
        span.insertAdjacentText('afterbegin', opening);
        span.insertAdjacentText('beforeend', closing);
        const parent = span.parentNode;
        while (span.firstChild) parent.insertBefore(span.firstChild, span);
        span.remove();
      });

      // Remove remaining preview-specific attributes and classes
      container.querySelectorAll('.chooseSource').forEach(el => {
        el.classList.remove('chooseSource');
        el.removeAttribute('active');
      });
      container.querySelectorAll('.ifClause').forEach(el => el.classList.remove('ifClause'));
      container.querySelectorAll('.rangeClause').forEach(el => el.classList.remove('rangeClause'));
      container.querySelectorAll('.hoverGray').forEach(el => el.classList.remove('hoverGray'));
      container.querySelectorAll('[data-clause]').forEach(el => el.removeAttribute('data-clause'));
      container.querySelectorAll('[data-ref]').forEach(el => el.removeAttribute('data-ref'));
      container.querySelectorAll('[data-refs]').forEach(el => el.removeAttribute('data-refs'));
      container.querySelectorAll('[data-ref-modifier]').forEach(el => el.removeAttribute('data-ref-modifier'));

      // Standard print cleanup
      container.querySelectorAll("[contenteditable]").forEach(node => node.removeAttribute("contenteditable"));
      container.querySelectorAll(".ProseMirror-selectednode, .selectedCell").forEach(node => {
        node.classList.remove("ProseMirror-selectednode", "selectedCell");
      });
      container.querySelectorAll(".column-resize-handle").forEach(node => node.remove());
      container.querySelectorAll("table").forEach(table => { table.style.borderCollapse = "collapse"; });
      container.querySelectorAll("img").forEach(img => { img.style.cursor = ""; });

      // The preview paginates at 297mm (full A4) but the PDF server's effective
      // print area is 285mm. Re-run the print reflow so pages split/compact at
      // the real print height — otherwise content that barely fits a 297mm page
      // overflows the 285mm box and leaves orphaned headings with large gaps.
      this._normalizeInlineFontFallbacks(container);
      this._reflowPrintPages(container);

      // Page numbers
      const allPrintPages = container.querySelectorAll('main.page');
      const totalPrintPages = allPrintPages.length;
      let printPageNum = 0;
      allPrintPages.forEach(page => {
        printPageNum++;
        page.querySelectorAll('.page-number-node').forEach(el => {
          const fmt = el.getAttribute('data-format') || '{{ .page }} / {{ .pages }}';
          el.textContent = fmt
            .replace(/\{\{\s*\.?page\s*\}\}/g, printPageNum)
            .replace(/\{\{\s*\.?pages\s*\}\}/g, totalPrintPages);
          el.removeAttribute('data-format');
        });
      });

      return container.innerHTML;
    },

    _getPrintableEditorHTMLFallback() {
      let html = this.editor ? this.editor.getHTML() || "" : "";

      const RESERVED_KEYWORDS = ['page', 'pages'];
      const printData = this.getPrintData();
      html = html.replace(/\{\{\s*\.([^}]+?)\s*\}\}/g, (match, variable) => {
        const name = variable.trim();
        if (RESERVED_KEYWORDS.includes(name)) return match;
        const value = _.get(printData, name, null);
        return value != null ? value : match;
      });

      const container = document.createElement("div");
      container.innerHTML = html;

      container.querySelectorAll("[contenteditable]").forEach(node => node.removeAttribute("contenteditable"));
      container.querySelectorAll(".ProseMirror-selectednode, .selectedCell").forEach(node => {
        node.classList.remove("ProseMirror-selectednode", "selectedCell");
      });
      container.querySelectorAll(".column-resize-handle").forEach(node => node.remove());
      container.querySelectorAll("table").forEach(table => { table.style.borderCollapse = "collapse"; });
      container.querySelectorAll("img").forEach(img => { img.style.cursor = ""; });

      this._normalizeInlineFontFallbacks(container);
      this._reflowPrintPages(container);

      const allPrintPages = container.querySelectorAll('main.page');
      const totalPrintPages = allPrintPages.length;
      let printPageNum = 0;
      allPrintPages.forEach(page => {
        printPageNum++;
        page.querySelectorAll('.page-number-node').forEach(el => {
          const fmt = el.getAttribute('data-format') || '{{ .page }} / {{ .pages }}';
          el.textContent = fmt
            .replace(/\{\{\s*\.?page\s*\}\}/g, printPageNum)
            .replace(/\{\{\s*\.?pages\s*\}\}/g, totalPrintPages);
          el.removeAttribute('data-format');
        });
      });

      return container.innerHTML;
    },

    _reflowPrintPages(container) {
      // Measure inside a shadow root carrying the EXACT shared geometry CSS, so
      // the print reflow sees the same cascade (zoom:0.9, document typography,
      // header/footer 11px, p-margins, img clamp) as both the on-screen preview
      // and the final PDF. Previously this measured bare content (no stylesheet
      // at all), so it broke pages at points the rendered output never used.
      const host = document.createElement('div');
      host.style.cssText = 'position:absolute;left:-10000px;top:0;visibility:hidden;pointer-events:none;width:210mm;';
      const shadow = host.attachShadow({ mode: 'open' });

      // Same canonical stylesheet (and web font) as the preview and the final
      // PDF, so the offscreen measurement breaks pages exactly where the rendered
      // output will.
      const measureFontLink = document.createElement('link');
      measureFontLink.rel = 'stylesheet';
      measureFontLink.href = this.getDocumentWebfontHref();
      shadow.appendChild(measureFontLink);
      const measureStyle = document.createElement('style');
      measureStyle.textContent = this.getDocumentCSS(':host');
      shadow.appendChild(measureStyle);
      shadow.appendChild(container);
      document.body.appendChild(host);

      try {
        const A4_W_PX = container.querySelector('main.page')?.getBoundingClientRect().width;
        if (!A4_W_PX) return;
        const maxPageHeight = A4_W_PX * (297 / 210);
        const MAX_ITERATIONS = 200;

        const getContentChildren = (page) => Array.from(page.children).filter(el =>
          !el.classList.contains('page-header-overlay') &&
          !el.classList.contains('page-footer-overlay') &&
          !el.classList.contains('page-header-content') &&
          !el.classList.contains('page-footer-content')
        );

        const getHeaderHeight = (page) => {
          const header = page.querySelector('.page-header-content');
          if (!header) return 0;
          const cs = window.getComputedStyle(header);
          return header.getBoundingClientRect().height
            + (parseFloat(cs.marginTop) || 0)
            + (parseFloat(cs.marginBottom) || 0);
        };

        const getEffectiveMax = (page) => {
          const headerSpace = getHeaderHeight(page);
          const footer = page.querySelector('.page-footer-content');
          let footerSpace = 0;
          if (footer) {
            const cs = window.getComputedStyle(footer);
            footerSpace = footer.getBoundingClientRect().height
              + (parseFloat(cs.marginTop) || 0)
              + (parseFloat(cs.marginBottom) || 0);
          }
          const paddingTop = parseFloat(window.getComputedStyle(page).paddingTop) || 0;
          const paddingBottom = parseFloat(window.getComputedStyle(page).paddingBottom) || 0;
          return maxPageHeight - paddingTop - paddingBottom - headerSpace - footerSpace;
        };

        const getContentTop = (page) => {
          const pageRect = page.getBoundingClientRect();
          const paddingTop = parseFloat(window.getComputedStyle(page).paddingTop) || 0;
          return pageRect.top + paddingTop + getHeaderHeight(page);
        };

        for (let iter = 0; iter < MAX_ITERATIONS; iter++) {
          const pages = container.querySelectorAll('main.page');
          if (!pages.length) break;

          // Compact
          let didCompact = false;
          for (let i = 0; i < pages.length - 1; i++) {
            const currentPage = pages[i];
            const nextPage = pages[i + 1];
            if (currentPage.closest('section.document-section') !== nextPage.closest('section.document-section')) continue;
            const nextChildren = getContentChildren(nextPage);
            if (!nextChildren.length) continue;

            const effectiveMax = getEffectiveMax(currentPage);
            const currentChildren = getContentChildren(currentPage);
            const contentTop = getContentTop(currentPage);
            const lastChild = currentChildren[currentChildren.length - 1];
            const usedHeight = lastChild ? lastChild.getBoundingClientRect().bottom - contentTop : 0;
            const firstNext = nextChildren[0];
            const nextBlockHeight = firstNext.getBoundingClientRect().height;

            if (usedHeight + nextBlockHeight + 10 <= effectiveMax) {
              const footer = currentPage.querySelector('.page-footer-content');
              if (footer) {
                currentPage.insertBefore(firstNext, footer);
              } else {
                currentPage.appendChild(firstNext);
              }
              didCompact = true;
              break;
            }
          }
          if (didCompact) continue;

          // Split
          let didSplit = false;
          for (let i = 0; i < pages.length; i++) {
            const page = pages[i];
            const contentTop = getContentTop(page);
            const effectiveMax = getEffectiveMax(page);
            const children = getContentChildren(page);
            if (children.length <= 1) continue;

            let overflowIdx = -1;
            for (let j = 0; j < children.length; j++) {
              if (children[j].getBoundingClientRect().bottom - contentTop > effectiveMax) {
                overflowIdx = j;
                break;
              }
            }
            if (overflowIdx === -1) continue;
            if (overflowIdx === 0) overflowIdx = 1;

            // Avoid orphaned headings / short blocks at the bottom of a page:
            // if the last element that would remain is a heading or a short
            // centered paragraph (like "Član 43."), pull it to the next page
            // together with the overflowing content so it stays with its body.
            while (overflowIdx > 1) {
              const lastKeep = children[overflowIdx - 1];
              const tag = lastKeep.tagName;
              const isHeading = /^H[1-6]$/.test(tag);
              const isShortCentered = tag === 'P'
                && (lastKeep.style.textAlign === 'center' || window.getComputedStyle(lastKeep).textAlign === 'center')
                && (lastKeep.textContent || '').trim().length < 80;
              const isEmpty = tag === 'P' && (lastKeep.textContent || '').trim().length === 0;
              if (isHeading || isShortCentered || isEmpty) {
                overflowIdx--;
              } else {
                break;
              }
            }

            const toMove = children.slice(overflowIdx);
            const existingNext = pages[i + 1];
            const sameSection = existingNext &&
              page.closest('section.document-section') === existingNext.closest('section.document-section');

            if (sameSection) {
              const firstContentChild = getContentChildren(existingNext)[0];
              toMove.forEach(el => {
                if (firstContentChild) {
                  existingNext.insertBefore(el, firstContentChild);
                } else {
                  const nextFooter = existingNext.querySelector('.page-footer-content');
                  if (nextFooter) {
                    existingNext.insertBefore(el, nextFooter);
                  } else {
                    existingNext.appendChild(el);
                  }
                }
              });
            } else {
              // Clone the page shell (no children) and copy its attributes so
              // the new page keeps the same header/footer config and inline
              // page styles; geometry comes from the shared stylesheet.
              const newPage = page.cloneNode(false);
              Array.from(page.attributes).forEach(attr => newPage.setAttribute(attr.name, attr.value));

              const header = page.querySelector('.page-header-content');
              const footer = page.querySelector('.page-footer-content');
              if (header) newPage.appendChild(header.cloneNode(true));
              if (footer) newPage.appendChild(footer.cloneNode(true));

              toMove.forEach(el => {
                const footerEl = newPage.querySelector('.page-footer-content');
                if (footerEl) {
                  newPage.insertBefore(el, footerEl);
                } else {
                  newPage.appendChild(el);
                }
              });

              page.parentNode.insertBefore(newPage, page.nextSibling);
            }

            didSplit = true;
            break;
          }

          if (!didSplit) break;
        }

        // Remove empty pages
        container.querySelectorAll('main.page').forEach(page => {
          if (getContentChildren(page).length === 0) page.remove();
        });

        // Orphan cleanup: if the last content element(s) on a page are
        // headings, short centered paragraphs (like "Član 43."), or empty
        // paragraphs, move them to the top of the next page so they stay
        // with their body content instead of sitting alone at the bottom.
        {
          const pagesAfterSplit = Array.from(container.querySelectorAll('main.page'));
          for (let i = 0; i < pagesAfterSplit.length - 1; i++) {
            const page = pagesAfterSplit[i];
            const nextPage = pagesAfterSplit[i + 1];
            if (page.closest('section.document-section') !== nextPage.closest('section.document-section')) continue;

            const children = getContentChildren(page);
            if (children.length <= 1) continue;

            let orphanStart = children.length;
            for (let j = children.length - 1; j >= 1; j--) {
              const el = children[j];
              const tag = el.tagName;
              const isHeading = /^H[1-6]$/.test(tag);
              const text = (el.textContent || '').trim();
              const isShortCentered = tag === 'P'
                && (el.style.textAlign === 'center' || window.getComputedStyle(el).textAlign === 'center')
                && text.length > 0 && text.length < 80;
              const isEmpty = tag === 'P' && text.length === 0;
              if (isHeading || isShortCentered || isEmpty) {
                orphanStart = j;
              } else {
                break;
              }
            }

            if (orphanStart < children.length) {
              const toMove = children.slice(orphanStart);
              const firstNextChild = getContentChildren(nextPage)[0];
              toMove.forEach(el => {
                if (firstNextChild) {
                  nextPage.insertBefore(el, firstNextChild);
                } else {
                  const footer = nextPage.querySelector('.page-footer-content');
                  if (footer) {
                    nextPage.insertBefore(el, footer);
                  } else {
                    nextPage.appendChild(el);
                  }
                }
              });
            }
          }
        }

        // Remove pages left empty by the orphan cleanup
        container.querySelectorAll('main.page').forEach(page => {
          if (getContentChildren(page).length === 0) page.remove();
        });

        // Ensure padding-top clears the header so content isn't hidden behind
        // it. The inline padding-top persists in the returned HTML, so the
        // server-rendered PDF gets the same adjustment as the preview.
        container.querySelectorAll('main.page').forEach(page => {
          const header = page.querySelector('.page-header-content');
          if (!header) return;
          const headerHeight = header.getBoundingClientRect().height;
          const currentPadding = parseFloat(window.getComputedStyle(page).paddingTop) || 0;
          if (currentPadding < headerHeight) {
            page.style.paddingTop = headerHeight + 'px';
          }
        });
      } finally {
        shadow.removeChild(container);
        document.body.removeChild(host);
      }
    },

    // Append the same web-safe fallback chain the print path uses to every inline
    // font-family that names a single (possibly unavailable) font like "Aptos".
    // Without this, a font missing on the machine falls back to the browser's
    // generic default in the preview but to Arial on the print server — different
    // metrics, different wrapping, different page breaks. Pinning the chain makes
    // the preview resolve to the SAME font the server will use, so preview == PDF.
    _normalizeInlineFontFallbacks(container) {
      const defaultFallback = 'Arial, "Liberation Sans", "Noto Sans", Helvetica, sans-serif';
      container.querySelectorAll("[style]").forEach((el) => {
        const font = el.style.fontFamily;
        if (font) {
          const lower = font.toLowerCase();
          if (!lower.includes("sans-serif") && !lower.includes("monospace") && !lower.includes("serif") && !lower.includes("cursive") && !lower.includes("fantasy")) {
            el.style.fontFamily = `${font}, ${defaultFallback}`;
          }
        }
      });
    },

    // Convert a computed line-height ("21px", "normal", "1.5") into a unitless
    // ratio string relative to the given font-size, so it scales per element the
    // way the editor's inherited unitless line-height does. "normal" and already
    // unitless values pass through unchanged.
    _toUnitlessLineHeight(lineHeight, fontSize) {
      if (!lineHeight || lineHeight === "normal") return "normal";
      const lhPx = parseFloat(lineHeight);
      const fsPx = parseFloat(fontSize);
      // Already unitless (no "px") — keep as-is.
      if (!/px\s*$/.test(String(lineHeight).trim())) return String(lineHeight);
      if (!lhPx || !fsPx) return "normal";
      return (lhPx / fsPx).toFixed(4);
    },

    getPrintableEditorStyles() {
      // Explicit document default font, identical to edit mode (.tiptap) and the
      // preview (:host). Arial is web-safe and present on the print server, so
      // all three surfaces match. User font choices arrive as inline
      // font-family on spans and still win over this base.
      const defaultFontFamily = "Arial, Helvetica, sans-serif";
      const editorDom = this.editor && this.editor.view && this.editor.view.dom;
      // Prefer the live editor root; fall back to the component root ($el, the
      // .editor-container) which is always connected and sits in the SAME app
      // cascade — so it reports the same inherited base size the editor would.
      const sampleEl = (editorDom && editorDom.isConnected) ? editorDom : this.$el;

      if (sampleEl && sampleEl.isConnected) {
        const styles = window.getComputedStyle(sampleEl);
        const result = {
          fontFamily: defaultFontFamily,
          fontSize: styles.fontSize || "16px",
          // line-height MUST be unitless so it scales with per-element font-size
          // exactly like the editor (which inherits Bootstrap's unitless 1.5).
          // getComputedStyle resolves line-height to px (e.g. "21px" for a 14px
          // base); applying that fixed px to :host/html,body would crush every
          // block whose font is larger than the base (16px body, headings) —
          // their lines would get 21px in preview/print but 24px in the editor,
          // shifting where blocks fall across pages. Convert back to the ratio.
          lineHeight: this._toUnitlessLineHeight(styles.lineHeight, styles.fontSize),
        };
        this._cachedEditorStyles = result;
        return result;
      }

      return this._cachedEditorStyles || {
        fontFamily: defaultFontFamily,
        fontSize: "16px",
        lineHeight: "normal",
      };
    },

    // Google Fonts stylesheet for the one web font the document picker offers
    // that isn't a system font. Loaded identically into the preview shadow DOM
    // and the print HTML so "Inter" resolves the same way on both.
    getDocumentWebfontHref() {
      return "https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap";
    },

    // The embedded document fonts that aren't system fonts and must be loaded
    // before any pagination measurement. Each entry is [file, weight, style].
    // The editor + preview pick these up from the global @font-face block (which
    // reaches the shadow DOM since fonts are document-scoped); the print HTML
    // re-declares them with absolute URLs via getPrintFontFaceCSS() so the
    // server-side renderer uses the real glyphs too.
    documentEmbeddedFonts() {
      return [
        ["Aptos.ttf", 400, "normal"],
        ["Aptos-Bold.ttf", 700, "normal"],
        ["Aptos-Italic.ttf", 400, "italic"],
        ["Aptos-Bold-Italic.ttf", 700, "italic"],
        ["Aptos-Light.ttf", 300, "normal"],
        ["Aptos-SemiBold.ttf", 600, "normal"],
      ];
    },

    // @font-face block with ABSOLUTE URLs for the print HTML. The PDF is rendered
    // server-side and has no access to the app's locally-installed fonts, so the
    // embedded files must be referenced by a public URL the server can fetch
    // (this deployment's own origin, which also serves /fonts/aptos/*).
    getPrintFontFaceCSS() {
      const origin = (typeof window !== "undefined" && window.location && window.location.origin) || "";
      return this.documentEmbeddedFonts().map(([file, weight, style]) =>
        `@font-face{font-family:'Aptos';src:url('${origin}/fonts/aptos/${file}') format('truetype');font-weight:${weight};font-style:${style};font-display:swap;}`
      ).join("\n");
    },

    // Force the embedded web fonts (Aptos weights + Inter) to load, then resolve
    // once the FontFaceSet is ready. Pagination MUST wait for this — otherwise it
    // measures with the swap-fallback (Arial) and breaks pages where the real
    // font does not, even though the text later swaps to the real glyphs.
    _loadDocumentFonts() {
      if (typeof document === "undefined" || !document.fonts || !document.fonts.load) {
        return Promise.resolve();
      }
      const specs = [];
      this.documentEmbeddedFonts().forEach(([, weight, style]) => {
        specs.push(`${style === "italic" ? "italic " : ""}${weight} 16px Aptos`);
      });
      specs.push("400 16px Inter", "700 16px Inter");
      return Promise.all(specs.map(s => document.fonts.load(s).catch(() => {})))
        .then(() => document.fonts.ready)
        .catch(() => {});
    },

    // ── THE single source of truth for document rendering ──────────────────
    // Returns the COMPLETE stylesheet that makes a surface render a document
    // identically to the editor: base typography (font, size, UNITLESS line
    // height, colour, wrapping, box model) on `rootSelector`, plus the shared
    // page geometry + probed block typography. Every surface that is NOT the
    // live editor builds its stylesheet from THIS function and nothing else, so
    // a property only ever has to be defined once here — never patched per
    // surface:
    //   • preview  → getDocumentCSS(':host')      + preview-only chrome
    //   • print    → getDocumentCSS('html, body') + print-only page rules
    //   • measure  → getDocumentCSS(':host')
    // The editor itself is the reference these values are probed from
    // (getPrintableEditorStyles / getDocumentTypographyCSS), so all four agree.
    getDocumentCSS(rootSelector) {
      return `${this.getDocumentBaseCSS(rootSelector)}\n${this.getSharedPageGeometryCSS()}`;
    },

    // The per-surface root rule: everything a document inherits from its surface
    // root. Defined ONCE; `rootSelector` is :host (shadow) or html, body (print)
    // or a scoping selector. line-height is unitless so it scales per element.
    getDocumentBaseCSS(rootSelector) {
      // Surface-root reset (isolated surfaces only) + the shared typography root.
      // The reset (display/margin/padding/background) is what must NOT be applied
      // to the editor; the typography root IS shared with the editor.
      return `
        ${rootSelector} {
          display: block;
          margin: 0;
          padding: 0;
          background: #fff;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        ${this.getDocumentTypographyRootCSS(rootSelector)}`;
    },

    // Single source of truth for every layout-affecting page rule. Folded into
    // getDocumentCSS (above) together with the base typography, so all non-editor
    // surfaces share ONE stylesheet. Anything here influences where a page
    // breaks, so it is emitted identically to preview, print and the
    // print-measurement pass. Preview-only chrome (shadows, page gaps, field
    // highlights, section separators) and print-only rules (@page, fixed height,
    // hard page breaks) are layered on top — never inside it — so they can never
    // shift a break.
    getSharedPageGeometryCSS() {
      return `
        section.document-section { display: block; }
        main.page {
          display: block;
          box-sizing: border-box;
          width: 210mm;
          min-height: 297mm;
          padding: 10mm;
          margin: 0;
          background-color: #fff;
          background-image: var(--page-bg-image, none);
          background-size: contain;
          background-repeat: no-repeat;
          background-position: center;
          position: relative;
          overflow-wrap: break-word;
          word-break: break-word;
        }
        main.page > * { zoom: 0.9; transform-origin: top left; }
        .page-header-content { position: absolute; top: 0; left: 0; right: 0; zoom: 1; font-size: 11px; color: #555; }
        .page-footer-content { position: absolute; bottom: 0; left: 0; right: 0; zoom: 1; font-size: 11px; color: #555; }
        .page-footer-content p, .page-header-content p { margin: 0; min-height: 1em; }
        .page-number-node { background: none; }
        /* tableWrapper margin is shared (in getDocumentContentCSS); overflow is
           surface chrome — visible for print/preview, scrollable in the editor. */
        .tableWrapper { overflow: visible; }

        /* Within-page document content rules — the same builder the editor uses
           (scoped) so edit/preview/print render content identically. */
        ${this.getDocumentContentCSS()}
      `;
    },

    // The within-page document CONTENT rules (typography + tables + text), shared
    // by EVERY surface INCLUDING the live editor. These are the drift-prone rules
    // that must be byte-identical edit/preview/print; defining them once here is
    // what stops the per-property whack-a-mole. `scope` prefixes every selector:
    // '' for the isolated preview/print/measurement surfaces, or the editor
    // content root (.editor-container .tiptap) for edit mode. Page geometry
    // (main.page, headers/footers, page-number chrome, tableWrapper) and all
    // surface chrome live OUTSIDE this and are NOT pushed into the editor.
    getDocumentContentCSS(scope = "") {
      const p = scope;
      return `
        ${p}*, ${p}*::before, ${p}*::after { box-sizing: border-box; }
        ${p}p { margin-top: 0; margin-bottom: 1em; min-height: 1em; }
        ${p}p:empty::before { content: "\\00a0"; }
        ${p}hr { margin: 1em 0; border: none; border-top: 1px solid rgba(0, 0, 0, 0.1); }
        ${p}:first-child { margin-top: 0; }
        ${p}h1, ${p}h2, ${p}h3, ${p}h4, ${p}h5, ${p}h6 { color: #000; }
        /* table-layout auto matches the editor (ProseMirror default): columns
           size to content so cells wrap exactly as in edit mode.
           The 1.5rem block margin is the crux of edit/preview parity: the editor
           wraps every table in <div class="tableWrapper"> that carries this
           margin, but editor.getHTML() emits a BARE <table> (no wrapper) to
           preview/print. So the margin is put on the table itself here (bare
           tables in preview/print get it) AND on .tableWrapper (editor), with the
           wrapped table reset to 0 so the editor doesn't double it. */
        ${p}table { border-collapse: collapse; table-layout: auto; margin: 1.5rem 0; }
        ${p}.tableWrapper { margin: 1.5rem 0; }
        ${p}.tableWrapper > table { margin: 0; }
        ${p}td, ${p}th { border: 1px solid #000; box-sizing: border-box; min-width: 1em; padding: 6px 8px; position: relative; vertical-align: top; }
        ${p}th { font-weight: bold; text-align: left; }
        /* Zero BOTH block margins inside cells; out-specifies the probed
           p/heading margin rules below (the leading table type selector raises
           specificity above those single-type selectors). */
        ${p}table td > *, ${p}table th > * { margin-top: 0; margin-bottom: 0; }
        ${p}tr, ${p}img { break-inside: avoid; page-break-inside: avoid; }
        ${p}img { max-width: 100%; height: auto; }

        /* Document block typography probed from the live editor so every surface
           matches edit mode. */
        ${this.getDocumentTypographyCSS(scope)}
      `;
    },

    // Root typography a document inherits (font, size, UNITLESS line-height,
    // colour, wrapping, white-space). Applied to each surface's content root:
    // :host / html,body for the isolated surfaces, .editor-container .tiptap for
    // the live editor. Kept separate from the surface-reset (display/margin/
    // padding/background) which must NOT touch the editor (it would paint the
    // dark canvas white).
    getDocumentTypographyRootCSS(rootSelector) {
      const s = this.getPrintableEditorStyles();
      return `${rootSelector} {
        color: #000;
        font-family: ${s.fontFamily};
        font-size: ${s.fontSize};
        line-height: ${s.lineHeight};
        overflow-wrap: break-word;
        word-break: break-word;
        white-space: break-spaces;
      }`;
    },

    // Inject the canonical document CONTENT stylesheet into the light-DOM editor,
    // scoped to the ProseMirror content root (.editor-container .tiptap), so edit
    // mode renders document content from the SAME source as preview/print. Page
    // geometry and editor chrome stay in the component SCSS. Idempotent (values
    // are cached + computed before injection, so the typography probe still reads
    // the app cascade, not this injected rule).
    applyEditorDocumentCSS() {
      if (typeof document === "undefined") return;
      const ROOT = ".editor-container .tiptap";
      const css =
        `${this.getDocumentTypographyRootCSS(ROOT)}\n${this.getDocumentContentCSS(ROOT + " ")}`;
      let styleEl = document.getElementById("protokol-editor-document-css");
      if (!styleEl) {
        styleEl = document.createElement("style");
        styleEl.id = "protokol-editor-document-css";
        styleEl.setAttribute("data-protokol", "editor-document-css");
        document.head.appendChild(styleEl);
      }
      styleEl.textContent = css;
    },

    // In EDIT mode the editor lives in the light DOM and inherits the app's
    // global block typography (e.g. CoreUI/Bootstrap h1{font-size:2.5rem}).
    // The Preview renders in a shadow DOM and the print output is a standalone
    // document — both are isolated from those global rules, so headings/lists
    // would fall back to the browser's UA defaults and render at a different
    // size/weight than the editor. To keep all three surfaces identical we
    // probe the editor's actual computed block styles here and replay them as
    // explicit rules into the preview/print stylesheets. Auto-syncs with theme.
    getDocumentTypographyCSS(scope = "") {
      // Probe once, cache the UNSCOPED [selector, declarations] pairs, then emit
      // them prefixed with `scope` so the same probe feeds every surface
      // (preview/print bare; editor scoped to .editor-container .tiptap).
      if (this._cachedTypographyRules) {
        return this._cachedTypographyRules.map(([sel, decl]) => `${scope}${sel}{${decl}}`).join("\n");
      }

      const root = this.$el && this.$el.isConnected
        ? this.$el
        : (this.editor && this.editor.view && this.editor.view.dom);
      if (!root || !root.isConnected) return "";

      const probe = document.createElement("div");
      probe.className = "tiptap";
      probe.setAttribute("aria-hidden", "true");
      probe.style.cssText =
        "position:absolute!important;left:-99999px!important;top:0!important;" +
        "width:210mm;visibility:hidden!important;pointer-events:none!important;";

      ["h1", "h2", "h3", "h4", "h5", "h6", "p", "blockquote"].forEach((t) => {
        const el = document.createElement(t);
        el.textContent = "x";
        probe.appendChild(el);
      });
      const ul = document.createElement("ul");
      ul.appendChild(Object.assign(document.createElement("li"), { textContent: "x" }));
      probe.appendChild(ul);
      const ol = document.createElement("ol");
      ol.appendChild(Object.assign(document.createElement("li"), { textContent: "x" }));
      probe.appendChild(ol);

      root.appendChild(probe);
      try {
        // Returns [selector, declarations] (declarations WITHOUT the selector or
        // braces) so the caller can prefix the selector with any scope.
        const ruleFor = (el, sel) => {
          const s = window.getComputedStyle(el);
          // Only structural typography — NOT font-family/color, which must keep
          // inheriting the document's own font + the explicit color rules.
          // line-height is emitted UNITLESS (ratio to this element's font-size)
          // so inline font-size overrides on child spans scale their leading the
          // same way they do in the editor, instead of being pinned to the px
          // line-height probed at the block's base size.
          //
          // font-weight is emitted ONLY for headings, which carry an intrinsic
          // non-normal weight that must match the editor. For p/li/blockquote we
          // MUST NOT pin it: their normal weight is the inherited value, and an
          // explicit p{font-weight:400} would override bold inherited from a
          // bold context (e.g. a <p> inside a <th>), making text that is bold in
          // the editor render non-bold in the preview/PDF.
          const isHeading = /^h[1-6]$/.test(sel);
          const weight = isHeading ? `font-weight:${s.fontWeight};` : "";
          return [sel, `font-size:${s.fontSize};${weight}` +
            `line-height:${this._toUnitlessLineHeight(s.lineHeight, s.fontSize)};` +
            `margin:${s.marginTop} ${s.marginRight} ${s.marginBottom} ${s.marginLeft};`];
        };
        const rules = [];
        ["h1", "h2", "h3", "h4", "h5", "h6", "p", "blockquote"].forEach((t) => {
          const el = probe.querySelector(t);
          if (el) rules.push(ruleFor(el, t));
        });
        const li = probe.querySelector("ul li");
        if (li) rules.push(ruleFor(li, "li"));
        ["ul", "ol"].forEach((t) => {
          const el = probe.querySelector(t);
          if (el) {
            const s = window.getComputedStyle(el);
            rules.push([t, `margin:${s.marginTop} ${s.marginRight} ${s.marginBottom} ${s.marginLeft};` +
              `padding-left:${s.paddingLeft};`]);
          }
        });
        this._cachedTypographyRules = rules;
        return rules.map(([sel, decl]) => `${scope}${sel}{${decl}}`).join("\n");
      } finally {
        root.removeChild(probe);
      }
    },

    showContextMenu(e) {
      e.preventDefault();
      if (e.target.closest(".page")) {
        const { state } = this.editor;
        this.contextMenu.x = e.clientX;
        this.contextMenu.y = e.clientY;
        this.contextMenu.hasSelection = !state.selection.empty;
        this.contextMenu.hasComment =
          !!e.target.closest(".protokol-comment") ||
          !!e.target.classList.contains("protokol-comment");
        this.contextMenu.canMerge = this.editorSections.length > 1 && this.getCurrentSectionIndex() > 0;
        this.contextMenu.visible = true;
        if (this.contextMenu.hasSelection) {
          const { from, to } = state.selection;
          const selectedText = state.doc.textBetween(from, to, " ");

          function extractConditionAndContent(text) {
            const ifBlockRegex =
              /^{{\s*if\s+(?:(or|and)\s+)?((?:\.(?:\w+\.)*\w+)(?:\s+(?:\.(?:\w+\.)*\w+))*)\s*}}/;
            const contentRegex = /}}(.*){{\s*end\s*}}$/;

            const ifBlockMatch = text.match(ifBlockRegex);
            const contentMatch = text.match(contentRegex);

            const condition = ifBlockMatch ? ifBlockMatch[2] : null;
            const modifier = ifBlockMatch ? ifBlockMatch[1] : null;
            const content = contentMatch ? contentMatch[1].trim() : null;
            // content is null if empty

            return { condition, modifier, content };
          }

          const { condition, modifier, content } =
            extractConditionAndContent(selectedText);

          this.contextMenu.isConditionSelected = !!condition;
          if (this.contextMenu.isConditionSelected) {
            this.contextMenu.conditionSelectionOptions = {
              condition,
              modifier,
              content,
            };
          }
        } else {
          this.contextMenu.isConditionSelected = false;
        }
      }
    },
    hideContextMenu() {
      this.contextMenu.visible = false;
    },

    isHTML(content) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(content, "text/html");

      return Array.from(doc.body.childNodes).some(
        (node) => node.nodeType === 1
      );
    },

    async setupDocumentPreview() {
      const RESERVED_KEYWORDS = ['page', 'pages'];
      const printBlockRegex = /{{\s*(\.(?:\w+\.)*\w+)\s*}}/g;
      const ifBlockRegex =
        /{{\s*if\s+(?:(or|and)\s+)?((?:\.(?:\w+\.)*\w+)(?:\s+(?:\.(?:\w+\.)*\w+))*)\s*}}/g;
      const rangeBlockRegex = /{{\s*range\s+(\.(?:\w+\.)*\w+)\s*}}/g;
      const endBlockRegex = /{{\s*end\s*}}/g;

      let template = this.editor.getHTML();

      // First, process if directive
      template = template.replace(
        ifBlockRegex,
        (match, modifier, condition) => {
          return `<span data-clause="if" data-refs="${condition.trim()}" data-ref-modifier="${
            modifier || ""
          }">`;
        }
      );

      // Process range directive
      template = template.replace(rangeBlockRegex, (match, variable) => {
        return `<span data-clause="range" data-ref="${variable.trim()}">`;
      });

      // Process end directive
      template = template.replace(endBlockRegex, (match) => {
        return `</span>`;
      });

      // Then process remaining placeholders outside of if blocks (skip reserved keywords)
      template = template.replace(printBlockRegex, (match, variable) => {
        const name = variable.trim().replace(/^\./, '');
        if (RESERVED_KEYWORDS.includes(name)) return match;
        return `<span data-clause="print" data-ref="${variable.trim()}"></span>`;
      });

      this.templateTransformed = template;

      this.documentFields = [];
      if (!this.data) this.data = {};

      const previewEl = document.querySelector("#document-preview");

      if (!previewEl) return;

      let shadowRoot;
      if (previewEl.shadowRoot) {
        shadowRoot = previewEl.shadowRoot;
        shadowRoot.innerHTML = "";
      } else {
        shadowRoot = previewEl.attachShadow({ mode: "open" });
      }

      shadowRoot.innerHTML = this.templateTransformed;

      // Pin inline-font fallbacks to the SAME chain the print path uses, so a
      // font missing from this machine (e.g. Aptos) resolves to the identical
      // substitute the PDF server will pick — keeping preview wrapping == PDF.
      this._normalizeInlineFontFallbacks(shadowRoot);

      const refsToReplace = shadowRoot.querySelectorAll("[data-clause]");

      // Same web font as the print HTML so "Inter" resolves identically.
      const fontLink = document.createElement('link');
      fontLink.rel = 'stylesheet';
      fontLink.href = this.getDocumentWebfontHref();
      shadowRoot.appendChild(fontLink);

      // Extract and inject all page styles inside shadowRoot
      const style = document.createElement("style");

      // Canonical document stylesheet — byte-identical to what the print HTML and
      // the print-measurement pass use (getDocumentCSS), so the preview renders
      // and paginates exactly like the PDF. Preview-only chrome (page shadows,
      // gaps, field highlights, section separators) is layered on top below and
      // never changes geometry.
      const previewPageStyles = `
        ${this.getDocumentCSS(':host')}

        /* ─── Preview-only chrome — must NOT change measured geometry ─── */
        main.page {
          margin: 0 auto 32px;
          box-shadow: 0 1px 4px rgba(0,0,0,0.08), 0 6px 20px rgba(0,0,0,0.14), 0 16px 48px rgba(0,0,0,0.22);
          border-radius: 2px;
        }
        main.page:first-of-type { margin-top: 20px; }
        main.page:last-of-type { margin-bottom: 20px; }
        section.document-section + section.document-section {
          margin-top: 40px;
          padding-top: 24px;
          border-top: 2px dashed rgba(0,0,0,0.12);
        }
        section.document-section + section.document-section::before {
          content: attr(data-name);
          display: block;
          text-align: center;
          padding: 0 0 16px;
          color: rgba(0,0,0,0.4);
          font-size: 13px;
          font-weight: 500;
          letter-spacing: 0.5px;
        }
        /* Field highlights use outline + background only (no border/padding/
           inline-block) so a placeholder occupies exactly the space of the
           plain text it becomes in print — keeps line wrapping identical. */
        .chooseSource {
          cursor: pointer;
          outline: 2px solid #f59e0b;
          background-color: rgba(245, 158, 11, 0.08);
          color: #000;
          transition: all 0.15s ease;
        }
        .chooseSource[active="true"] {
          outline-color: #10b981;
          background-color: rgba(16, 185, 129, 0.08);
        }
        .ifClause {
          cursor: pointer;
          outline: 1px solid #818cf8;
          background-color: rgba(129, 140, 248, 0.08);
          color: #000;
        }
        .ifClauseCondition { font-family: monospace; font-size: 0.9em; }
        .rangeClause {
          cursor: pointer;
          outline: 1px solid #34d399;
          background-color: rgba(52, 211, 153, 0.08);
          color: #000;
        }
        .rangeClauseSource { font-family: monospace; font-size: 0.9em; }
        .hoverGray:hover:not(:has(.hoverGray:hover)) {
          background-color: rgba(211, 211, 211, 0.5);
        }
      `;

      style.textContent = previewPageStyles;
      shadowRoot.appendChild(style);

      const root = this;

      // Define components once
      const PrintClause = Vue.extend({
        template:
          '<span :id="id" class="chooseSource hoverGray" :active="isFieldPopulated" v-html="content" @click="onFieldClick"></span>',
        props: ["refName"],
        computed: {
          isLocalScoped() {
            return !!this.$el.closest(".rangeClause");
          },
          path() {
            return this.refName.replace(/^\./, "");
          },
          paths() {
            if (this.isLocalScoped) {
              return [];
            } else {
              return [this.path];
            }
          },
          content() {
            // if (v) {
            //   if (root.isHTML(v)) {
            //     const parser = new DOMParser();
            //     const doc = parser.parseFromString(v, "text/html");
            //     const computedStyles = window.getComputedStyle(this.$el);
            //     doc.querySelectorAll("*").forEach((el) => {
            //       el.style.color = computedStyles.color;
            //       el.style.fontFamily = computedStyles.fontFamily;
            //       el.style.fontSize = computedStyles.fontSize;
            //     });
            //     // _.set(
            //     //   root.data,
            //     //   this.path,
            //     //   doc.querySelector("body").innerHTML
            //     // );
            //     return doc.querySelector("body").innerHTML;
            //   }
            // }
            const v = _.get(root.data, this.path, null);
            if (v) return v;

            const field = root.fields.find(({ field }) => field == this.path);
            if (field && field.label) return field.label;

            return this.path;
          },
          isFieldPopulated() {
            const scopeAbove = this.$el.parentElement.closest(".rangeClause");
            if (scopeAbove) {
              return scopeAbove.getAttribute("active");
            } else {
              const v = _.get(root.data, this.path, null);
              return !!v && v.length > 0;
            }
          },
          id() {
            return `tooltip-field-indoc-${this.path}`;
          },
        },
        methods: {
          onFieldClick(e) {
            root.onFieldClick(e, { value: this.path });
          },
        },
      });

      const RangeClause = Vue.extend({
        template: `<div :id="id" :data-ref="refName" class="rangeClause hoverGray" :active="isFieldPopulated" @click="onFieldClick"><span class="rangeClauseSource">for {{ path }}</span><slot></slot></div>`,
        props: ["refName"],
        computed: {
          isLocalScoped() {
            return !!this.$el.parentElement.closest(".rangeClause");
          },
          path() {
            return this.refName.replace(/^\./, "");
          },
          paths() {
            // parentElement to exclude self from search
            if (this.isLocalScoped) {
              return [];
            } else {
              return [this.path];
            }
          },
          isFieldPopulated() {
            const scopeAbove = this.$el.parentElement.closest(".rangeClause");
            if (scopeAbove) {
              return scopeAbove.getAttribute("active");
            } else {
              const v = _.get(root.data, this.path, null);
              return !!v && v.length > 0;
            }
          },
          id() {
            return `tooltip-field-indoc-${this.path}`;
          },
        },
        methods: {
          onFieldClick(e) {
            root.onFieldClick(e, { value: this.path });
          },
        },
      });

      const IfClause = Vue.extend({
        template: `<div :data-refs="refNames" :data-ref-modifier="refModifier" :active="isFieldPopulated" class="ifClause hoverGray" @click="onFieldClick"><span class="ifClauseCondition">if {{ paths.join(\` \${refModifier} \`) }}</span><slot></slot></div>`,
        props: ["refNames", "refModifier"],
        computed: {
          isLocalScoped() {
            return !!this.$el.closest(".rangeClause");
          },
          paths() {
            if (this.isLocalScoped) {
              return [];
            } else {
              return this.refNames.map((refName) => refName.replace(/^\./, ""));
            }
          },
          isFieldPopulated() {
            const scopeAbove = this.$el.parentElement.closest(".rangeClause"); // not responsive (not listening)
            if (scopeAbove) {
              return scopeAbove.getAttribute("active");
            } else {
              return this.paths
                .map((path) => {
                  const v = _.get(root.data, path, null);
                  return !!v && v.length > 0;
                })
                .reduce((v1, v2) => v1 && v2);
            }
          },
          // If can have multiple references so which tooltip should show?
        },
      });

      const components = [];

      for (const r of refsToReplace) {
        const clauseType = r.getAttribute("data-clause");
        const refName = r.getAttribute("data-ref");

        if (clauseType === "print") {
          const dynamicComponent = new PrintClause({
            propsData: { refName },
          });
          dynamicComponent.$mount(r);
          components.push(dynamicComponent);
        }

        if (clauseType === "if") {
          const refModifier = r.getAttribute("data-ref-modifier") || null;
          const refNames = r.getAttribute("data-refs").split(/\s+/g);

          const children = Array.from(r.childNodes);

          const wrapper = document.createElement("div");
          r.replaceWith(wrapper); // Replace in DOM so Vue can mount

          const ifComponent = new IfClause({
            propsData: { refNames, refModifier },
          });

          ifComponent.$mount(wrapper);
          components.push(ifComponent);

          // Move existing child nodes inside slot
          children.forEach((child) => {
            ifComponent.$el.appendChild(child);
          });
        }

        if (clauseType === "range") {
          const children = Array.from(r.childNodes);

          const wrapper = document.createElement("div");
          r.replaceWith(wrapper); // Replace in DOM so Vue can mount

          const rangeComponent = new RangeClause({
            propsData: { refName },
          });

          rangeComponent.$mount(wrapper);
          components.push(rangeComponent);

          // Move existing child nodes inside slot
          children.forEach((child) => {
            rangeComponent.$el.appendChild(child);
          });
        }
      }

      const data = {};
      for (const comp of components) {
        const paths = comp.paths;
        for (const path of paths) {
          if (!this.documentFields.includes(path)) {
            _.set(data, path, null);
            this.documentFields.push(path);
          }
        }
      }
      //   this.data = _.merge({}, data, this.data); // Persist fields even if they are not on the paper (They can still be deleted with "Remove")
      function mergeWithWhitelist(base, override) {
        const result = _.cloneDeep(base);

        _.forOwn(base, (value, key) => {
          if (_.isPlainObject(value)) {
            result[key] = mergeWithWhitelist(value, override[key] || {});
          } else if (override && key in override) {
            result[key] = override[key];
          }
        });

        return result;
      }
      this.data = mergeWithWhitelist(data, this.data);
      this.fieldsSetup();
      this.$nextTick(() => this.reflowPreviewPages());
      // Re-paginate once the embedded web fonts have actually loaded. The pass
      // above may have measured with the swap-fallback (Arial), which breaks
      // pages where the real font (e.g. Aptos) does not — this authoritative
      // pass makes the preview match the editor and the PDF.
      this._loadDocumentFonts().then(() => this.$nextTick(() => this.reflowPreviewPages()));
    },

    _getPreviewContentChildren(pageEl) {
      return Array.from(pageEl.children).filter(el =>
        !el.classList.contains('page-header-overlay') &&
        !el.classList.contains('page-footer-overlay') &&
        !el.classList.contains('page-header-content') &&
        !el.classList.contains('page-footer-content')
      );
    },

    _getPreviewHeaderHeight(pageEl) {
      const header = pageEl.querySelector('.page-header-content');
      if (!header) return 0;
      const cs = window.getComputedStyle(header);
      return header.getBoundingClientRect().height
        + (parseFloat(cs.marginTop) || 0)
        + (parseFloat(cs.marginBottom) || 0);
    },

    _getPreviewEffectiveMax(pageEl, maxPageHeight) {
      const header = pageEl.querySelector('.page-header-content');
      let headerSpace = 0;
      if (header) {
        const cs = window.getComputedStyle(header);
        headerSpace = header.getBoundingClientRect().height
          + (parseFloat(cs.marginTop) || 0)
          + (parseFloat(cs.marginBottom) || 0);
      }
      const footer = pageEl.querySelector('.page-footer-content');
      let footerSpace = 0;
      if (footer) {
        const cs = window.getComputedStyle(footer);
        footerSpace = footer.getBoundingClientRect().height
          + (parseFloat(cs.marginTop) || 0)
          + (parseFloat(cs.marginBottom) || 0);
      }
      const paddingTop = parseFloat(window.getComputedStyle(pageEl).paddingTop) || 0;
      const paddingBottom = parseFloat(window.getComputedStyle(pageEl).paddingBottom) || 0;
      return maxPageHeight - paddingTop - paddingBottom - headerSpace - footerSpace;
    },

    reflowPreviewPages() {
      const previewEl = document.querySelector('#document-preview');
      if (!previewEl || !previewEl.shadowRoot) return;
      const shadowRoot = previewEl.shadowRoot;

      const A4_RATIO = 297 / 210;
      const MAX_ITERATIONS = 200;

      for (let iter = 0; iter < MAX_ITERATIONS; iter++) {
        const pages = shadowRoot.querySelectorAll('main.page');
        if (!pages.length) break;

        const pageWidth = pages[0].getBoundingClientRect().width;
        if (!pageWidth) break;
        const maxPageHeight = pageWidth * A4_RATIO;

        // Compact: pull blocks from next page into current page if they fit
        let didCompact = false;
        for (let i = 0; i < pages.length - 1; i++) {
          const currentPage = pages[i];
          const nextPage = pages[i + 1];
          if (currentPage.closest('section.document-section') !== nextPage.closest('section.document-section')) continue;
          const nextChildren = this._getPreviewContentChildren(nextPage);
          if (!nextChildren.length) continue;

          const effectiveMax = this._getPreviewEffectiveMax(currentPage, maxPageHeight);
          const currentChildren = this._getPreviewContentChildren(currentPage);
          const pageRect = currentPage.getBoundingClientRect();
          const paddingTop = parseFloat(window.getComputedStyle(currentPage).paddingTop) || 0;
          const headerHeight = this._getPreviewHeaderHeight(currentPage);
          const contentTop = pageRect.top + paddingTop + headerHeight;
          const lastChild = currentChildren[currentChildren.length - 1];
          const usedHeight = lastChild
            ? lastChild.getBoundingClientRect().bottom - contentTop
            : 0;

          const firstNext = nextChildren[0];
          const nextBlockHeight = firstNext.getBoundingClientRect().height;
          const marginBuffer = 10;

          if (usedHeight + nextBlockHeight + marginBuffer <= effectiveMax) {
            const footer = currentPage.querySelector('.page-footer-content');
            if (footer) {
              currentPage.insertBefore(firstNext, footer);
            } else {
              currentPage.appendChild(firstNext);
            }
            didCompact = true;
            break;
          }
        }
        if (didCompact) continue;

        // Split: move overflowing blocks to a new page
        let didSplit = false;
        for (let i = 0; i < pages.length; i++) {
          const page = pages[i];
          const pageRect = page.getBoundingClientRect();
          const pagePaddingTop = parseFloat(window.getComputedStyle(page).paddingTop) || 0;
          const pageHeaderHeight = this._getPreviewHeaderHeight(page);
          const pageContentTop = pageRect.top + pagePaddingTop + pageHeaderHeight;
          const effectiveMax = this._getPreviewEffectiveMax(page, maxPageHeight);

          const children = this._getPreviewContentChildren(page);
          if (children.length <= 1) continue;

          let overflowIdx = -1;
          for (let j = 0; j < children.length; j++) {
            const childRect = children[j].getBoundingClientRect();
            if (childRect.bottom - pageContentTop > effectiveMax) {
              overflowIdx = j;
              break;
            }
          }
          if (overflowIdx === -1) continue;
          if (overflowIdx === 0) overflowIdx = 1;

          while (overflowIdx > 1) {
            const lastKeep = children[overflowIdx - 1];
            const tag = lastKeep.tagName;
            const isHeading = /^H[1-6]$/.test(tag);
            const isShortCentered = tag === 'P'
              && (lastKeep.style.textAlign === 'center' || window.getComputedStyle(lastKeep).textAlign === 'center')
              && (lastKeep.textContent || '').trim().length < 80;
            const isEmpty = tag === 'P' && (lastKeep.textContent || '').trim().length === 0;
            if (isHeading || isShortCentered || isEmpty) {
              overflowIdx--;
            } else {
              break;
            }
          }

          const toMove = children.slice(overflowIdx);

          // If there's an existing next page in the same section, prepend there
          const existingNext = pages[i + 1];
          const sameSection = existingNext &&
            page.closest('section.document-section') === existingNext.closest('section.document-section');

          if (sameSection) {
            const firstContentChild = this._getPreviewContentChildren(existingNext)[0];
            toMove.forEach(el => {
              if (firstContentChild) {
                existingNext.insertBefore(el, firstContentChild);
              } else {
                const nextFooter = existingNext.querySelector('.page-footer-content');
                if (nextFooter) {
                  existingNext.insertBefore(el, nextFooter);
                } else {
                  existingNext.appendChild(el);
                }
              }
            });
          } else {
            const newPage = page.cloneNode(false);
            Array.from(page.attributes).forEach(attr => newPage.setAttribute(attr.name, attr.value));

            const header = page.querySelector('.page-header-content');
            const footer = page.querySelector('.page-footer-content');
            if (header) newPage.appendChild(header.cloneNode(true));
            if (footer) newPage.appendChild(footer.cloneNode(true));

            toMove.forEach(el => {
              const footerEl = newPage.querySelector('.page-footer-content');
              if (footerEl) {
                newPage.insertBefore(el, footerEl);
              } else {
                newPage.appendChild(el);
              }
            });

            page.parentNode.insertBefore(newPage, page.nextSibling);
          }

          didSplit = true;
          break;
        }

        if (!didSplit) break;
      }

      // Remove pages with no content (only header/footer remain)
      const finalPages = shadowRoot.querySelectorAll('main.page');
      finalPages.forEach(page => {
        if (this._getPreviewContentChildren(page).length === 0) {
          page.remove();
        }
      });

      // Ensure page padding-top clears the header so content isn't hidden behind it
      shadowRoot.querySelectorAll('main.page').forEach(page => {
        const header = page.querySelector('.page-header-content');
        if (!header) return;
        const headerHeight = header.getBoundingClientRect().height;
        const currentPadding = parseFloat(window.getComputedStyle(page).paddingTop) || 0;
        if (currentPadding < headerHeight) {
          page.style.paddingTop = headerHeight + 'px';
        }
      });
    },

    onFieldClick(e, field) {
      e.stopPropagation();

      this.clearFieldEditorDummy();

      const targetElement = e.target || null;
      const targetId = targetElement ? targetElement.id : null;
      const targetRect = targetElement
        ? targetElement.getBoundingClientRect()
        : null;

      this.fieldEditor.value = null;
      this.fieldEditor.field = field.value;
      this.fieldEditor.sources = [];
      this.fieldEditor.settings = null;
      this.fieldEditor.data = null;
      this.fieldEditor.label = null;

      const settings = this.getFieldSettings(field.value);
      this.fieldEditor = Object.assign({}, this.fieldEditor, settings);

      this.fieldEditorSourceChanged();
      this.$nextTick(() => this.syncVisualFromExpression());

      this.fieldEditor.tooltipTarget = targetId;
      this.fieldEditorRect = targetRect;

      if (targetId) {
        this.ensureFieldEditorTooltipAnchor(targetId, targetRect);
      }
    },

    closeFieldEditor() {
      this.fieldEditor.tooltipTarget = null;
      this.fieldEditorRect = null;
      this.clearFieldEditorDummy();
    },

    ensureFieldEditorTooltipAnchor(targetId, rect) {
      const existingTarget = document.getElementById(targetId);

      if (existingTarget && existingTarget !== this.fieldEditorDummyElement) {
        this.clearFieldEditorDummy();
        return;
      }

      if (!rect) {
        return;
      }

      let dummy = this.fieldEditorDummyElement;

      if (!dummy) {
        dummy = document.createElement("div");
        dummy.setAttribute("data-field-editor-dummy", "true");
        dummy.style.pointerEvents = "none";
        dummy.style.background = "transparent";
        dummy.style.position = "fixed";
        dummy.style.margin = "0";
        dummy.style.padding = "0";
        dummy.style.border = "0";
        dummy.style.zIndex = "1";
        this.fieldEditorDummyElement = dummy;
      }

      if (!dummy.isConnected) {
        document.body.appendChild(dummy);
      }

      dummy.id = targetId;
      dummy.style.top = `${rect.top}px`;
      dummy.style.left = `${rect.left}px`;
      dummy.style.width = `${Math.max(rect.width || 0, 1)}px`;
      dummy.style.height = `${Math.max(rect.height || 0, 1)}px`;
    },

    clearFieldEditorDummy() {
      if (
        this.fieldEditorDummyElement &&
        this.fieldEditorDummyElement.parentNode
      ) {
        this.fieldEditorDummyElement.parentNode.removeChild(
          this.fieldEditorDummyElement
        );
      }

      this.fieldEditorDummyElement = null;
    },

    getMappedField(field) {
      const f = field.value;

      const settings = this.getFieldSettings(f);

      return settings;
    },

    getFieldSettings(field) {
      // reset everything before we do another calculations
      let settings = {};

      let v = [
        {
          text: "Text",
          value: "text",
        },
      ];

      this.sources.forEach((src) => {
        v.push({
          text: src.tag,
          value: src.tag,
        });
      });

      // check does field have additional definitions
      const definitions = this.getFieldDefinitions(field);
      const sources = definitions ? definitions.sources || [] : [];
      sources.forEach((def) => {
        v.push({
          text: def.text,
          value: def.value,
        });

        settings.settings = Object.assign({}, settings.settings, {
          definitionSettings: def.settings,
        });
      });

      settings.source = definitions
        ? definitions.defaultSource || "text"
        : "text";
      settings.sources = v;

      const savedField = this.getSavedField(field);
      if (savedField) {
        settings = Object.assign({}, settings, savedField);
      }

      return settings;
    },

    getIndependentFieldValue(field) {
      return this.data[field.value];
    },

    getFieldDefinitions(field) {
      return this.fieldDefinitions[field] || {};
    },

    getSavedField(field) {
      const a = this.fields.find((f) => f.field == field);

      return _.cloneDeep(a);
    },

    isFieldPopulated(field) {
      const v = _.get(this.data, field, null);
      return !!v && v.length > 0;
    },
    getFieldValueOptions({ value }) {
      const { options } = this.fieldValueOptions[value] || {};

      return options;
    },
    getFieldValue(field) {
      return _.get(this.data, field, null);
    },

    addSource() {
      if (
        !this.rootSource ||
        !this.rootSourceValue ||
        !this.rootSourceValueLabel ||
        !this.rootSourceTag
      ) {
        this.$bvToast.toast(this.$t("someFieldsAreNotValid"), {
          title: this.$t("error"),
          variant: "danger",
        });

        return;
      }

      const existingWithThisTag = this.sources.find(
        (src) => src.tag == this.rootSourceTag
      );

      if (existingWithThisTag) {
        this.$bvToast.toast("Source nickname already taken", {
          title: this.$t("error"),
          variant: "danger",
        });

        return;
      }

      let s = new Set(this.sources);
      s.add({
        type: this.rootSource,
        value: this.rootSourceValue,
        label: this.rootSourceValueLabel,
        tag: this.rootSourceTag,
      });

      this.sources = Array.from(s);

      this.addSourceSidebar = false;

      this.saveDocument();
    },
    async removeSource(idx) {
      const confirm = await this.$mainDialog.msgBoxConfirm(
        `${this.$t("areYouSure")}?`
      );
      if (confirm) this.sources.splice(idx, 1);
      this.saveDocument();
    },

    async rootSourceValueSelected(rootSource, rootSourceValue) {
      switch (rootSource) {
        case "component":
          const { settings } = await this.$store.dispatch(
            "fetchComponentsSettings",
            rootSourceValue.value
          );
          this.rootSourceComponentFields = settings
            ? settings.fields.map((f) => {
                return {
                  text: f.name,
                  value: f.key,
                };
              })
            : [];

          this.rootSourceComponentFields.push({
            text: "Protokol ID",
            value: "uuid",
          });

          this.rootSourceTag = rootSourceValue.text;

          break;
      }
    },

    sourceDataSelected(source, value) {
      const sourceData = this.sourceData.find(
        (d) => d.source == source.type && d.tag == source.tag
      );
      if (!sourceData) {
        this.sourceData.push({
          source: source.type,
          value: source.value,
          tag: source.tag,
          data: value,
        });
      } else {
        this.sourceData = this.sourceData.map((d) => {
          if (d.source == source.type && d.tag == source.tag) {
            d.data = value;
            return d;
          }

          return d;
        });
      }

      this.fieldsSetup();
      this.$nextTick(() => this.reflowPreviewPages());
    },

    async fetchSourceData(source, query, page) {
      console.log("Fetching source data:", source);
      switch (source.type) {
        case "component":
          return await this.$store.dispatch("fetchComponentsModels", {
            context: {
              filter: query,
              offset: page,
            },
            uuid: source.value,
          });
      }
    },

    sourceDataValue(source) {
      const sourceModel = this.sourceData.find(
        (d) => d.source == source.type && d.tag == source.tag
      );
      return sourceModel ? sourceModel.data : undefined;
    },

    removeSavedField() {
      this.fields = this.fields.filter(
        (f) => f.field != this.fieldEditor.field
      );
      _.unset(this.data, this.fieldEditor.field);

      this.fieldsSetup();
      //   this.save();
    },

    fieldsSetup() {
      this.fieldValueOptions = {};

      const d = Object.assign({}, this.data);

      this.fields.forEach((field) => {
        if (field.source == "text") {
          const { value } = field;
          _.set(d, field.field, value);
        } else {
          const sourceEntry =
            this.sourceData.find(
              (s) => s.tag == field.source || s.source == field.source
            ) || {};
          const { data } = sourceEntry;

          const { sourceField } = field.data || {};
          const toField = field.field;
          let value = _.get(data, sourceField, null);
          const expr = (field && field.format) || "";
          const isJexl = expr && !/\{\{/.test(expr);

          if (isJexl && expr) {
            let result = evaluateExpressionSync(expr, { value, data });

            if (Array.isArray(value) && value.length > 1) {
              this.fieldValueOptions[field.field] = {
                options: value.map((item) => {
                  const r = evaluateExpressionSync(expr, { value: item, data });
                  return r != null ? String(r) : "";
                }),
              };
            }

            if (result != null && typeof result === "object") {
              result = JSON.stringify(result);
            }
            _.set(d, toField, result != null ? result : value);
          } else if (typeof value != "object") {
            _.set(d, toField, value);
          } else {
            const template = _.template(expr, {
              interpolate: /{{.([\s\S]+?)}}/g,
            });

            let a = {};

            if (Array.isArray(value)) {
              if (value.length > 0) {
                a = value[0];

                if (value.length > 1) {
                  this.fieldValueOptions[field.field] = {
                    options: value.map((v) => {
                      return template(v);
                    }),
                  };
                }
              }
            }

            let result = null;

            try {
              result = template(a);
            } catch (err) {
              console.log(err);
            }

            _.set(d, toField, result);
          }
        }
      });

      this.data = d;
    },

    editGroup(prefix) {
      this.groupEditor.show = true;
      this.groupEditor.value = prefix;
      const groupLabel = this.groupLabels.find(
        (g) => g.value == this.groupEditor.value
      );
      this.groupEditor.label = groupLabel ? groupLabel.text : null;
    },

    saveGroup() {
      const groupLabel = this.groupLabels.find(
        (g) => g.value == this.groupEditor.value
      );
      if (!groupLabel) {
        this.groupLabels.push({
          value: this.groupEditor.value,
          text: this.groupEditor.label,
        });
      } else {
        this.groupLabels = this.groupLabels.map((g) => {
          if (g.value == this.groupEditor.value) {
            g.text = this.groupEditor.label;
          }

          return g;
        });
      }

      this.groupEditor.show = false;
      //   this.save();
    },

    saveField() {
      let fieldEditor = _.cloneDeep(this.fieldEditor);
      delete fieldEditor["tooltipTarget"];
      delete fieldEditor["settings"];
      delete fieldEditor["sources"];

      let field = this.fields.find((f) => f.field == fieldEditor.field);
      if (field) {
        this.fields = this.fields.map((f) => {
          if (f.field == fieldEditor.field) {
            return fieldEditor;
          }

          return f;
        });
      } else {
        this.fields.push(fieldEditor);
      }

      this.closeFieldEditor();

      this.fieldsSetup();
      //   this.save();
      this.setupDocumentPreview();
      this.saveDocument();
    },

    // async save() {
    //   this.template = Object.assign({}, this.template, {});
    // },

    debouncedSave: _.debounce(async function () {
      this.autosavingTemplate = true;
      await Promise.all([
        this.saveDocument(),
        new Promise((res) => setTimeout(res, 2000)),
      ]);
      this.autosavingTemplate = false;
    }, 2000),

    async saveDocument() {
      try {
        this.savingTemplate = true;

        const docName = this.$route.query.document.replace(/^\//, "");
        const { version } = await this.$store.dispatch(
          "documents/updateDocument",
          {
            path: docName,
            variant: this.documentVariant,
            version: this.version,
            content: this.content,
            sources: this.sources,
            fields: this.fields,
          }
        );

        this.version = version;
        this.hasUnsavedChanges = false;
        this.lastSavedContent = this.content;
      } catch (error) {
        this.$mainDialog.msgBoxOk(error.message, { noStacking: true });

        if (error.response !== undefined) {
          if (error.response.status == 500) {
            this.$serverErrorModal(error);
            return;
          }

          return;
        }
      } finally {
        this.savingTemplate = false;
      }
    },
    newVariantSubmit() {
      if (this.newVariantName.trim() !== "") {
        this.documentVariant = this.newVariantName.trim();
        this.showNewVariantModal = false;
        nextTick(this.saveDocument);
      } else {
        this.$mainDialog.msgBoxOk(this.$t("fieldsNotValid"));
      }
    },
  },

  mounted() {
    let libUUID = this.$route.query.lib;
    this.$store.commit("documents/SET_LIBRARY_UUID", libUUID);

    this.loadDocList();

    let docName = this.$route.query.document.replace(/^\//, "");
    this.$store
      .dispatch("documents/fetchDocument", {
        path: docName,
      })
      .then(() => {
        this.loadingDocument = false;
      })
      .catch((error) => {
        this.loadingDocument = false;
        this.$mainDialog.msgBoxOk(error.message, { noStacking: true });

        if (error.response !== undefined) {
          if (error.response.status == 500) {
            this.$serverErrorModal(error);
            return;
          }

          return;
        }
      });

    document.addEventListener("click", this.hideContextMenu);
    this._beforeUnloadHandler = (e) => {
      if (this.hasUnsavedChanges && !this.autoSave) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', this._beforeUnloadHandler);

    this.$store.dispatch("fetchComponents").then((cs) => {
      this.componentsList = cs.map((c) => {
        return {
          text: this.$translateLabel(c.label, c.name),
          value: c.getRef(),
        };
      });
    });

    const {
      CustomTableCell,
      CustomTableHeader,
      CustomTable,
      HardBreakEnter,
      FontSize,
      ImageResize,
      MainTagExtension,
      SectionNode,
      CustomDocument,
      Comments,
      Spinner,
      PageHeaderNode,
      PageFooterNode,
      PageNumberNode,
      TextSpacing,
      ReservedPlaceholderHighlight,
    } = loadExtensions();

    this.editor = new Editor({
      extensions: [
        StarterKit.configure({
          history: true,
          document: true,
          code: true,
          codeBlock: true,
          dropcursor: true,
          gapcursor: true,
          italic: true,
          listItem: true,
          orderedList: true,
          paragraph: true,
          strike: true,
          text: true,
          hardBreak: true,
        }),
        TextAlign.configure({
          types: ["heading", "paragraph"],
        }),
        Highlight,
        Color,
        TextStyle,
        Image.configure({
          allowBase64: true,
          inline: true,
        }),
        Table.configure({
          resizable: true,
        }),
        TableRow,
        CustomTableHeader,
        CustomTableCell,
        FontFamily.configure({
          types: ["textStyle"],
        }),
        FontSize,
        ImageResize,
        HardBreakEnter,
        MainTagExtension,
        SectionNode,
        CustomTable,
        CustomDocument,
        Comments,
        Spinner,
        PageHeaderNode,
        PageFooterNode,
        PageNumberNode,
        TextSpacing,
        ReservedPlaceholderHighlight,
      ],
      content: this.content,

      onUpdate: ({ editor }) => {
        if (this._formattingLock) return;
        this.content = editor.getHTML();
        this.setupDocumentPreview();
        if (!this.formattingDocument) {
          this.debouncedLightPageBreak();
          this.debouncedAutoPageBreak();
        }
        const pages = editor.view.dom.querySelectorAll('main.page');
        if (pages.length !== this.totalPages) {
          this.totalPages = pages.length;
          this.$nextTick(() => this.setupPageObserver());
        }
      },
    });

    this.$nextTick(() => this.setupPageObserver());

    this.editor.commands.setColor(this.defaultColor);
    this.textColor = this.defaultColor;
    this.fontFamily = this.getFontFamily();

    this.editor.on("selectionUpdate", ({ editor }) => {
      this.textColor = this.getColor();
      this.fontFamily = this.getFontFamily();

      var sel = editor.state.selection;
      if (sel.from !== sel.to) {
        this.savedFontSizeSelection = { from: sel.from, to: sel.to };
      }

      this.currentSelection = editor.chain().focus();

      //const selection = editor.state.selection;
      // const mainNode = editor.state.doc.nodeAt(0);

      // if (!mainNode) return;

      // const isOutsideMain =
      //     selection.from <= mainNode.content.findDiffStart(mainNode.content) ||
      //     selection.to > mainNode.content.size;

      // if (isOutsideMain) {
      //     editor.commands.setTextSelection(mainNode.content.findDiffStart(mainNode.content) || 0);
      // }
    });

    nextTick(() => {
      this.setupDocumentPreview();
      // Probe + cache the editor's base typography (unitless line-height) and
      // inject the canonical document stylesheet into the editor so edit mode
      // renders content from the shared source. Both self-guard if the editor
      // isn't connected yet; loadDoc re-runs them once the document is loaded.
      this.getPrintableEditorStyles();
      this.applyEditorDocumentCSS();
    });

    try {
      this.editor.commands.calculatePages();
    } catch {
      console.warn("failed to calculate pages")
    }

    // ── Kortex tool registrations for the Document Editor ──────────────
    window.kortex.registerContext(
      'Document Editor',
      'The user is editing a rich-text document. ' +
      'The document is HTML-based and supports headings (H1–H3), paragraphs, bold, italic, strikethrough, highlights, text alignment, custom font family and size, foreground/background colours, inline images, and tables. ' +
      'Pages are represented as <main class="page"> elements. A single-page document has one <main class="page"> block; multi-page documents have multiple ones in sequence. ' +
      'The first <main> has no page-break style; every subsequent one gets page-break-before:always so they print as separate A4 pages. ' +
      'Documents are organized into sections, each starting on a new printed page. Use list_sections to see all sections, add_section to create one, focus_section to scroll to it, rename_section / delete_section / merge_section_with_previous to manage them, and set_page_margins to adjust margins. ' +
      'Dynamic placeholders / merge fields use golang html template syntax e.g. {{.field_name}} inside the HTML. ' +
      'A document can have multiple variants (e.g. language editions). Use get_document_info to list them, switch_variant to change the active variant, and create_variant to add a new language edition. ' +
      'Use get_document_content to read the full current HTML before making edits. Prefer replace_selected_text for targeted edits and set_document_content only when rewriting the whole document. ' +
      'Protokol documents use golang html template engine, so you can use all available template syntax. ' +
      'Tables support per-cell border styling on each side (top/right/bottom/left) with configurable width, style, and colour, plus cell background colours. ' +
      'Use insert_table to create tables, modify_table to add/remove rows and columns, set_cell_borders to style borders, and set_cell_background to colour cells. ' +
      'Use format_text for inline formatting (bold/italic/strikethrough/highlight), set_text_style for font family/size/colour, set_text_alignment for paragraph alignment, and set_heading for heading levels. ' +
      'Use insert_html_at_cursor to insert rich HTML content (not just plain text). Use save_document to persist changes. ' +
      'Documents have revision history. Use list_revisions to see past versions, preview_revision to load one read-only, get_revision_content to read its content, restore_revision to adopt it as the current content, and exit_revision to discard the preview. ' +
      'Documents support data sources and dynamic fields. Sources are external data providers (e.g. components) that supply structured data to fields. ' +
      'Fields are placeholders in the document template that map to either static text or a source field. ' +
      'Use list_document_sources / add_document_source / remove_document_source to manage sources. ' +
      'Use list_document_fields / update_field to manage field mappings. Use get_source_available_fields to discover what fields a source offers. ' +
      'Fields support JEXL format expressions to transform source values. The raw value is available as "value" and the full source record as "data". ' +
      'Examples: value|upper (uppercase string), value|map("name")|join(", ") (extract names from array and join), value.address.city (deep access), value|currency("$") (format number as currency), value|formatDate("DD.MM.YYYY") (format date). ' +
      'Use get_field_source_data to inspect the raw data shape before writing an expression. Use preview_field_format to test an expression without saving. Use list_format_transforms to see all available transforms. ' +
      'When a user struggles with formatting a field value, first inspect the raw data with get_field_source_data, then suggest and preview an appropriate JEXL expression, and finally apply it with update_field.',
      'fa-file-alt'
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'get_document_info',
          description: 'Returns information about the currently open document: its name, active variant name, current version, and the full list of available variant names (use this before calling switch_variant or create_variant).',
          parameters: { type: 'object', properties: {} }
        }
      },
      () => {
        var doc = this.storedDoc
        return {
          name: doc ? doc.name : null,
          variant: this.documentVariant,
          version: this.version,
          variants: doc ? doc.variants.map(function (v) { return v.name }) : [],
        }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'get_document_content',
          description: 'Returns the full content of the current document variant as both HTML and plain text.',
          parameters: { type: 'object', properties: {} }
        }
      },
      () => {
        return {
          html: this.editor.getHTML(),
          text: this.editor.getText(),
        }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'get_selected_text',
          description: 'Returns the text that is currently selected in the document editor. Returns an empty string if nothing is selected.',
          parameters: { type: 'object', properties: {} }
        }
      },
      () => {
        var selection = this.editor.state.selection
        var text = ''
        if (selection.from !== selection.to) {
          text = this.editor.state.doc.textBetween(selection.from, selection.to, ' ')
        }
        return { selected_text: text, has_selection: selection.from !== selection.to }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'insert_text_at_cursor',
          description: 'Inserts plain text at the current cursor position in the document editor.',
          parameters: {
            type: 'object',
            required: ['text'],
            properties: {
              text: { type: 'string', description: 'The text to insert at the cursor.' }
            }
          }
        }
      },
      (args) => {
        this.editor.chain().focus().insertContent(args.text || '').run()
        return { inserted: true }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'replace_selected_text',
          description: 'Replaces the currently selected text in the document editor with the provided content. If nothing is selected, the content is inserted at the cursor.',
          parameters: {
            type: 'object',
            required: ['content'],
            properties: {
              content: { type: 'string', description: 'The text to replace the selection with.' }
            }
          }
        }
      },
      (args) => {
        var selection = this.editor.state.selection
        if (selection.from !== selection.to) {
          this.editor.chain().focus().deleteSelection().insertContent(args.content || '').run()
        } else {
          this.editor.chain().focus().insertContent(args.content || '').run()
        }
        return { replaced: true }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'set_document_content',
          description: 'Replaces the entire content of the current document variant with the provided HTML. Use with care — this overwrites everything.',
          parameters: {
            type: 'object',
            required: ['html'],
            properties: {
              html: { type: 'string', description: 'The full HTML content to set as the document body.' }
            }
          }
        }
      },
      (args) => {
        this.editor.commands.setContent(args.html || '', false)
        this.content = this.editor.getHTML()
        return { set: true }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'switch_variant',
          description: 'Switches the document editor to a different variant (e.g. language edition). The variant must already exist. Use get_document_info first to see available variant names.',
          parameters: {
            type: 'object',
            required: ['variant_name'],
            properties: {
              variant_name: { type: 'string', description: 'The name of the variant to switch to (e.g. "en_US", "sr", "default").' }
            }
          }
        }
      },
      (args) => {
        var targetVariant = (args.variant_name || '').trim()
        if (!targetVariant) {
          return { error: 'variant_name is required' }
        }
        var variantNames = this.storedDoc ? this.storedDoc.variants.map(function (v) { return v.name }) : []
        if (variantNames.indexOf(targetVariant) === -1) {
          return { error: 'variant "' + targetVariant + '" does not exist. Available variants: ' + variantNames.join(', ') }
        }
        this.documentVariant = targetVariant
        return { switched_to: targetVariant, variants: variantNames }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'create_variant',
          description: 'Creates a new document variant (e.g. a new language edition). Optionally copies the content from the current variant as a starting point. The new variant is saved automatically.',
          parameters: {
            type: 'object',
            required: ['variant_name'],
            properties: {
              variant_name: { type: 'string', description: 'The name for the new variant (e.g. "en_US", "sr", "de_DE").' },
              copy_content: { type: 'boolean', description: 'If true, copy the current variant\'s HTML content as the starting point for the new variant. Defaults to false.' }
            }
          }
        }
      },
      (args) => {
        var newName = (args.variant_name || '').trim()
        if (!newName) {
          return { error: 'variant_name is required' }
        }
        var variantNames = this.storedDoc ? this.storedDoc.variants.map(function (v) { return v.name }) : []
        if (variantNames.indexOf(newName) !== -1) {
          return { error: 'variant "' + newName + '" already exists.' }
        }
        // Capture current HTML before switching (in case we want to copy)
        var currentHTML = args.copy_content ? this.editor.getHTML() : null
        // Switch to the new variant — the computed setter updates the route,
        // which triggers the watcher → loadDoc → empty variant state.
        this.documentVariant = newName
        var self = this
        this.$nextTick(function () {
          if (currentHTML) {
            self.editor.commands.setContent(currentHTML, false)
            self.content = self.editor.getHTML()
          }
          // Persist the variant to the backend (matches newVariantSubmit behaviour)
          self.saveDocument()
        })
        return { created: newName, copy_content: !!args.copy_content }
      }
    )

    // ── Table tools ───────────────────────────────────────────────────────

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'insert_table',
          description: 'Inserts a new table at the current cursor position with the specified number of rows and columns.',
          parameters: {
            type: 'object',
            properties: {
              rows: { type: 'number', description: 'Number of rows (default 3).' },
              cols: { type: 'number', description: 'Number of columns (default 3).' },
              with_header_row: { type: 'boolean', description: 'Whether the first row should be a header row (default true).' }
            }
          }
        }
      },
      (args) => {
        this.editor.chain().focus().insertTable({
          rows: args.rows || 3,
          cols: args.cols || 3,
          withHeaderRow: args.with_header_row !== false,
        }).run()
        return { inserted: true }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'modify_table',
          description: 'Modifies the table the cursor is currently inside. Supported actions: add_column_before, add_column_after, delete_column, add_row_before, add_row_after, delete_row, delete_table, merge_cells, split_cell, toggle_header_row, toggle_header_column.',
          parameters: {
            type: 'object',
            required: ['action'],
            properties: {
              action: { type: 'string', description: 'The table modification action to perform.' }
            }
          }
        }
      },
      (args) => {
        var actions = {
          add_column_before: () => this.editor.chain().focus().addColumnBefore().run(),
          add_column_after: () => this.editor.chain().focus().addColumnAfter().run(),
          delete_column: () => this.editor.chain().focus().deleteColumn().run(),
          add_row_before: () => this.editor.chain().focus().addRowBefore().run(),
          add_row_after: () => this.editor.chain().focus().addRowAfter().run(),
          delete_row: () => this.editor.chain().focus().deleteRow().run(),
          delete_table: () => this.editor.chain().focus().deleteTable().run(),
          merge_cells: () => this.editor.chain().focus().mergeCells().run(),
          split_cell: () => this.editor.chain().focus().splitCell().run(),
          toggle_header_row: () => this.editor.chain().focus().toggleHeaderRow().run(),
          toggle_header_column: () => this.editor.chain().focus().toggleHeaderColumn().run(),
        }
        var fn = actions[args.action]
        if (!fn) return { error: 'Unknown action "' + args.action + '". Available: ' + Object.keys(actions).join(', ') }
        fn()
        return { action: args.action, done: true }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'set_cell_borders',
          description: 'Sets border styling on the currently selected table cell(s). You can target individual sides or all sides at once. The cursor must be inside a table cell.',
          parameters: {
            type: 'object',
            properties: {
              side: { type: 'string', description: 'Which side to style: "all", "top", "right", "bottom", or "left". Defaults to "all".' },
              width: { type: 'number', description: 'Border width in pixels (e.g. 1, 2, 3). Defaults to 1.' },
              style: { type: 'string', description: 'Border style: "solid", "dashed", "dotted", "double", or "none". Defaults to "solid".' },
              color: { type: 'string', description: 'Border colour as hex (e.g. "#000000", "#ff0000"). Defaults to "#000000".' }
            }
          }
        }
      },
      (args) => {
        var side = args.side || 'all'
        var width = args.width != null ? args.width : 1
        var style = args.style || 'solid'
        var color = args.color || '#000000'
        var val = style === 'none' ? 'none' : (width + 'px ' + style + ' ' + color)
        var attrs = {}
        if (side === 'all') {
          attrs = { borderTop: val, borderRight: val, borderBottom: val, borderLeft: val }
        } else {
          var key = 'border' + side.charAt(0).toUpperCase() + side.slice(1)
          attrs[key] = val
        }
        this.setCellBorderAttrs(attrs)
        return { side: side, value: val, done: true }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'set_cell_background',
          description: 'Sets the background colour of the currently selected table cell(s). Pass null or empty string to clear the background.',
          parameters: {
            type: 'object',
            properties: {
              color: { type: 'string', description: 'Background colour as hex (e.g. "#f0f0f0", "#ffcccc"), or null to clear.' }
            }
          }
        }
      },
      (args) => {
        this.setCellBorderAttrs({ backgroundColor: args.color || null })
        return { color: args.color || null, done: true }
      }
    )

    // ── Text formatting tools ─────────────────────────────────────────────

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'format_text',
          description: 'Toggles inline text formatting on the current selection. If nothing is selected, the format is applied to the next text typed.',
          parameters: {
            type: 'object',
            required: ['format'],
            properties: {
              format: { type: 'string', description: 'The format to toggle: "bold", "italic", "strike", or "highlight".' }
            }
          }
        }
      },
      (args) => {
        var formats = {
          bold: () => this.editor.chain().focus().toggleBold().run(),
          italic: () => this.editor.chain().focus().toggleItalic().run(),
          strike: () => this.editor.chain().focus().toggleStrike().run(),
          highlight: () => this.editor.chain().focus().toggleHighlight().run(),
        }
        var fn = formats[args.format]
        if (!fn) return { error: 'Unknown format "' + args.format + '". Available: ' + Object.keys(formats).join(', ') }
        fn()
        return { format: args.format, done: true }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'set_text_style',
          description: 'Sets text style properties (colour, font family, font size) on the current selection or for the next text typed. Pass only the properties you want to change.',
          parameters: {
            type: 'object',
            properties: {
              color: { type: 'string', description: 'Text colour as hex (e.g. "#ff0000"). Pass null to reset.' },
              font_family: { type: 'string', description: 'Font family name (e.g. "Arial", "Georgia", "Courier New", "Inter").' },
              font_size: { type: 'number', description: 'Font size in pixels (6–100).' }
            }
          }
        }
      },
      (args) => {
        var chain = this.editor.chain().focus()
        var applied = []
        if (args.color !== undefined) {
          if (args.color) {
            chain = chain.setColor(args.color)
          } else {
            chain = chain.unsetColor()
          }
          applied.push('color')
        }
        if (args.font_family) {
          chain = chain.setFontFamily(args.font_family)
          applied.push('font_family')
        }
        if (args.font_size != null) {
          var size = Math.max(6, Math.min(100, args.font_size))
          chain = chain.setFontSize(size + 'px')
          applied.push('font_size')
        }
        chain.run()
        return { applied: applied, done: true }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'set_text_alignment',
          description: 'Sets the text alignment of the current paragraph or heading.',
          parameters: {
            type: 'object',
            required: ['alignment'],
            properties: {
              alignment: { type: 'string', description: 'Alignment: "left", "center", "right", or "justify".' }
            }
          }
        }
      },
      (args) => {
        var valid = ['left', 'center', 'right', 'justify']
        if (valid.indexOf(args.alignment) === -1) {
          return { error: 'Invalid alignment "' + args.alignment + '". Must be one of: ' + valid.join(', ') }
        }
        this.editor.chain().focus().setTextAlign(args.alignment).run()
        return { alignment: args.alignment, done: true }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'set_heading',
          description: 'Toggles a heading level on the current paragraph, or converts it back to a paragraph. Level 0 converts to a normal paragraph.',
          parameters: {
            type: 'object',
            required: ['level'],
            properties: {
              level: { type: 'number', description: 'Heading level: 1, 2, or 3. Use 0 to convert back to a paragraph.' }
            }
          }
        }
      },
      (args) => {
        if (args.level === 0) {
          this.editor.chain().focus().setParagraph().run()
        } else if ([1, 2, 3].indexOf(args.level) !== -1) {
          this.editor.chain().focus().toggleHeading({ level: args.level }).run()
        } else {
          return { error: 'Invalid heading level. Use 0 (paragraph), 1, 2, or 3.' }
        }
        return { level: args.level, done: true }
      }
    )

    // ── Content & structure tools ─────────────────────────────────────────

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'insert_html_at_cursor',
          description: 'Inserts rich HTML content at the current cursor position. Unlike insert_text_at_cursor, this preserves HTML formatting (bold, tables, images, etc.).',
          parameters: {
            type: 'object',
            required: ['html'],
            properties: {
              html: { type: 'string', description: 'The HTML fragment to insert.' }
            }
          }
        }
      },
      (args) => {
        this.editor.chain().focus().insertContent(args.html || '').run()
        return { inserted: true }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'insert_image',
          description: 'Inserts an image at the current cursor position. Accepts a URL or a base64 data URI.',
          parameters: {
            type: 'object',
            required: ['src'],
            properties: {
              src: { type: 'string', description: 'Image URL or base64 data URI (e.g. "data:image/png;base64,...").' },
              alt: { type: 'string', description: 'Alt text for the image.' },
              title: { type: 'string', description: 'Title/tooltip for the image.' }
            }
          }
        }
      },
      (args) => {
        if (!args.src) return { error: 'src is required' }
        this.editor.chain().focus().setImage({
          src: args.src,
          alt: args.alt || '',
          title: args.title || '',
        }).run()
        return { inserted: true }
      }
    )

    // ── Section tools ───────────────────────────────────────────────────

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'list_sections',
          description: 'Lists all document sections with their names, page counts, and indices. Use the index to target a specific section in other section tools.',
          parameters: { type: 'object', properties: {} }
        }
      },
      () => {
        var sections = this.editorSections
        var currentIdx = this.getCurrentSectionIndex()
        return {
          count: sections.length,
          current_index: currentIdx,
          sections: sections.map(function (s, i) {
            return { index: i, name: s.name, page_count: s.pageCount }
          }),
        }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'add_section',
          description: 'Adds a new document section at the end. Each section starts on a new printed page.',
          parameters: {
            type: 'object',
            properties: {
              name: { type: 'string', description: 'Name for the new section. Defaults to "Document N".' }
            }
          }
        }
      },
      (args) => {
        var name = (args.name || '').trim() || ('Document ' + (this.editorSections.length + 1))
        this.editor.commands.addSection(name)
        var self = this
        this.$nextTick(function () {
          self.updateSectionCount()
          self.runAutoPageBreak()
        })
        return { added: true, name: name, total_sections: this.editorSections.length }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'focus_section',
          description: 'Scrolls the editor to bring the specified section into view.',
          parameters: {
            type: 'object',
            required: ['index'],
            properties: {
              index: { type: 'number', description: 'Zero-based section index (from list_sections).' }
            }
          }
        }
      },
      (args) => {
        if (args.index == null) return { error: 'index is required' }
        var sections = this.editorSections
        if (args.index < 0 || args.index >= sections.length) {
          return { error: 'Section index ' + args.index + ' out of range. Document has ' + sections.length + ' section(s) (0–' + (sections.length - 1) + ').' }
        }
        this.scrollToSection(args.index)
        return { focused: true, index: args.index, name: sections[args.index].name }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'rename_section',
          description: 'Renames a document section.',
          parameters: {
            type: 'object',
            required: ['index', 'name'],
            properties: {
              index: { type: 'number', description: 'Zero-based section index.' },
              name: { type: 'string', description: 'New name for the section.' }
            }
          }
        }
      },
      (args) => {
        if (args.index == null) return { error: 'index is required' }
        var newName = (args.name || '').trim()
        if (!newName) return { error: 'name is required' }
        var sections = this.editorSections
        if (args.index < 0 || args.index >= sections.length) {
          return { error: 'Section index ' + args.index + ' out of range.' }
        }
        this.editor.commands.renameSection(args.index, newName)
        return { renamed: true, index: args.index, name: newName }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'delete_section',
          description: 'Deletes a document section by index. Cannot delete the last remaining section.',
          parameters: {
            type: 'object',
            required: ['index'],
            properties: {
              index: { type: 'number', description: 'Zero-based section index to delete.' }
            }
          }
        }
      },
      (args) => {
        if (args.index == null) return { error: 'index is required' }
        var sections = this.editorSections
        if (sections.length <= 1) return { error: 'Cannot delete the last section.' }
        if (args.index < 0 || args.index >= sections.length) {
          return { error: 'Section index ' + args.index + ' out of range.' }
        }
        var deletedName = sections[args.index].name
        this.editor.commands.deleteSection(args.index)
        var self = this
        this.$nextTick(function () {
          self.updateSectionCount()
          self.runAutoPageBreak()
        })
        return { deleted: true, name: deletedName, remaining_sections: sections.length - 1 }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'merge_section_with_previous',
          description: 'Merges a section with the one before it. The content of both sections is combined into the previous section. Cannot merge the first section (index 0).',
          parameters: {
            type: 'object',
            required: ['index'],
            properties: {
              index: { type: 'number', description: 'Zero-based section index to merge into the previous one. Must be >= 1.' }
            }
          }
        }
      },
      (args) => {
        if (args.index == null) return { error: 'index is required' }
        if (args.index < 1) return { error: 'Cannot merge section 0 — there is no previous section.' }
        var sections = this.editorSections
        if (args.index >= sections.length) {
          return { error: 'Section index ' + args.index + ' out of range.' }
        }
        var mergedName = sections[args.index].name
        var targetName = sections[args.index - 1].name
        this.editor.commands.mergeSection(args.index)
        var self = this
        this.$nextTick(function () {
          self.updateSectionCount()
          self.runAutoPageBreak()
        })
        return { merged: true, merged_section: mergedName, into_section: targetName }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'set_page_margins',
          description: 'Sets the page margins in pixels for the current section.',
          parameters: {
            type: 'object',
            properties: {
              top: { type: 'number', description: 'Top margin in px.' },
              right: { type: 'number', description: 'Right margin in px.' },
              bottom: { type: 'number', description: 'Bottom margin in px.' },
              left: { type: 'number', description: 'Left margin in px.' }
            }
          }
        }
      },
      (args) => {
        var current = this.margins
        this.margins = {
          top: args.top != null ? args.top : current.top,
          right: args.right != null ? args.right : current.right,
          bottom: args.bottom != null ? args.bottom : current.bottom,
          left: args.left != null ? args.left : current.left,
        }
        return { margins: this.margins, done: true }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'save_document',
          description: 'Saves the current document and all its variants to the server.',
          parameters: { type: 'object', properties: {} }
        }
      },
      () => {
        this.saveDocument()
        return { saving: true }
      }
    )

    // ── Revision tools ────────────────────────────────────────────────────

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'list_revisions',
          description: 'Lists all saved revisions of the current document, ordered from newest to oldest. Each revision has a version number, document version label, and timestamp.',
          parameters: { type: 'object', properties: {} }
        }
      },
      async () => {
        await this.loadRevisions()
        return {
          count: this.revisions.length,
          revisions: this.revisions.map(function (r) {
            return {
              version: r.version,
              document_version: r.documentVersion || null,
              updated_at: r.updatedBy ? r.updatedBy.time : null,
              updated_by: r.updatedBy ? r.updatedBy.name : null,
            }
          }),
        }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'preview_revision',
          description: 'Loads a specific revision into the editor as a read-only preview. The editor becomes non-editable while previewing. Use exit_revision to return to the current version. Call list_revisions first to get available version numbers.',
          parameters: {
            type: 'object',
            required: ['version'],
            properties: {
              version: { type: 'number', description: 'The revision version number to preview (from list_revisions).' }
            }
          }
        }
      },
      async (args) => {
        if (args.version == null) return { error: 'version is required' }
        if (this.revisions.length === 0) await this.loadRevisions()
        var rev = this.revisions.find(function (r) { return r.version === args.version })
        if (!rev) return { error: 'Revision version ' + args.version + ' not found. Use list_revisions to see available versions.' }
        await this.previewRevision(rev)
        return {
          previewing: true,
          version: rev.version,
          document_version: rev.documentVersion || null,
          updated_at: rev.updatedBy ? rev.updatedBy.time : null,
        }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'get_revision_content',
          description: 'Returns the HTML and plain-text content of the currently previewed revision. Must call preview_revision first.',
          parameters: { type: 'object', properties: {} }
        }
      },
      () => {
        if (!this.reviewingRevision) {
          return { error: 'No revision is being previewed. Call preview_revision first.' }
        }
        return {
          version: this.reviewingRevision.version,
          document_version: this.reviewingRevision.documentVersion || null,
          html: this.editor.getHTML(),
          text: this.editor.getText(),
        }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'restore_revision',
          description: 'Restores a previewed revision by copying its content into the current document and exiting preview mode. The editor becomes editable again with the revision content. You must save_document afterwards to persist the change.',
          parameters: { type: 'object', properties: {} }
        }
      },
      () => {
        if (!this.reviewingRevision) {
          return { error: 'No revision is being previewed. Call preview_revision first.' }
        }
        var restoredVersion = this.reviewingRevision.version
        this.restoreRevision()
        return { restored: true, from_version: restoredVersion, note: 'Call save_document to persist this change.' }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'exit_revision',
          description: 'Exits the revision preview and returns to the current document version. Any previewed content is discarded.',
          parameters: { type: 'object', properties: {} }
        }
      },
      () => {
        if (!this.reviewingRevision) {
          return { error: 'No revision is being previewed.' }
        }
        this.exitRevisionPreview()
        return { exited: true }
      }
    )

    // ── Kortex tools: Data Sources & Fields ──────────────────────────────

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'list_document_sources',
          description: 'Lists all data sources configured for this document. Each source has a type (e.g. "component"), a value (component UUID), a tag (unique alias used to reference the source in fields), and a label.',
          parameters: { type: 'object', properties: {} }
        }
      },
      () => {
        return {
          sources: this.sources.map((s) => ({
            type: s.type,
            value: s.value,
            tag: s.tag,
            label: s.label,
            has_data: this.sourceData.some((d) => d.tag == s.tag),
          })),
        }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'add_document_source',
          description: 'Adds a new data source to the document. Currently only "component" type is supported. The tag must be unique across all sources. After adding, the source data must be selected separately by the user from the source tab.',
          parameters: {
            type: 'object',
            required: ['type', 'component_uuid', 'tag'],
            properties: {
              type: { type: 'string', enum: ['component'], description: 'Source type. Currently only "component" is supported.' },
              component_uuid: { type: 'string', description: 'The UUID of the component to use as data source.' },
              tag: { type: 'string', description: 'Unique alias for this source. Used to reference the source when configuring fields.' },
              label: { type: 'string', description: 'Human-readable label for this source. Defaults to the tag value.' },
            }
          }
        }
      },
      (args) => {
        var tag = (args.tag || '').trim()
        if (!tag) return { error: 'tag is required' }
        if (!args.component_uuid) return { error: 'component_uuid is required' }

        var existing = this.sources.find((s) => s.tag == tag)
        if (existing) return { error: 'A source with tag "' + tag + '" already exists.' }

        this.sources.push({
          type: args.type || 'component',
          value: args.component_uuid,
          label: args.label || tag,
          tag: tag,
        })
        this.saveDocument()
        return { added: true, tag: tag }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'remove_document_source',
          description: 'Removes a data source from the document by its tag. Fields referencing this source will lose their mapping.',
          parameters: {
            type: 'object',
            required: ['tag'],
            properties: {
              tag: { type: 'string', description: 'The tag of the source to remove.' }
            }
          }
        }
      },
      (args) => {
        var tag = (args.tag || '').trim()
        var idx = this.sources.findIndex((s) => s.tag == tag)
        if (idx === -1) return { error: 'No source found with tag "' + tag + '".' }

        this.sources.splice(idx, 1)
        this.sourceData = this.sourceData.filter((d) => d.tag != tag)
        this.saveDocument()
        return { removed: true, tag: tag }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'list_document_fields',
          description: 'Lists all fields configured for this document, including their source mapping, format expression, and current resolved value.',
          parameters: { type: 'object', properties: {} }
        }
      },
      () => {
        return {
          fields: this.fields.map((f) => {
            var resolved = _.get(this.data, f.field, null)
            return {
              field: f.field,
              label: f.label || null,
              source: f.source,
              source_field: f.data ? f.data.sourceField : null,
              format: f.format || null,
              static_value: f.source == 'text' ? f.value : null,
              resolved_value: resolved != null ? (typeof resolved === 'object' ? JSON.stringify(resolved) : String(resolved)) : null,
            }
          }),
        }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'get_field_source_data',
          description: 'Returns the raw source data for a specific field, before any format expression is applied. Useful for understanding the data shape to write a format expression.',
          parameters: {
            type: 'object',
            required: ['field'],
            properties: {
              field: { type: 'string', description: 'The field path (e.g. "customer.name").' }
            }
          }
        }
      },
      (args) => {
        var saved = this.fields.find((f) => f.field == args.field)
        if (!saved) return { error: 'Field "' + args.field + '" not found.' }
        if (saved.source == 'text') return { type: 'text', value: saved.value }

        var sourceEntry = this.sourceData.find((d) => d.tag == saved.source || d.source == saved.source)
        if (!sourceEntry || !sourceEntry.data) return { error: 'No source data loaded for source "' + saved.source + '". The user needs to select a data record first.' }

        var sourceField = saved.data ? saved.data.sourceField : null
        var raw = sourceField ? _.get(sourceEntry.data, sourceField, null) : sourceEntry.data

        var valueType = raw === null ? 'null' : Array.isArray(raw) ? 'array' : typeof raw
        var sample = raw
        if (Array.isArray(raw) && raw.length > 3) {
          sample = raw.slice(0, 3)
        }

        return {
          field: args.field,
          source: saved.source,
          source_field: sourceField,
          value_type: valueType,
          sample: sample,
          total_items: Array.isArray(raw) ? raw.length : null,
        }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'update_field',
          description: 'Updates a field\'s configuration. Can change its source, source_field mapping, format expression, label, or static value. Only provided properties are updated; omitted ones remain unchanged. Call save_document after to persist.',
          parameters: {
            type: 'object',
            required: ['field'],
            properties: {
              field: { type: 'string', description: 'The field path to update (e.g. "customer.name").' },
              source: { type: 'string', description: 'New source: "text" for static value, or a source tag name.' },
              source_field: { type: 'string', description: 'The source field path to map (e.g. "email", "address.city"). Only used when source is a tag.' },
              format: { type: 'string', description: 'JEXL format expression to transform the source value. The raw value is available as "value" and the full source record as "data". Use pipe transforms like: value|map("name")|join(", ")' },
              label: { type: 'string', description: 'Human-readable nickname for this field.' },
              value: { type: 'string', description: 'Static text value. Only used when source is "text".' },
            }
          }
        }
      },
      (args) => {
        var existing = this.fields.find((f) => f.field == args.field)
        if (!existing) {
          existing = { field: args.field, source: 'text', format: '', value: null, label: null, data: null }
          this.fields.push(existing)
        }

        if (args.source !== undefined) existing.source = args.source
        if (args.label !== undefined) existing.label = args.label
        if (args.format !== undefined) existing.format = args.format
        if (args.value !== undefined) existing.value = args.value
        if (args.source_field !== undefined) {
          existing.data = Object.assign({}, existing.data || {}, { sourceField: args.source_field })
        }

        this.fieldsSetup()
        this.setupDocumentPreview()
        this.saveDocument()

        var resolved = _.get(this.data, args.field, null)
        return {
          updated: true,
          field: args.field,
          resolved_value: resolved != null ? (typeof resolved === 'object' ? JSON.stringify(resolved) : String(resolved)) : null,
        }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'preview_field_format',
          description: 'Tests a JEXL format expression against a field\'s current source data without saving. Returns the result so you can verify it before applying. The expression receives "value" (the raw field value) and "data" (the full source record).',
          parameters: {
            type: 'object',
            required: ['field', 'expression'],
            properties: {
              field: { type: 'string', description: 'The field path whose source data to test against.' },
              expression: { type: 'string', description: 'The JEXL expression to evaluate.' },
            }
          }
        }
      },
      (args) => {
        var saved = this.fields.find((f) => f.field == args.field)
        if (!saved) return { error: 'Field "' + args.field + '" not found.' }
        if (saved.source == 'text') return { error: 'Field uses a static text source. Format expressions only apply to data sources.' }

        var sourceEntry = this.sourceData.find((d) => d.tag == saved.source || d.source == saved.source)
        if (!sourceEntry || !sourceEntry.data) return { error: 'No source data loaded for source "' + saved.source + '".' }

        var sourceField = saved.data ? saved.data.sourceField : null
        var raw = sourceField ? _.get(sourceEntry.data, sourceField, null) : sourceEntry.data

        var result = evaluateExpressionSync(args.expression, { value: raw, data: sourceEntry.data })
        return {
          expression: args.expression,
          input_type: raw === null ? 'null' : Array.isArray(raw) ? 'array' : typeof raw,
          result: result,
          result_type: result === null ? 'null' : Array.isArray(result) ? 'array' : typeof result,
        }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'list_format_transforms',
          description: 'Lists all available JEXL transforms that can be used in field format expressions. Each transform shows its name, arguments, example usage, and description.',
          parameters: { type: 'object', properties: {} }
        }
      },
      () => {
        return { transforms: listTransforms() }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'get_source_available_fields',
          description: 'Returns the list of available fields for a given source tag. Use this to discover what fields can be mapped to document fields.',
          parameters: {
            type: 'object',
            required: ['source_tag'],
            properties: {
              source_tag: { type: 'string', description: 'The source tag to get available fields for.' }
            }
          }
        }
      },
      async (args) => {
        var source = this.sources.find((s) => s.tag == args.source_tag)
        if (!source) return { error: 'No source found with tag "' + args.source_tag + '".' }

        await this.fetchComponentSettings(source.value)
        var fields = this.getComponentSettingsFields(source.value, (fields) => {
          return fields.map((f) => ({ name: f.name, key: f.key, module: f.module, type: f.type }))
        })

        return { source_tag: args.source_tag, fields: fields }
      }
    )
  },
  beforeRouteLeave(to, from, next) {
    if (this.hasUnsavedChanges && !this.autoSave) {
      this.$mainDialog.msgBoxConfirm(
        'You have unsaved changes. Do you want to leave without saving?',
        { title: 'Unsaved Changes', okTitle: 'Leave', cancelTitle: 'Stay', noStacking: true }
      ).then((confirmed) => {
        next(!!confirmed);
      });
    } else {
      next();
    }
  },

  beforeDestroy() {
    window.removeEventListener('beforeunload', this._beforeUnloadHandler);
    const injectedDocCss = document.getElementById("protokol-editor-document-css");
    if (injectedDocCss) injectedDocCss.remove();
    this.destroyPageObserver();
    document.removeEventListener("click", this.hideContextMenu);
    this.clearFieldEditorDummy();
    window.kortex.unregisterContext()
    window.kortex.unregisterTool('get_document_info')
    window.kortex.unregisterTool('get_document_content')
    window.kortex.unregisterTool('get_selected_text')
    window.kortex.unregisterTool('insert_text_at_cursor')
    window.kortex.unregisterTool('replace_selected_text')
    window.kortex.unregisterTool('set_document_content')
    window.kortex.unregisterTool('switch_variant')
    window.kortex.unregisterTool('create_variant')
    window.kortex.unregisterTool('insert_table')
    window.kortex.unregisterTool('modify_table')
    window.kortex.unregisterTool('set_cell_borders')
    window.kortex.unregisterTool('set_cell_background')
    window.kortex.unregisterTool('format_text')
    window.kortex.unregisterTool('set_text_style')
    window.kortex.unregisterTool('set_text_alignment')
    window.kortex.unregisterTool('set_heading')
    window.kortex.unregisterTool('insert_html_at_cursor')
    window.kortex.unregisterTool('insert_image')
    window.kortex.unregisterTool('list_sections')
    window.kortex.unregisterTool('add_section')
    window.kortex.unregisterTool('focus_section')
    window.kortex.unregisterTool('rename_section')
    window.kortex.unregisterTool('delete_section')
    window.kortex.unregisterTool('merge_section_with_previous')
    window.kortex.unregisterTool('set_page_margins')
    window.kortex.unregisterTool('save_document')
    window.kortex.unregisterTool('list_revisions')
    window.kortex.unregisterTool('preview_revision')
    window.kortex.unregisterTool('get_revision_content')
    window.kortex.unregisterTool('restore_revision')
    window.kortex.unregisterTool('exit_revision')
    window.kortex.unregisterTool('list_document_sources')
    window.kortex.unregisterTool('add_document_source')
    window.kortex.unregisterTool('remove_document_source')
    window.kortex.unregisterTool('list_document_fields')
    window.kortex.unregisterTool('get_field_source_data')
    window.kortex.unregisterTool('update_field')
    window.kortex.unregisterTool('preview_field_format')
    window.kortex.unregisterTool('list_format_transforms')
    window.kortex.unregisterTool('get_source_available_fields')
    this.editor.destroy();
  },
};
</script>

<style lang="scss" scoped>
// ─── Design Tokens ─────────────────────────────────────────────────────────────
$bg-base:        #0d0f17;
$bg-surface:     #13161f;
$bg-elevated:    #1a1d29;
$bg-hover:       #1f2333;
$border-subtle:  #232840;
$border-default: #2d3250;
$accent:         #5b7cf6;
$accent-dim:     rgba(91, 124, 246, 0.15);
$accent-hover:   #7090f8;
$text-primary:   #e8eaf2;
$text-secondary: #7c87a8;
$text-muted:     #4a5270;
$danger:         #f06680;
$danger-dim:     rgba(240, 102, 128, 0.12);
$success:        #4ecb8d;
$success-dim:    rgba(78, 203, 141, 0.12);
$radius-sm:      5px;
$radius-md:      8px;
$radius-lg:      12px;
$toolbar-h:      50px;
$table-bar-h:    42px;
$sidebar-w:      268px;
$transition:     all 0.18s ease;

// ─── Container ─────────────────────────────────────────────────────────────────
.editor-container {
  position: relative;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 100px);
  background: $bg-base;
  border: 1px solid $border-subtle !important;
  border-radius: $radius-md !important;
  overflow: visible;
}

// ─── Toolbar ───────────────────────────────────────────────────────────────────
.editor-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: $toolbar-h;
  min-height: $toolbar-h;
  padding: 0 12px;
  background: $bg-surface;
  border-bottom: 1px solid $border-subtle;
  flex-shrink: 0;
  gap: 4px;
  overflow-x: auto;
  overflow-y: visible;
  position: relative;
  z-index: 100;

  &::-webkit-scrollbar { height: 0; }
}

.toolbar-left,
.toolbar-right {
  display: flex;
  align-items: center;
  gap: 3px;
  flex-shrink: 0;
}

.toolbar-right {
  margin-left: auto;
  padding-left: 12px;
}

// ─── Toolbar Atoms ─────────────────────────────────────────────────────────────
.tb-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: $radius-sm;
  background: transparent;
  color: $text-secondary;
  cursor: pointer;
  font-size: 13px;
  transition: $transition;
  position: relative;
  padding: 0;
  outline: none;
  flex-shrink: 0;

  &:hover {
    background: $bg-elevated;
    color: $text-primary;
  }

  &:disabled {
    cursor: wait;
    opacity: 0.7;
  }

  &.active {
    background: $accent-dim;
    color: $accent;
  }

  &.is-loading {
    background: $accent-dim;
    color: $accent;
  }

  &--text {
    font-size: 11px;
    font-weight: 700;
    width: 26px;
    letter-spacing: -0.3px;
  }

  &--color {
    &::after {
      content: '';
      position: absolute;
      bottom: 3px;
      left: 6px;
      right: 6px;
      height: 2px;
      background: var(--color-val, #000);
      border-radius: 1px;
    }
  }

  &--comment {
    width: auto;
    padding: 0 8px;
    gap: 5px;
  }

  &--save {
    width: auto;
    padding: 0 14px;
    gap: 6px;
    background: $accent;
    color: #fff;
    border-radius: $radius-sm;
    font-size: 12px;
    font-weight: 600;
    height: 30px;

    &:hover {
      background: $accent-hover;
      color: #fff;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &.has-unsaved {
      animation: pulse-save 2s ease-in-out infinite;
    }
  }
}

@keyframes pulse-save {
  0%, 100% { box-shadow: none; }
  50% { box-shadow: 0 0 0 3px rgba(91, 124, 246, 0.35); }
}

.tb-save-group {
  display: inline-flex;
  align-items: center;
  background: $accent;
  border-radius: $radius-sm;
  height: 30px;
  overflow: hidden;

  .tb-btn--save {
    border-radius: $radius-sm 0 0 $radius-sm;
  }
}

.tb-autosave-switch {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  height: 100%;
  cursor: pointer;
  margin: 0;
  border-left: 1px solid rgba(255, 255, 255, 0.2);

  input[type="checkbox"] {
    appearance: none;
    width: 24px;
    height: 14px;
    border-radius: 7px;
    background: rgba(255, 255, 255, 0.25);
    position: relative;
    cursor: pointer;
    transition: background 0.2s;
    margin: 0;

    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 2px;
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.6);
      transform: translateY(-50%);
      transition: transform 0.2s, background 0.2s;
    }

    &:checked {
      background: #34c759;

      &::after {
        transform: translateX(10px) translateY(-50%);
        background: #fff;
      }
    }
  }
}

.tb-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  background: $accent;
  color: #fff;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 700;
}

.tb-group {
  display: flex;
  align-items: center;
  gap: 1px;
  background: $bg-elevated;
  border-radius: $radius-sm;
  padding: 2px;

  .tb-btn {
    border-radius: 3px;
  }
}

.tb-divider {
  width: 1px;
  height: 20px;
  background: $border-subtle;
  margin: 0 4px;
  flex-shrink: 0;
}

.tb-doc-selector {
  position: relative;
  display: flex;
  align-items: center;
}

.tb-doc-name {
  width: auto !important;
  max-width: 200px;
  padding: 0 8px !important;
  gap: 6px;
  font-size: 12px;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .fa-caret-down { font-size: 10px; opacity: 0.5; }
}

.tb-doc-backdrop {
  position: fixed;
  inset: 0;
  z-index: 999;
}

.tb-doc-dropdown {
  position: fixed;
  width: 280px;
  max-height: 360px;
  background: $bg-elevated;
  border: 1px solid $border-default;
  border-radius: $radius-md;
  box-shadow: 0 8px 32px rgba(0,0,0,0.5);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.tb-doc-search {
  padding: 8px;
  border-bottom: 1px solid $border-subtle;

  input {
    width: 100%;
    background: $bg-surface;
    border: 1px solid $border-subtle;
    border-radius: $radius-sm;
    color: $text-primary;
    padding: 6px 10px;
    font-size: 12px;
    outline: none;

    &:focus { border-color: $accent; }
    &::placeholder { color: $text-muted; }
  }
}

.tb-doc-list {
  overflow-y: auto;
  max-height: 300px;
  padding: 4px;
}

.tb-doc-item {
  display: block;
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  background: transparent;
  border: none;
  border-radius: $radius-sm;
  color: $text-secondary;
  font-size: 12px;
  cursor: pointer;
  transition: $transition;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover { background: $bg-hover; color: $text-primary; }
  &.active { color: $accent; background: $accent-dim; }
}

.tb-doc-empty {
  padding: 16px;
  text-align: center;
  color: $text-muted;
  font-size: 12px;
}

.tb-mode-switch {
  display: flex;
  align-items: center;
  background: $bg-elevated;
  border-radius: $radius-sm;
  padding: 2px;
  gap: 1px;
}

.tb-mode-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 0 10px;
  height: 26px;
  border: none;
  border-radius: 3px;
  background: transparent;
  color: $text-secondary;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: $transition;
  outline: none;

  i { font-size: 11px; }

  &:hover { color: $text-primary; background: $bg-hover; }

  &.active {
    background: $accent;
    color: #fff;
  }
}

.tb-dropdown {
  flex-shrink: 0;

  ::v-deep .tb-dropdown-toggle {
    display: inline-flex !important;
    align-items: center;
    gap: 5px;
    padding: 0 10px !important;
    height: 30px;
    border: none !important;
    border-radius: $radius-sm !important;
    background: $bg-elevated !important;
    color: $text-secondary !important;
    font-size: 12px !important;
    font-weight: 500;
    transition: $transition;
    box-shadow: none !important;
    text-decoration: none !important;

    &:hover {
      background: $bg-hover !important;
      color: $text-primary !important;
    }

    i:first-child { font-size: 12px; }
  }

  ::v-deep .dropdown-menu {
    background: #252836;
    border: 1px solid #3a3f55;
    border-radius: $radius-md;
    padding: 6px;
    box-shadow: 0 8px 32px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.04) inset;
    min-width: 160px;
    z-index: 9999 !important;
  }

  ::v-deep .dropdown-item {
    color: #b0b8d0;
    font-size: 13px;
    border-radius: $radius-sm;
    padding: 6px 10px;
    transition: $transition;

    &:hover { background: #2e3348; color: $text-primary; }
    &.active, &:active { background: $accent-dim; color: $accent; }
  }

  ::v-deep .dropdown-header {
    display: none;
  }

  ::v-deep .dropdown-divider { border-color: #363b52; margin: 4px 0; }
}

.tb-size-input {
  display: flex;
  align-items: center;
  background: $bg-elevated;
  border: 1px solid $border-default;
  border-radius: $radius-sm;
  overflow: visible;
  height: 30px;
  padding: 0 6px;
  gap: 2px;

  input {
    width: 36px;
    background: $bg-elevated !important;
    border: none !important;
    box-shadow: none !important;
    color: $text-primary !important;
    font-size: 12px;
    font-weight: 500;
    outline: none;
    text-align: center;
    padding: 0 !important;
    height: 28px !important;
    border-radius: 0 !important;
    -moz-appearance: textfield;
    appearance: textfield;

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button { -webkit-appearance: none; }
  }

  .tb-size-unit {
    font-size: 10px;
    color: $text-muted;
  }
}

// ─── Margin custom input ─────────────────────────────────────────────────────
.tb-margin-custom {
  padding: 6px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  label {
    font-size: 12px;
    color: #b0b8d0;
    white-space: nowrap;
    margin: 0;
  }
}

.tb-margin-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
}

.tb-margin-field {
  display: flex;
  align-items: center;
  gap: 4px;

  span {
    font-size: 11px;
    color: #8890a8;
    width: 38px;
    text-transform: capitalize;
  }

  input {
    width: 52px;
    height: 26px;
    background: #1e2130;
    border: 1px solid #3a3f55;
    border-radius: $radius-sm;
    color: $text-primary;
    font-size: 12px;
    text-align: center;
    outline: none;
    -moz-appearance: textfield;
    appearance: textfield;

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button { -webkit-appearance: none; }
    &:focus { border-color: $accent; }
  }
}

.tb-hf-settings {
  padding: 6px 12px;

  label {
    font-size: 11px;
    color: #8890a8;
    margin: 0 0 4px;
    display: block;
  }
}

.tb-hf-settings-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tb-hf-field {
  display: flex;
  align-items: center;
  gap: 3px;

  span {
    font-size: 10px;
    color: #6b7280;
    width: 30px;
  }

  input[type="number"] {
    width: 42px;
    height: 24px;
    background: #1e2130;
    border: 1px solid #3a3f55;
    border-radius: $radius-sm;
    color: $text-primary;
    font-size: 11px;
    text-align: center;
    outline: none;
    -moz-appearance: textfield;
    appearance: textfield;

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button { -webkit-appearance: none; }
    &:focus { border-color: $accent; }
  }

  input[type="color"] {
    width: 26px;
    height: 24px;
    border: 1px solid #3a3f55;
    border-radius: $radius-sm;
    background: none;
    padding: 1px;
    cursor: pointer;

    &::-webkit-color-swatch-wrapper { padding: 0; }
    &::-webkit-color-swatch { border: none; border-radius: 3px; }
  }
}

.tb-hf-color-clear {
  width: 20px;
  height: 20px;
  border: none;
  border-radius: $radius-sm;
  background: transparent;
  color: $text-muted;
  cursor: pointer;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  &:hover { background: $danger-dim; color: $danger; }
}

.tb-pn-format-input {
  width: 100%;
  height: 28px;
  background: #1e2130;
  border: 1px solid #3a3f55;
  border-radius: $radius-sm;
  color: $text-primary;
  font-size: 11px;
  padding: 0 8px;
  outline: none;
  margin-top: 4px;
  font-family: monospace;

  &:focus { border-color: $accent; }
  &::placeholder { color: #555; }
}

.tb-margin-input {
  width: 60px;
  height: 28px;
  background: #1e2130;
  border: 1px solid #3a3f55;
  border-radius: $radius-sm;
  color: $text-primary;
  font-size: 12px;
  text-align: center;
  outline: none;
  -moz-appearance: textfield;
  appearance: textfield;

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button { -webkit-appearance: none; }
  &:focus { border-color: $accent; }
}


// ─── Bubble Menu ──────────────────────────────────────────────────────────────
.bubble-menu {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
  background: $bg-surface;
  border: 1px solid $border-default;
  border-radius: $radius-lg;
  box-shadow:
    0 4px 6px rgba(0, 0, 0, 0.15),
    0 12px 40px rgba(0, 0, 0, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.04);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
}

.bm-row {
  display: flex;
  align-items: center;
  gap: 4px;
}

.bm-group {
  display: flex;
  align-items: center;
  gap: 1px;
  background: $bg-elevated;
  border-radius: $radius-sm;
  padding: 2px;
}

.bm-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 3px;
  background: transparent;
  color: $text-secondary;
  cursor: pointer;
  font-size: 13px;
  transition: $transition;
  padding: 0;
  outline: none;
  flex-shrink: 0;

  &:hover { background: $bg-hover; color: $text-primary; }
  &.active { background: $accent-dim; color: $accent; }

  &--text {
    font-size: 11px;
    font-weight: 700;
    width: 26px;
    letter-spacing: -0.3px;
  }

  &--color {
    position: relative;
    &::after {
      content: '';
      position: absolute;
      bottom: 4px;
      left: 6px;
      right: 6px;
      height: 2px;
      background: var(--color-val, #000);
      border-radius: 1px;
    }
  }
}

.bm-divider {
  width: 1px;
  height: 20px;
  background: $border-subtle;
  margin: 0 2px;
  flex-shrink: 0;
}

.bm-font-select {
  flex-shrink: 0;

  select {
    height: 30px;
    padding: 0 8px;
    padding-right: 22px;
    background: $bg-elevated;
    border: 1px solid $border-default;
    border-radius: $radius-sm;
    color: $text-secondary;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    outline: none;
    transition: $transition;
    appearance: none;
    -webkit-appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' fill='none'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%237c87a8' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 6px center;

    &:hover { border-color: lighten($border-default, 8%); color: $text-primary; }
    &:focus { border-color: $accent; color: $text-primary; }
  }
}

.bm-size-input {
  display: flex;
  align-items: center;
  background: $bg-elevated;
  border: 1px solid $border-default;
  border-radius: $radius-sm;
  height: 30px;
  padding: 0 6px;
  gap: 2px;
  transition: $transition;

  &:focus-within { border-color: $accent; }

  input {
    width: 34px;
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    color: $text-primary !important;
    font-size: 12px;
    font-weight: 500;
    outline: none;
    text-align: center;
    padding: 0 !important;
    height: 28px !important;
    -moz-appearance: textfield;
    appearance: textfield;

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button { -webkit-appearance: none; }
  }

  .bm-size-unit {
    font-size: 10px;
    color: $text-muted;
    user-select: none;
  }
}

.bm-select {
  height: 24px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 4px;
  color: $text-primary;
  font-size: 10px;
  padding: 0 2px;
  outline: none;
  cursor: pointer;

  &:focus { border-color: $accent; }
}

.bm-padding-group {
  display: flex;
  align-items: center;
  gap: 2px;
}

.bm-padding-input {
  width: 30px;
  height: 22px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 3px;
  color: $text-primary;
  font-size: 10px;
  text-align: center;
  outline: none;
  -moz-appearance: textfield;
  appearance: textfield;

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button { -webkit-appearance: none; }
  &:focus { border-color: $accent; }

  &::placeholder { color: rgba(255,255,255,0.2); font-size: 9px; }
}

// ─── Field Editor Popover ─────────────────────────────────────────────────────
.fe-fade-enter-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.fe-fade-leave-active { transition: opacity 0.1s ease, transform 0.1s ease; }
.fe-fade-enter, .fe-fade-leave-to { opacity: 0; transform: translateY(-4px); }

.fe-popover {
  width: 320px;
  background: $bg-surface;
  border: 1px solid $border-default;
  border-radius: $radius-lg;
  box-shadow:
    0 4px 6px rgba(0, 0, 0, 0.1),
    0 16px 48px rgba(0, 0, 0, 0.35);
  z-index: 9999;
  overflow: hidden;

  &__arrow {
    position: absolute;
    top: -5px;
    left: 50%;
    transform: translateX(-50%) rotate(45deg);
    width: 10px;
    height: 10px;
    background: $bg-surface;
    border-top: 1px solid $border-default;
    border-left: 1px solid $border-default;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid $border-subtle;
    background: $bg-elevated;
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 600;
    color: $text-primary;

    i {
      font-size: 12px;
      color: $accent;
    }
  }

  &__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border: none;
    border-radius: $radius-sm;
    background: transparent;
    color: $text-muted;
    cursor: pointer;
    font-size: 11px;
    transition: $transition;
    outline: none;

    &:hover { background: $bg-hover; color: $text-primary; }
  }

  &__body {
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__footer {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    border-top: 1px solid $border-subtle;
    background: $bg-elevated;
  }
}

.fe-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.fe-label {
  font-size: 11px;
  font-weight: 600;
  color: $text-muted;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0;
}

.fe-input {
  width: 100%;
  height: 34px;
  padding: 0 10px;
  background: $bg-elevated;
  border: 1px solid $border-default;
  border-radius: $radius-sm;
  color: $text-primary;
  font-size: 13px;
  outline: none;
  transition: $transition;

  &::placeholder { color: $text-muted; }
  &:focus { border-color: $accent; }

  &.fe-textarea {
    height: auto;
    padding: 8px 10px;
    resize: vertical;
    font-family: inherit;
    line-height: 1.4;
  }
}


.fe-format-expr {
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace !important;
  font-size: 12px !important;
  line-height: 1.5 !important;
}

.fe-label-hint {
  color: $text-muted;
  margin-left: 4px;
  cursor: help;
  i { font-size: 11px; }
}

.fe-format-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  .fe-label { margin-bottom: 0; }
}

.fe-format-toggle {
  display: flex;
  gap: 0;
  border: 1px solid $border-default;
  border-radius: $radius-sm;
  overflow: hidden;
}

.fe-format-toggle-btn {
  padding: 2px 10px;
  font-size: 11px;
  font-weight: 500;
  border: none;
  background: transparent;
  color: $text-muted;
  cursor: pointer;
  transition: all 0.15s;
  &.active {
    background: $accent;
    color: #fff;
  }
  &:not(.active):hover {
    background: rgba($accent, 0.08);
  }
}

.fe-visual-format {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.fe-value-type-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  background: rgba($accent, 0.06);
  border: 1px solid rgba($accent, 0.15);
  border-radius: $radius-sm;
  font-size: 11px;
  font-weight: 500;
  color: $text-secondary;
  i { font-size: 10px; color: $accent; }
  span { color: $text-muted; font-weight: 400; }
}

.fe-visual-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.fe-visual-label {
  font-size: 11px;
  font-weight: 600;
  color: $text-secondary;
}

.fe-visual-select {
  font-size: 12px !important;
  height: 30px !important;
  padding: 0 8px !important;
}

.fe-visual-input {
  font-size: 12px !important;
  height: 30px !important;
  padding: 0 8px !important;
  width: 120px !important;
}

.fe-visual-options {
  display: flex;
  gap: 4px;
  &--wrap { flex-wrap: wrap; }
}

.fe-visual-option {
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 500;
  border: 1px solid $border-default;
  border-radius: $radius-sm;
  background: transparent;
  color: $text-secondary;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  i { margin-right: 3px; font-size: 10px; }
  &.active {
    background: $accent;
    border-color: $accent;
    color: #fff;
  }
  &:not(.active):hover {
    border-color: $accent;
    color: $accent;
  }
}

.fe-visual-empty {
  font-size: 11px;
  color: $text-muted;
  padding: 8px 0;
  i { margin-right: 4px; }
}

.fe-generated-expr {
  margin-top: 4px;
  code {
    font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
    font-size: 10px;
    color: $text-muted;
    background: rgba(0,0,0,0.04);
    padding: 2px 6px;
    border-radius: 3px;
  }
}

.fe-format-preview {
  margin-top: 6px;
  padding: 6px 8px;
  background: rgba($accent, 0.08);
  border-radius: $radius-sm;
  font-size: 11px;
  line-height: 1.4;
  word-break: break-all;

  .fe-format-preview-label {
    color: $text-muted;
    margin-right: 4px;
    font-weight: 600;
  }
  .fe-format-preview-value {
    color: $text-primary;
  }
}

.fe-datepicker {
  ::v-deep .form-control {
    background: $bg-elevated !important;
    border: 1px solid $border-default !important;
    border-radius: $radius-sm !important;
    color: $text-primary !important;
    font-size: 13px;
    height: 34px;
  }
}

.fe-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 32px;
  padding: 0 14px;
  border: none;
  border-radius: $radius-sm;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: $transition;
  outline: none;

  i { font-size: 11px; }

  &--save {
    background: $accent;
    color: #fff;
    flex: 1;

    &:hover { background: $accent-hover; }
  }

  &--remove {
    background: $danger-dim;
    color: $danger;

    &:hover { background: rgba($danger, 0.2); }
  }

  &--cancel {
    background: $bg-elevated;
    color: $text-secondary;
    border: 1px solid $border-default;

    &:hover { background: $bg-hover; color: $text-primary; }
  }
}

.fe-dialog-form {
  display: flex;
  flex-direction: column;
  gap: 14px;

  .fe-label {
    color: var(--text-secondary, #{$text-muted});
  }

  .fe-input {
    background: var(--form-bg, #{$bg-elevated});
    border-color: var(--form-border, #{$border-default});
    color: var(--text-primary, #{$text-primary});

    &::placeholder { color: var(--text-muted, #{$text-muted}); }
    &:focus { border-color: var(--border-active, #{$accent}); }
  }

  .fe-skeleton {
    background: var(--form-bg, #{$bg-elevated});
    border-color: var(--form-border, #{$border-default});
  }
}

.fe-skeleton {
  height: 34px;
  background: $bg-elevated;
  border: 1px solid $border-default;
  border-radius: $radius-sm;
  animation: fe-pulse 1.5s ease-in-out infinite;
}

@keyframes fe-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

// ─── Table Toolbar (secondary bar) ─────────────────────────────────────────────
.table-toolbar {
  display: flex;
  align-items: center;
  height: $table-bar-h;
  padding: 0 12px;
  background: rgba($bg-elevated, 0.7);
  border-bottom: 1px solid $border-subtle;
  gap: 4px;
  overflow-x: auto;
  flex-shrink: 0;

  &::-webkit-scrollbar { height: 0; }
}

.table-toolbar-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: $text-muted;
  white-space: nowrap;
  padding-right: 8px;
}

.table-toolbar-close {
  margin-left: auto;
  flex-shrink: 0;
}

.tb-tool-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0 10px;
  height: 26px;
  border: 1px solid $border-subtle;
  border-radius: $radius-sm;
  background: transparent;
  color: $text-secondary;
  font-size: 11px;
  cursor: pointer;
  white-space: nowrap;
  transition: $transition;
  outline: none;

  &:hover { background: $bg-elevated; color: $text-primary; border-color: $border-default; }

  &--danger {
    &:hover { background: $danger-dim; color: $danger; border-color: rgba($danger, 0.3); }
  }
}

.tb-border-controls {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.tb-border-width {
  width: 42px;
  height: 26px;
  padding: 0 4px;
  border: 1px solid $border-subtle;
  border-radius: $radius-sm;
  background: $bg-base;
  color: $text-primary;
  font-size: 11px;
  text-align: center;
  outline: none;
  -moz-appearance: textfield;
  appearance: textfield;

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button { -webkit-appearance: none; appearance: none; margin: 0; }
  &:focus { border-color: $accent; }
}

.tb-border-style {
  height: 26px;
  padding: 0 4px;
  border: 1px solid $border-subtle;
  border-radius: $radius-sm;
  background: $bg-base;
  color: $text-primary;
  font-size: 11px;
  outline: none;
  cursor: pointer;

  &:focus { border-color: $accent; }
}

.tb-border-color-wrap {
  position: relative;
  width: 26px;
  height: 26px;
  border: 1px solid $border-subtle;
  border-radius: $radius-sm;
  overflow: hidden;
  cursor: pointer;

  &:hover { border-color: $border-default; }
}

.tb-border-color-swatch {
  width: 100%;
  height: 100%;
  border-radius: inherit;
}

.tb-border-color-input {
  position: absolute;
  inset: 0;
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
  border: none;
  padding: 0;
}

.tb-border-sides {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

// ─── Slide transitions ─────────────────────────────────────────────────────────
.slide-down-enter-active,
.slide-down-leave-active { transition: all 0.2s ease; }
.slide-down-enter, .slide-down-leave-to { opacity: 0; transform: translateY(-8px); }

.slide-sidebar-left-enter-active,
.slide-sidebar-left-leave-active { transition: all 0.22s ease; }
.slide-sidebar-left-enter, .slide-sidebar-left-leave-to { opacity: 0; transform: translateX(-20px); }

.slide-sidebar-right-enter-active,
.slide-sidebar-right-leave-active { transition: all 0.22s ease; }
.slide-sidebar-right-enter, .slide-sidebar-right-leave-to { opacity: 0; transform: translateX(20px); }

// ─── Main Layout ───────────────────────────────────────────────────────────────
.editor-main-layout {
  display: flex;
  flex: 1;
  overflow: hidden;
}

// ─── Sidebars ──────────────────────────────────────────────────────────────────
.editor-sidebar {
  width: $sidebar-w;
  min-width: $sidebar-w;
  background: $bg-surface;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: $border-default; border-radius: 2px; }

  &-left { border-right: 1px solid $border-subtle; }
  &-right { border-left: 1px solid $border-subtle; }
}

.sidebar-section {
  border-bottom: 1px solid $border-subtle;

  &:last-child { border-bottom: none; }
}

.sidebar-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  height: 44px;
  cursor: pointer;
  user-select: none;
  transition: $transition;

  &:hover { background: $bg-elevated; }

  .sidebar-chevron {
    font-size: 10px;
    color: $text-muted;
    transition: transform 0.2s ease;
  }

  // When collapsed, chevron rotates
  &.not-collapsed .sidebar-chevron,
  &[aria-expanded="true"] .sidebar-chevron {
    transform: rotate(180deg);
  }
}

.sidebar-section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.3px;
  color: $text-secondary;

  i { font-size: 13px; color: $text-muted; }
}

.sidebar-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: $accent-dim;
  color: $accent;
  border-radius: 9px;
  font-size: 10px;
  font-weight: 700;
}

.sidebar-section-body {
  padding: 16px;
  background: $bg-base;

  &--flush { padding: 0; }
}

.sidebar-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 16px;
  color: $text-muted;
  gap: 8px;
  text-align: center;

  i { font-size: 24px; }
  p { font-size: 13px; margin: 0; }
}

// ─── Comment cards (sidebar) ───────────────────────────────────────────────────
.comment-card {
  padding: 12px 14px;
  border-bottom: 1px solid $border-subtle;
  transition: $transition;
  cursor: default;

  &:hover { background: $bg-elevated; }
  &:last-child { border-bottom: none; }

  &.is-deleting { opacity: 0.4; pointer-events: none; }
}

.comment-card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.comment-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, $accent, #8b5cf6);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &--sm {
    width: 22px;
    height: 22px;
    font-size: 10px;
    background: linear-gradient(135deg, #6d758f, #4a5270);
  }
}

.comment-card-meta {
  flex: 1;
  min-width: 0;
}

.comment-author-name {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: $text-primary;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.comment-timestamp {
  display: block;
  font-size: 10px;
  color: $text-muted;
  margin-top: 1px;
}

.comment-card-actions {
  display: flex;
  gap: 2px;
  opacity: 0;
  transition: $transition;

  .comment-card:hover & { opacity: 1; }
}

.comment-action-btn {
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: $text-muted;
  cursor: pointer;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: $transition;
  outline: none;

  &:hover { background: $bg-hover; color: $text-secondary; }
  &--danger:hover { background: $danger-dim; color: $danger; }
}

.comment-card-body { cursor: pointer; }

.comment-text {
  font-size: 13px;
  line-height: 1.5;
  color: $text-primary;
  margin-bottom: 5px;
}

.comment-quote {
  font-size: 11px;
  color: $text-muted;
  padding-left: 8px;
  border-left: 2px solid $border-default;
  font-style: italic;

  i { margin-right: 4px; }
}

.comment-replies {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid $border-subtle;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.comment-reply-card {
  background: $bg-surface;
  border-radius: $radius-sm;
  padding: 8px 10px;
  transition: $transition;

  &:hover { background: $bg-elevated; }
  &.is-deleting { opacity: 0.4; pointer-events: none; }

  .comment-card-header { margin-bottom: 4px; }
  .comment-text { font-size: 12px; margin-bottom: 0; }
}

// ─── Metadata ──────────────────────────────────────────────────────────────────
.metadata-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.meta-group { display: flex; flex-direction: column; }

.meta-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: $text-muted;
  margin-bottom: 8px;
}

.meta-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 4px 0;
  border-bottom: 1px solid $border-subtle;
  gap: 8px;

  &:last-child { border-bottom: none; }

  span:first-child {
    font-size: 12px;
    color: $text-muted;
    flex-shrink: 0;
  }

  span:last-child {
    font-size: 12px;
    color: $text-primary;
    font-weight: 500;
    text-align: right;
    word-break: break-word;
  }
}

// ─── Formatting Overlay ───────────────────────────────────────────────────────
.document-loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 300;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(13, 15, 23, 0.9);
  backdrop-filter: blur(4px);
}

.document-loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: $text-primary;
  font-size: 14px;
  font-weight: 500;
}

.page-indicator {
  position: absolute;
  bottom: 56px;
  right: 20px;
  z-index: 50;
  padding: 5px 12px;
  border-radius: 20px;
  background: rgba(30, 34, 48, 0.85);
  backdrop-filter: blur(6px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: $text-secondary;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.3px;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(30, 34, 48, 0.95);
    border-color: rgba(255, 255, 255, 0.15);
  }
}

.formatting-overlay {
  position: absolute;
  inset: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(13, 15, 23, 0.75);
  backdrop-filter: blur(2px);
}

.formatting-spinner {
  display: flex;
  align-items: center;
  gap: 10px;
  color: $text-primary;
  font-size: 14px;
  font-weight: 500;

  i {
    font-size: 18px;
    color: $accent;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

// ─── Editor Canvas ─────────────────────────────────────────────────────────────
.editor-content-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  background: #171a24;
  background-image:
    radial-gradient(ellipse at 50% 0%, rgba(91, 124, 246, 0.03) 0%, transparent 70%);
}

.editor-canvas-wrapper {
  flex: 1;
  overflow-y: auto;
  padding: 56px 48px 60px;
  display: flex;
  flex-direction: column;
  align-items: center;

  &::-webkit-scrollbar { width: 5px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: rgba(255,255,255,0.08);
    border-radius: 3px;
    &:hover { background: rgba(255,255,255,0.14); }
  }

  .editor {
    width: 100%;
    max-width: 820px;
    min-height: 600px;
    color: #000;
    flex-shrink: 0;
  }

  #document-preview {
    width: 100%;
    max-width: 820px;
    min-height: 600px;
    color: #000;
    flex-shrink: 0;
    padding-bottom: 20px;
  }
}

// ─── Comment Dialog ────────────────────────────────────────────────────────────
.cd-reply-context {
  background: $bg-elevated;
  border: 1px solid $border-default;
  border-radius: $radius-sm;
  padding: 10px 14px;
  margin-bottom: 14px;

  &--selection {
    border-color: rgba($accent, 0.3);
    background: $accent-dim;
  }

  &__label {
    font-size: 11px;
    font-weight: 600;
    color: $text-muted;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 6px;
    i { margin-right: 5px; }
  }

  &__quote {
    font-size: 13px;
    color: $text-secondary;
    line-height: 1.5;
    font-style: italic;
  }
}

.cd-comment-input {
  width: 100%;
  background: $bg-elevated !important;
  border: 1px solid $border-default !important;
  border-radius: $radius-sm !important;
  color: $text-primary !important;
  font-size: 13px;
  padding: 10px 14px;
  resize: vertical;
  line-height: 1.5;
  min-height: 100px;
  outline: none;
  transition: $transition;
  display: block;
  box-sizing: border-box;

  &:focus {
    border-color: $accent !important;
    box-shadow: 0 0 0 2px rgba($accent, 0.15) !important;
  }

  &::placeholder { color: $text-muted; }
}

.cd-dialog-btn {
  padding: 8px 18px;
  border-radius: $radius-sm;
  font-size: 13px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: $transition;
  outline: none;

  &--cancel {
    background: $bg-elevated;
    color: $text-secondary;
    border: 1px solid $border-default;
    &:hover { background: $bg-hover; color: $text-primary; }
  }

  &--primary {
    background: $accent;
    color: #fff;
    &:hover { background: $accent-hover; }
    &:disabled { opacity: 0.4; cursor: not-allowed; }
  }
}

// ─── Global Chat ───────────────────────────────────────────────────────────────
.gc-chat {
  display: flex;
  flex-direction: column;
  height: 480px;
  margin: -1.5rem;
}

.gc-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: $border-default; border-radius: 2px; }
}

.gc-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: $text-muted;
  gap: 10px;
  text-align: center;

  i { font-size: 32px; }
  p { font-size: 14px; margin: 0; }
}

.gc-thread { margin-bottom: 14px; }

.gc-msg {
  display: flex;
  gap: 10px;
  &.is-deleting { opacity: 0.4; pointer-events: none; }
}

.gc-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: linear-gradient(135deg, $accent, #8b5cf6);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &--sm {
    width: 26px;
    height: 26px;
    font-size: 11px;
    background: linear-gradient(135deg, #6d758f, #4a5270);
  }
}

.gc-msg-body { flex: 1; min-width: 0; }

.gc-msg-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.gc-author { font-size: 13px; font-weight: 600; color: $text-primary; }

.gc-time { font-size: 11px; color: $text-muted; }

.gc-actions {
  display: flex;
  gap: 4px;
  margin-left: auto;
  opacity: 0;
  transition: $transition;

  .gc-msg:hover & { opacity: 1; }
  .gc-reply:hover & { opacity: 1; }
}

.gc-action-btn {
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: $text-muted;
  cursor: pointer;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: $transition;
  outline: none;

  &:hover { background: $bg-hover; color: $text-secondary; }
  &--danger:hover { background: $danger-dim; color: $danger; }
}

.gc-bubble {
  font-size: 13px;
  line-height: 1.6;
  color: $text-primary;
  background: $bg-elevated;
  border-radius: 0 8px 8px 8px;
  padding: 8px 12px;
  white-space: pre-wrap;
  word-break: break-word;
  display: inline-block;
  max-width: 100%;

  &--reply { font-size: 12px; background: $bg-surface; }
}

.gc-replies {
  margin-left: 44px;
  padding-left: 12px;
  border-left: 2px solid $border-subtle;
  margin-top: 6px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.gc-reply {
  display: flex;
  gap: 8px;
  &.is-deleting { opacity: 0.4; pointer-events: none; }
}

.gc-input-area {
  background: $bg-surface;
  border-top: 1px solid $border-subtle;
  padding: 14px 16px;
  flex-shrink: 0;
  border-radius: 0 0 12px 12px;
}

.gc-reply-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  background: $accent-dim;
  border: 1px solid rgba($accent, 0.25);
  border-radius: $radius-sm;
  padding: 6px 12px;
  margin-bottom: 10px;
  font-size: 12px;
  color: $text-secondary;

  strong { color: $accent; }
  i { color: $accent; }
}

.gc-cancel-reply {
  margin-left: auto;
  background: none;
  border: none;
  color: $text-muted;
  cursor: pointer;
  font-size: 12px;
  outline: none;
  padding: 2px 4px;
  border-radius: 3px;
  transition: $transition;
  &:hover { color: $text-primary; background: $bg-hover; }
}

.gc-input-row {
  display: flex;
  gap: 10px;
  align-items: flex-end;
}

.gc-textarea {
  flex: 1;
  background: $bg-elevated !important;
  border: 1px solid $border-default !important;
  color: $text-primary !important;
  border-radius: $radius-sm !important;
  font-size: 13px;
  padding: 10px 14px;
  resize: none;
  outline: none;
  line-height: 1.5;
  min-height: 60px;
  transition: $transition;
  box-sizing: border-box;
  width: 100%;

  &:focus {
    border-color: $accent !important;
    box-shadow: 0 0 0 2px rgba($accent, 0.15) !important;
  }

  &::placeholder { color: $text-muted; }
}

.gc-send-btn {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: $radius-sm;
  background: $accent;
  color: #fff;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: $transition;
  outline: none;

  &:hover { background: $accent-hover; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
}

.revision-list {
  display: flex;
  flex-direction: column;
}

.revision-item {
  padding: 10px 14px;
  border-bottom: 1px solid $border-subtle;
  cursor: pointer;
  transition: $transition;

  &:hover {
    background: $bg-hover;
  }

  &:last-child {
    border-bottom: none;
  }
}

.revision-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.revision-version {
  font-size: 11px;
  font-weight: 600;
  color: $accent;
  background: $accent-dim;
  padding: 2px 6px;
  border-radius: $radius-sm;
}

.revision-date {
  font-size: 12px;
  color: $text-secondary;
}

.revision-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  background: $accent-dim;
  border-bottom: 1px solid $accent;
  font-size: 13px;
  color: $text-primary;
  z-index: 10;

  .fa-history {
    color: $accent;
  }

  span {
    flex: 1;
  }
}

.revision-banner-btn {
  border: none;
  background: $accent;
  color: #fff;
  padding: 4px 12px;
  border-radius: $radius-sm;
  cursor: pointer;
  font-size: 12px;
  transition: $transition;

  &:hover {
    background: $accent-hover;
  }

  &.restore {
    background: #28a745;

    &:hover {
      background: #218838;
    }
  }
}
</style>

<style lang="scss">
@font-face {
  font-family: 'Aptos';
  src: url('/fonts/aptos/Aptos.ttf') format('truetype');
  font-weight: 400;
  font-style: normal;
  font-display: swap;
}
@font-face {
  font-family: 'Aptos';
  src: url('/fonts/aptos/Aptos-Bold.ttf') format('truetype');
  font-weight: 700;
  font-style: normal;
  font-display: swap;
}
@font-face {
  font-family: 'Aptos';
  src: url('/fonts/aptos/Aptos-Italic.ttf') format('truetype');
  font-weight: 400;
  font-style: italic;
  font-display: swap;
}
@font-face {
  font-family: 'Aptos';
  src: url('/fonts/aptos/Aptos-Bold-Italic.ttf') format('truetype');
  font-weight: 700;
  font-style: italic;
  font-display: swap;
}
@font-face {
  font-family: 'Aptos';
  src: url('/fonts/aptos/Aptos-Light.ttf') format('truetype');
  font-weight: 300;
  font-style: normal;
  font-display: swap;
}
@font-face {
  font-family: 'Aptos';
  src: url('/fonts/aptos/Aptos-SemiBold.ttf') format('truetype');
  font-weight: 600;
  font-style: normal;
  font-display: swap;
}

// ─── TipTap / ProseMirror global styles ────────────────────────────────────────
.ProseMirror-focused {
  outline: none;
}

.ProseMirror,
.ProseMirror:focus,
.ProseMirror:focus-visible {
  outline: none;
}

// ─── Section Dividers ────────────────────────────────────────────────────────
.document-section {
  display: block;
}

.document-section + .document-section {
  margin-top: 40px;
  padding-top: 20px;
  position: relative;

  &::before {
    content: attr(data-name);
    display: block;
    text-align: center;
    padding: 0 0 16px;
    color: rgba(255, 255, 255, 0.35);
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 1px;
    text-transform: uppercase;
    max-width: 210mm;
    margin: 0 auto;
    border-top: 1px dashed rgba(255, 255, 255, 0.12);
    padding-top: 16px;
  }
}

.document-section:first-child::before {
  content: attr(data-name);
  display: block;
  text-align: center;
  padding: 0 0 8px;
  color: rgba(255, 255, 255, 0.25);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.5px;
  max-width: 210mm;
  margin: 0 auto;
}

.page {
  border: none;
  outline: none;
  margin: 0 auto 32px;
  width: 100%;
  max-width: 210mm;
  min-height: 297mm;
  position: relative;
  background-color: #fff;
  background-image: var(--page-bg-image, none);
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  box-shadow:
    0 1px 4px rgba(0,0,0,0.08),
    0 6px 20px rgba(0,0,0,0.14),
    0 16px 48px rgba(0,0,0,0.22);
  border-radius: 2px;

  &:first-child { margin-top: 20px; }
  &:last-child { margin-bottom: 20px; }

  &::after {
    content: '';
    position: absolute;
    bottom: -16px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 1px;
    background: rgba(255,255,255,0.06);
  }

  &:last-child::after { display: none; }
}

.page-header-content,
.page-footer-content {
  color: #555;
  font-size: 11px;
  min-height: 16px;
  position: relative;

  &:empty::before,
  & > p:only-child:empty::before {
    content: attr(data-placeholder);
    color: rgba(0,0,0,0.2);
    pointer-events: none;
    font-style: italic;
  }

  p { margin: 0; min-height: 1em; }
}

.page-header-content {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 5;
  zoom: 1;
  border-bottom: 1px dashed rgba(0,0,0,0.08);
}

.page-footer-content {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 5;
  zoom: 1;
  border-top: 1px dashed rgba(0,0,0,0.08);
}

.page-number-node {
  background: rgba(91, 124, 246, 0.08);
  border-radius: 3px;
  padding: 0 4px;
  font-variant-numeric: tabular-nums;
  cursor: default;
  user-select: all;
}

.reserved-placeholder-warning {
  color: #e53e3e;
  background: rgba(229, 62, 62, 0.1);
  border-bottom: 2px wavy #e53e3e;
  border-radius: 2px;
  cursor: help;
  position: relative;

  &::after {
    content: 'Reserved keyword — use "Insert Page Number" instead';
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    background: #1a1a2e;
    color: #f0f0f0;
    font-size: 11px;
    padding: 4px 8px;
    border-radius: 4px;
    white-space: nowrap;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.15s;
    z-index: 100;
    margin-bottom: 4px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
  }

  &:hover::after {
    opacity: 1;
  }
}

.page > * {
  zoom: 0.9;
  transform-origin: top left;
}

.tiptap {
  // Document CONTENT rendering (font, colour, headings, tables, text margins,
  // line-height, white-space) is governed by the canonical stylesheet injected
  // at runtime via applyEditorDocumentCSS() — scoped to `.editor-container
  // .tiptap`, the SAME source preview/print build from — so all surfaces render
  // identically. Only editor-specific CHROME (selection, resize affordances,
  // wrapper scrolling) lives here; do NOT re-add document typography/table rules
  // or they will drift from preview/print again.
  table {
    .selectedCell:after {
      background: rgba(91, 124, 246, 0.12);
      content: "";
      left: 0; right: 0; top: 0; bottom: 0;
      pointer-events: none;
      position: absolute;
      z-index: 2;
    }

    .column-resize-handle {
      background-color: #5b7cf6;
      bottom: -2px;
      pointer-events: none;
      position: absolute;
      right: -2px;
      top: 0;
      width: 3px;
    }
  }

  // Editor-only: let wide tables scroll while authoring. The 1.5rem block margin
  // comes from the canonical .tableWrapper rule (shared with preview/print).
  .tableWrapper {
    overflow-x: auto;
  }

  &.resize-cursor {
    cursor: col-resize;
  }
}

// ─── Context menu ──────────────────────────────────────────────────────────────
.context-menu {
  position: fixed;
  background: #1a1d29;
  border: 1px solid #2d3250;
  box-shadow: 0 8px 32px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.3);
  z-index: 9999;
  border-radius: 8px;
  overflow: hidden;
  min-width: 160px;

  ul {
    list-style: none;
    margin: 0;
    padding: 6px;
  }

  li {
    padding: 7px 12px;
    cursor: pointer;
    color: #9ca3af;
    font-size: 13px;
    border-radius: 5px;
    transition: all 0.15s ease;

    &:hover {
      background: #232840;
      color: #e8eaf2;
    }
  }
}

// ─── Template node highlights ──────────────────────────────────────────────────
.chooseSource {
  cursor: pointer;
  border: 2px solid #f59e0b;
  border-radius: 4px;
  padding: 1px 4px;
  display: inline-block;
  background-color: rgba(245, 158, 11, 0.08);
  color: #000;
  transition: all 0.15s ease;

  &[active="true"] {
    border-color: #10b981;
    background-color: rgba(16, 185, 129, 0.08);
  }
}

.ifClause {
  cursor: pointer;
  border: 1px solid #818cf8;
  border-radius: 4px;
  padding: 1px 4px;
  display: inline-block;
  background-color: rgba(129, 140, 248, 0.08);
  color: #000;

  .ifClauseCondition { font-family: monospace; font-size: 0.9em; }
}

.rangeClause {
  cursor: pointer;
  border: 1px solid #34d399;
  border-radius: 4px;
  padding: 1px 4px;
  display: inline-block;
  background-color: rgba(52, 211, 153, 0.08);
  color: #000;

  .rangeClauseSource { font-family: monospace; font-size: 0.9em; }
}

.hoverGray:hover:not(:has(.hoverGray:hover)) {
  background-color: rgba(211, 211, 211, 0.5);
}

// ─── Comment marks ─────────────────────────────────────────────────────────────
.protokol-comment {
  background-color: rgba(255, 229, 100, 0.45);
  position: relative;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 2px solid rgba(245, 158, 11, 0.6);
}

.protokol-comment:hover {
  background-color: rgba(255, 229, 100, 0.7);
}

.protokol-comment:hover::after {
  content: attr(data-comment-content);
  position: absolute;
  left: 0;
  top: 100%;
  margin-top: 4px;
  background: #fffbe7;
  border: 1px solid #e5d769;
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 12px;
  line-height: 1.4;
  white-space: pre-wrap;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  pointer-events: none;
  max-width: 260px;
}

// ─── Modal overrides for dark theme ───────────────────────────────────────────
.modal-content {
  background: #13161f !important;
  border: 1px solid #2d3250 !important;
  border-radius: 12px !important;
  color: #e8eaf2 !important;
}

.modal-header {
  background: #0d0f17 !important;
  border-bottom: 1px solid #232840 !important;
  padding: 16px 20px !important;

  .modal-title { color: #e8eaf2 !important; font-size: 15px !important; font-weight: 600 !important; }
  .close { color: #7c87a8 !important; opacity: 1 !important; }
}

.modal-body {
  padding: 0 !important;
}

.modal-footer {
  background: #0d0f17 !important;
  border-top: 1px solid #232840 !important;
  padding: 12px 20px !important;
}

.container-fluid:has(.editor-container) {
  margin: 0 !important;
  padding: 0 !important;
}

.main:has(.editor-container) .breadcrumb,
.main:has(.editor-container) .home-nav {
  margin-bottom: 0 !important;
}
</style>
